// src/components/admin/DocumentBuilderEnhanced.js
import React, { useState, useEffect, useRef } from 'react';
import {
    FileText, CheckCircle, Eye, X, Save, Plus, Edit2, Send,
    Mail, Download, Search, ChevronDown, Clock, Check,
    FileEdit, FileCheck, Copy, Trash2, AlertCircle, FilePlus,
    Type, Square, MoreVertical, User, Building, ZoomIn, ZoomOut,
    RotateCcw, Info, Users, AlertTriangle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import documentTemplateService from '../../services/documentTemplate.service';
import employeeService from '../../services/employee.service';
import documentDatabaseService from '../../services/documentDatabase.service';
import documentService from '../../services/document.service';
import compensationService from '../../services/compensation.service';
import onboardingService from '../../services/onboarding.service';
import shiftService from '../../services/shift.service';
import exitService from '../../services/exit.service';
import { api } from '../../services/api';
import RichTextEditorEnhanced from '../common/RichTextEditor';
import PlaceholderSelector from '../common/PlaceholderSelector';
import { replacePlaceholders } from '../../utils/documentPlaceholders';

// Convert number to words (Indian numbering system - Lakh, Crore)
const numberToWordsIndian = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';

    const number = Math.abs(Math.round(Number(num)));
    if (number === 0) return 'Zero Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertLessThanHundred = (n) => {
        if (n < 20) return ones[n];
        return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    };

    const convertLessThanThousand = (n) => {
        if (n < 100) return convertLessThanHundred(n);
        return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanHundred(n % 100) : '');
    };

    let result = '';
    let remaining = number;

    // Crore (1,00,00,000)
    if (remaining >= 10000000) {
        result += convertLessThanThousand(Math.floor(remaining / 10000000)) + ' Crore ';
        remaining %= 10000000;
    }

    // Lakh (1,00,000)
    if (remaining >= 100000) {
        result += convertLessThanHundred(Math.floor(remaining / 100000)) + ' Lakh ';
        remaining %= 100000;
    }

    // Thousand (1,000)
    if (remaining >= 1000) {
        result += convertLessThanHundred(Math.floor(remaining / 1000)) + ' Thousand ';
        remaining %= 1000;
    }

    // Hundred and below
    if (remaining > 0) {
        result += convertLessThanThousand(remaining);
    }

    return (num < 0 ? 'Minus ' : '') + result.trim() + ' Only';
};

// Required fields per document template type
const TEMPLATE_REQUIRED_FIELDS = {
    appointment: ['designation', 'department', 'joining_date', 'ctc', 'location'],
    confirmation: ['designation', 'department', 'joining_date'],
    experience: ['designation', 'department', 'joining_date'],
    promotion: ['designation', 'department'],
    termination: ['designation', 'department'],
    payslip: ['designation', 'department', 'ctc']
};

// Common fields always required for all documents
const COMMON_REQUIRED_FIELDS = ['first_name', 'last_name', 'email', 'employee_code'];

// Field labels for display
const FIELD_LABELS = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email',
    employee_code: 'Employee Code',
    designation: 'Designation',
    department: 'Department',
    joining_date: 'Joining Date',
    exit_date: 'Last Working Day',
    confirmation_date: 'Confirmation Date',
    effective_date: 'Effective Date',
    ctc: 'CTC/Salary',
    basic_salary: 'Basic Salary',
    location: 'Work Location',
    manager_name: 'Manager Name',
    address: 'Address',
    phone: 'Phone Number',
    new_designation: 'New Designation',
    new_salary: 'New Salary',
    reason: 'Reason'
};

// Field aliases - maps required field names to all possible DB column names
const FIELD_ALIASES = {
    first_name: ['first_name', 'firstName', 'firstname'],
    last_name: ['last_name', 'lastName', 'lastname'],
    middle_name: ['middle_name', 'middleName', 'middlename'],
    ctc: ['ctc', 'salary', 'gross_salary', 'annual_ctc'],
    location: ['location', 'work_location', 'office_location'],
    designation: ['designation', 'position', 'job_title'],
    department: ['department', 'department_name'],
    joining_date: ['joining_date', 'joiningDate', 'join_date'],
    email: ['email', 'work_email'],
    employee_code: ['employee_code', 'employeeCode', 'emp_code'],
    manager_name: ['manager_name', 'reporting_manager'],
    phone: ['phone', 'mobile_number', 'contact_number']
};

// Validation function for employee data before document generation
// Now async to fetch compensation data for CTC validation
const validateEmployeeForDocument = async (employee, templateCategory) => {
    const requiredFields = [
        ...COMMON_REQUIRED_FIELDS,
        ...(TEMPLATE_REQUIRED_FIELDS[templateCategory] || [])
    ];

    const missingFields = [];

    // Merge employee data with compensation data if needed
    let mergedEmployee = { ...employee };

    // If CTC is required and not present in employee data, fetch compensation
    const needsCtc = requiredFields.includes('ctc');
    if (needsCtc && !employee?.ctc) {
        try {
            console.log('📊 Fetching compensation data for employee:', employee?.id);
            const compensationResponse = await compensationService.getEmployeeCompensation(employee?.id);
            console.log('📦 Compensation response:', compensationResponse);

            if (compensationResponse.success && compensationResponse.compensation) {
                const comp = compensationResponse.compensation;
                // CTC can be in different places: pay_package.ctc, annual_ctc, or directly ctc
                const ctcValue = comp.pay_package?.ctc || comp.annual_ctc || comp.ctc || 0;
                mergedEmployee.ctc = ctcValue;
                mergedEmployee.annual_ctc = ctcValue;
                mergedEmployee.salary_components = comp.salary_components;
                console.log('✅ Compensation data loaded:', { ctc: mergedEmployee.ctc });
            }
        } catch (error) {
            console.warn('⚠️ Could not fetch compensation data:', error);
        }
    }

    // Debug: Log the merged employee object
    console.log('🔍 Document Validation - Employee data:', {
        employeeId: mergedEmployee?.id,
        employeeName: `${mergedEmployee?.first_name} ${mergedEmployee?.last_name}`,
        ctc: mergedEmployee?.ctc,
        annual_ctc: mergedEmployee?.annual_ctc,
        location: mergedEmployee?.location,
        work_location: mergedEmployee?.work_location,
        designation: mergedEmployee?.designation,
        department: mergedEmployee?.department
    });

    for (const field of requiredFields) {
        // Get all possible field name variations for this field
        const aliases = FIELD_ALIASES[field] || [field, field.replace(/_/g, ''), field + '_name'];

        // Check if any of the aliases has a value
        let hasValue = false;
        let foundValue = null;
        let foundAlias = null;

        for (const alias of aliases) {
            const value = mergedEmployee[alias];
            // For numeric fields (ctc, salary), allow any number > 0
            // For string fields, check for non-empty value
            if (value !== null && value !== undefined) {
                if (typeof value === 'number') {
                    if (value > 0) {
                        hasValue = true;
                        foundValue = value;
                        foundAlias = alias;
                        break;
                    }
                } else if (typeof value === 'string' && value.trim() !== '') {
                    hasValue = true;
                    foundValue = value;
                    foundAlias = alias;
                    break;
                } else if (value) {
                    // For other types (objects, arrays, etc.)
                    hasValue = true;
                    foundValue = value;
                    foundAlias = alias;
                    break;
                }
            }
        }

        if (!hasValue) {
            console.log(`❌ Missing field: ${field}, checked aliases:`, aliases.map(a => `${a}=${mergedEmployee[a]}`));
            missingFields.push({
                field: field,
                label: FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            });
        } else {
            console.log(`✅ Found field: ${field} as '${foundAlias}' = ${foundValue}`);
        }
    }

    console.log('📋 Validation result:', { isValid: missingFields.length === 0, missingFields });

    return {
        isValid: missingFields.length === 0,
        missingFields
    };
};

// Replace placeholders in content with actual employee/company data for preview
// Function to detect unreplaced placeholders in processed content
const detectUnreplacedPlaceholders = (content) => {
    if (!content) return [];

    // Pattern to match placeholders like [Employee Name], [Job Title], etc.
    const placeholderRegex = /\[([^\]]+)\]/g;
    const unreplacedPlaceholders = [];
    let match;

    // Placeholder to field mapping for user-friendly labels
    const placeholderToField = {
        // Employee basic info
        'Employee Name': { field: 'employeeName', label: 'Employee Name', editPath: 'basic' },
        'First Name': { field: 'firstName', label: 'First Name', editPath: 'basic' },
        'Middle Name': { field: 'middleName', label: 'Middle Name', editPath: 'basic' },
        'Last Name': { field: 'lastName', label: 'Last Name', editPath: 'basic' },
        'Display Name': { field: 'displayName', label: 'Display Name', editPath: 'basic' },
        'Gender': { field: 'gender', label: 'Gender', editPath: 'basic' },
        'Date of Birth': { field: 'dateOfBirth', label: 'Date of Birth', editPath: 'basic' },
        'Blood Group': { field: 'bloodGroup', label: 'Blood Group', editPath: 'basic' },
        'Personal Email': { field: 'personalEmail', label: 'Personal Email', editPath: 'basic' },
        'Mobile Number': { field: 'mobileNumber', label: 'Mobile Number', editPath: 'basic' },
        'Emergency Contact': { field: 'emergencyContactName', label: 'Emergency Contact Name', editPath: 'basic' },
        'Emergency Contact Name': { field: 'emergencyContactName', label: 'Emergency Contact Name', editPath: 'basic' },
        'Emergency Phone': { field: 'emergencyContactNumber', label: 'Emergency Contact Phone', editPath: 'basic' },
        'Emergency Contact Number': { field: 'emergencyContactNumber', label: 'Emergency Contact Phone', editPath: 'basic' },
        'Emergency Contact Relationship': { field: 'emergencyContactRelationship', label: 'Emergency Contact Relationship', editPath: 'basic' },
        'Mr/Ms': { field: 'salutation', label: 'Salutation (Mr/Ms)', editPath: 'basic' },
        'Title': { field: 'salutation', label: 'Title', editPath: 'basic' },
        // Address fields
        'City': { field: 'city', label: 'City', editPath: 'basic' },
        'State': { field: 'state', label: 'State', editPath: 'basic' },
        'Postal Code': { field: 'postalCode', label: 'Postal Code', editPath: 'basic' },
        'Country': { field: 'country', label: 'Country', editPath: 'basic' },
        'Employee Address': { field: 'address', label: 'Employee Address', editPath: 'basic' },
        // Employment info
        'Job Title': { field: 'designation', label: 'Designation/Job Title', editPath: 'employment' },
        'Designation': { field: 'designation', label: 'Designation', editPath: 'employment' },
        'Department': { field: 'department', label: 'Department', editPath: 'employment' },
        'Department Name': { field: 'department', label: 'Department', editPath: 'employment' },
        'Branch': { field: 'branch', label: 'Branch', editPath: 'employment' },
        'Employment Type': { field: 'employmentType', label: 'Employment Type', editPath: 'employment' },
        'Employment Status': { field: 'employmentStatus', label: 'Employment Status', editPath: 'employment' },
        'Shift': { field: 'shift', label: 'Work Shift', editPath: 'employment' },
        'Work Shift': { field: 'shift', label: 'Work Shift', editPath: 'employment' },
        'Manager Name': { field: 'managerName', label: 'Reporting Manager', editPath: 'employment' },
        'Location': { field: 'location', label: 'Work Location', editPath: 'employment' },
        'Years of Service': { field: 'yearsOfService', label: 'Years of Service', editPath: 'employment' },
        'Tenure': { field: 'tenure', label: 'Tenure / Years of Service', editPath: 'employment' },
        // Company info
        'Company Name': { field: 'companyName', label: 'Company Name', editPath: 'company' },
        'Company Address': { field: 'companyAddress', label: 'Company Address', editPath: 'company' },
        'Company Website': { field: 'companyWebsite', label: 'Company Website', editPath: 'company' },
        'HR Email': { field: 'hrEmail', label: 'HR Email', editPath: 'company' },
        'CEO Name': { field: 'ceoName', label: 'CEO Name', editPath: 'company' },
        // Salary/Compensation
        'Amount': { field: 'salary', label: 'CTC/Salary', editPath: 'compensation' },
        'Salary': { field: 'salary', label: 'CTC/Salary', editPath: 'compensation' },
        'Amount in Words': { field: 'salary', label: 'CTC/Salary (for conversion to words)', editPath: 'compensation' },
        'Salary in Words': { field: 'salary', label: 'CTC/Salary (for conversion to words)', editPath: 'compensation' },
        'CTC in Words': { field: 'salary', label: 'CTC/Salary (for conversion to words)', editPath: 'compensation' },
        'Basic Salary': { field: 'basicSalary', label: 'Basic Salary', editPath: 'compensation' },
        'Monthly Salary': { field: 'monthlySalary', label: 'Monthly Salary', editPath: 'compensation' },
        'Net Salary': { field: 'netSalary', label: 'Net Salary', editPath: 'compensation' },
        'HRA': { field: 'hra', label: 'HRA', editPath: 'compensation' },
        'Conveyance': { field: 'conveyance', label: 'Conveyance', editPath: 'compensation' },
        'Special Allowance': { field: 'specialAllowance', label: 'Special Allowance', editPath: 'compensation' },
        'PF Contribution': { field: 'pfContribution', label: 'PF Contribution', editPath: 'compensation' },
        'ESI Contribution': { field: 'esiContribution', label: 'ESI Contribution', editPath: 'compensation' },
        'Professional Tax': { field: 'professionalTax', label: 'Professional Tax', editPath: 'compensation' },
        'TDS': { field: 'tds', label: 'TDS', editPath: 'compensation' },
        'Increment Percentage': { field: 'incrementPercentage', label: 'Increment Percentage', editPath: 'compensation' },
        'New Salary': { field: 'newSalary', label: 'New Salary', editPath: 'compensation' },
        // Bank details
        'Bank Name': { field: 'bankName', label: 'Bank Name', editPath: 'bank' },
        'Account Number': { field: 'accountNumber', label: 'Account Number', editPath: 'bank' },
        'IFSC Code': { field: 'ifscCode', label: 'IFSC Code', editPath: 'bank' },
        // Identity
        'PAN Number': { field: 'panNumber', label: 'PAN Number', editPath: 'identity' },
        'UAN Number': { field: 'uanNumber', label: 'UAN Number', editPath: 'identity' },
        'Aadhaar Number': { field: 'aadhaarNumber', label: 'Aadhaar Number', editPath: 'identity' },
        'Passport Number': { field: 'passportNumber', label: 'Passport Number', editPath: 'identity' },
        'Passport Expiry': { field: 'passportExpiry', label: 'Passport Expiry', editPath: 'identity' },
        'Visa Status': { field: 'visaStatus', label: 'Visa Status', editPath: 'identity' },
        // Personal/Family
        'Nationality': { field: 'nationality', label: 'Nationality', editPath: 'basic' },
        'Marital Status': { field: 'maritalStatus', label: 'Marital Status', editPath: 'basic' },
        'Spouse Name': { field: 'spouseName', label: 'Spouse Name', editPath: 'basic' },
        'Father Name': { field: 'fatherName', label: 'Father Name', editPath: 'basic' },
        'Mother Name': { field: 'motherName', label: 'Mother Name', editPath: 'basic' },
        // HR related
        'HR Name': { field: 'hrName', label: 'HR Name', editPath: 'company' },
        'Employee Code': { field: 'employeeCode', label: 'Employee Code', editPath: 'basic' },
        'Email': { field: 'email', label: 'Email Address', editPath: 'basic' },
        'Phone': { field: 'phone', label: 'Phone Number', editPath: 'basic' },
        // Dates
        'Joining Date': { field: 'joiningDate', label: 'Joining Date', editPath: 'employment' },
        'Start Date': { field: 'startDate', label: 'Start Date', editPath: 'employment' },
        'End Date': { field: 'endDate', label: 'End Date/Last Working Day', editPath: 'exit' },
        'Last Working Day': { field: 'lastWorkingDay', label: 'Last Working Day', editPath: 'exit' },
        'Confirmation Date': { field: 'confirmationDate', label: 'Confirmation Date', editPath: 'employment' },
        'Effective Date': { field: 'effectiveDate', label: 'Effective Date', editPath: 'user_input', requiresDateInput: true },
        'Issue Date': { field: 'issueDate', label: 'Issue Date', editPath: 'user_input' },
        'Expiry Date': { field: 'expiryDate', label: 'Expiry Date', editPath: 'user_input' },
        'Promotion Date': { field: 'promotionDate', label: 'Promotion Date', editPath: 'user_input' },
        // Periods
        'X': { field: 'probationMonths', label: 'Probation Period (months)', editPath: 'employment', requiresNumberInput: true },
        'Y': { field: 'noticePeriod', label: 'Notice Period (days)', editPath: 'employment', requiresNumberInput: true },
        'Notice Period': { field: 'noticePeriod', label: 'Notice Period (days)', editPath: 'employment', requiresNumberInput: true },
        'Probation Period': { field: 'probationMonths', label: 'Probation Period (months)', editPath: 'employment', requiresNumberInput: true },
        // Performance
        'Performance Rating': { field: 'performanceRating', label: 'Performance Rating', editPath: 'performance' },
        'Review Period': { field: 'reviewPeriod', label: 'Review Period', editPath: 'performance' },
        'Reviewer Name': { field: 'reviewerName', label: 'Reviewer Name', editPath: 'performance' },
        'Goals Achieved': { field: 'goalsAchieved', label: 'Goals Achieved', editPath: 'performance' },
        'New Designation': { field: 'newDesignation', label: 'New Designation', editPath: 'performance' },
        // Misc
        'Reference Number': { field: 'referenceNumber', label: 'Reference Number', editPath: 'user_input' },
        'Reason': { field: 'reason', label: 'Reason', editPath: 'user_input' },
        'Remarks': { field: 'remarks', label: 'Remarks', editPath: 'user_input' },
        'Attachments': { field: 'attachments', label: 'Attachments', editPath: 'user_input' }
    };

    // Find all unreplaced placeholders
    while ((match = placeholderRegex.exec(content)) !== null) {
        const placeholder = match[1];
        // Skip date placeholders that are always auto-filled
        if (['DD/MM/YYYY', 'Date', 'Today', 'Month', 'Year', 'Month Year', 'Currency'].includes(placeholder)) {
            continue;
        }
        // Skip gender pronouns
        if (['his/her', 'His/Her', 'he/she', 'He/She', 'him/her', 'Him/Her'].includes(placeholder)) {
            continue;
        }

        const fieldInfo = placeholderToField[placeholder];
        if (fieldInfo) {
            // Avoid duplicates
            if (!unreplacedPlaceholders.find(p => p.field === fieldInfo.field)) {
                unreplacedPlaceholders.push({
                    placeholder: `[${placeholder}]`,
                    ...fieldInfo
                });
            }
        } else {
            // Unknown placeholder, add with generic info
            if (!unreplacedPlaceholders.find(p => p.placeholder === `[${placeholder}]`)) {
                unreplacedPlaceholders.push({
                    placeholder: `[${placeholder}]`,
                    field: placeholder.toLowerCase().replace(/\s+/g, '_'),
                    label: placeholder,
                    editPath: 'unknown'
                });
            }
        }
    }

    return unreplacedPlaceholders;
};

const replaceDocumentPlaceholders = (content, employeeData, companyData) => {
    if (!content) return '';

    let processedContent = content;

    // Replace common placeholders
    processedContent = processedContent
        // Employee basic info
        .replace(/\[Employee Name\]/g, employeeData.employeeName || '[Employee Name]')
        .replace(/\[Display Name\]/g, employeeData.displayName || employeeData.employeeName || '[Display Name]')
        .replace(/\[First Name\]/g, employeeData.firstName || '[First Name]')
        .replace(/\[Middle Name\]/g, employeeData.middleName || '')
        .replace(/\[Last Name\]/g, employeeData.lastName || '[Last Name]')
        .replace(/\[Gender\]/g, employeeData.genderDisplay || '[Gender]')
        .replace(/\[Mr\/Ms\]/g, employeeData.salutation || '[Mr/Ms]')
        .replace(/\[Title\]/g, employeeData.salutation || '[Title]')
        .replace(/\[Date of Birth\]/g, employeeData.dateOfBirth || '[Date of Birth]')
        .replace(/\[Mobile Number\]/g, employeeData.mobileNumber || '[Mobile Number]')
        .replace(/\[Personal Email\]/g, employeeData.personalEmail || '[Personal Email]')
        .replace(/\[Work Email\]/g, employeeData.workEmail || employeeData.email || '[Work Email]')
        .replace(/\[Emergency Contact Name\]/g, employeeData.emergencyContactName || '[Emergency Contact Name]')
        .replace(/\[Emergency Contact\]/g, employeeData.emergencyContactName || '[Emergency Contact]')
        .replace(/\[Emergency Contact Number\]/g, employeeData.emergencyContactNumber || '[Emergency Contact Number]')
        .replace(/\[Emergency Phone\]/g, employeeData.emergencyContactNumber || '[Emergency Phone]')
        .replace(/\[Emergency Contact Relationship\]/g, employeeData.emergencyContactRelationship || '[Emergency Contact Relationship]')
        .replace(/\[Blood Group\]/g, employeeData.bloodGroup || '[Blood Group]')
        // Address fields
        .replace(/\[Employee Address\]/g, employeeData.address || 'N/A')
        .replace(/\[Full Address\]/g, employeeData.fullAddress || employeeData.address || '[Full Address]')
        .replace(/\[Address Line 1\]/g, employeeData.addressLine1 || '[Address Line 1]')
        .replace(/\[Address Line 2\]/g, employeeData.addressLine2 || '')
        .replace(/\[City\]/g, employeeData.city || '[City]')
        .replace(/\[State\]/g, employeeData.state || '[State]')
        .replace(/\[Country\]/g, employeeData.country || '[Country]')
        .replace(/\[PIN Code\]/g, employeeData.pinCode || '[PIN Code]')
        .replace(/\[Postal Code\]/g, employeeData.postalCode || employeeData.pinCode || '[Postal Code]')
        // Permanent address
        .replace(/\[Permanent Address\]/g, employeeData.permanentAddress || '[Permanent Address]')
        .replace(/\[Permanent Address Line 1\]/g, employeeData.permanentAddressLine1 || '[Permanent Address Line 1]')
        .replace(/\[Permanent Address Line 2\]/g, employeeData.permanentAddressLine2 || '')
        .replace(/\[Permanent City\]/g, employeeData.permanentCity || '[Permanent City]')
        .replace(/\[Permanent State\]/g, employeeData.permanentState || '[Permanent State]')
        .replace(/\[Permanent Country\]/g, employeeData.permanentCountry || '[Permanent Country]')
        .replace(/\[Permanent PIN Code\]/g, employeeData.permanentPinCode || '[Permanent PIN Code]')
        // Employment details
        .replace(/\[Job Title\]/g, employeeData.designation || '[Job Title]')
        .replace(/\[Designation\]/g, employeeData.designation || '[Designation]')
        .replace(/\[Department Name\]/g, employeeData.department || '[Department Name]')
        .replace(/\[Department\]/g, employeeData.department || '[Department]')
        .replace(/\[Manager Name\]/g, employeeData.managerName || 'N/A')
        .replace(/\[Location\]/g, employeeData.location || companyData.location || '[Location]')
        .replace(/\[Work Location\]/g, employeeData.workLocation || employeeData.location || '[Work Location]')
        .replace(/\[Employment Type\]/g, employeeData.employmentType || '[Employment Type]')
        .replace(/\[Employment Status\]/g, employeeData.employmentStatus || '[Employment Status]')
        .replace(/\[Sub Department\]/g, employeeData.subDepartment || 'N/A')
        .replace(/\[Payment Mode\]/g, employeeData.paymentMode || 'Bank Transfer')
        .replace(/\[Shift\]/g, employeeData.shift || '[Shift]')
        .replace(/\[Work Shift\]/g, employeeData.shift || '[Work Shift]')
        .replace(/\[Working Hours\]/g, employeeData.workingHours || '[Working Hours]')
        // Bank details
        .replace(/\[Bank Name\]/g, employeeData.bankName || '[Bank Name]')
        .replace(/\[Account Number\]/g, employeeData.accountNumber || '[Account Number]')
        .replace(/\[IFSC Code\]/g, employeeData.ifscCode || '[IFSC Code]')
        .replace(/\[Bank IFSC\]/g, employeeData.ifscCode || '[Bank IFSC]')
        .replace(/\[Branch Name\]/g, employeeData.branchName || '[Branch Name]')
        .replace(/\[Account Holder Name\]/g, employeeData.accountHolderName || employeeData.employeeName || '[Account Holder Name]')
        .replace(/\[Account Type\]/g, employeeData.accountType || 'Savings')
        // Compensation
        .replace(/\[CTC\]/g, employeeData.salary ? employeeData.salary.toLocaleString() : '[CTC]')
        .replace(/\[Annual CTC\]/g, employeeData.salary ? employeeData.salary.toLocaleString() : '[Annual CTC]')
        .replace(/\[Monthly CTC\]/g, employeeData.monthlyCTC ? employeeData.monthlyCTC.toLocaleString() : '[Monthly CTC]')
        .replace(/\[Gross Salary\]/g, employeeData.grossSalary ? employeeData.grossSalary.toLocaleString() : '[Gross Salary]')
        .replace(/\[Net Salary\]/g, employeeData.netSalary ? employeeData.netSalary.toLocaleString() : '[Net Salary]')
        .replace(/\[Basic Salary\]/g, employeeData.basicSalary ? employeeData.basicSalary.toLocaleString() : '[Basic Salary]')
        .replace(/\[Monthly Salary\]/g, employeeData.monthlySalary ? employeeData.monthlySalary.toLocaleString() : '[Monthly Salary]')
        .replace(/\[HRA\]/g, employeeData.hra ? employeeData.hra.toLocaleString() : '[HRA]')
        .replace(/\[Special Allowance\]/g, employeeData.specialAllowance ? employeeData.specialAllowance.toLocaleString() : '[Special Allowance]')
        .replace(/\[Transport Allowance\]/g, employeeData.transportAllowance ? employeeData.transportAllowance.toLocaleString() : '[Transport Allowance]')
        .replace(/\[Conveyance\]/g, employeeData.conveyance ? employeeData.conveyance.toLocaleString() : '[Conveyance]')
        .replace(/\[Medical Allowance\]/g, employeeData.medicalAllowance ? employeeData.medicalAllowance.toLocaleString() : '[Medical Allowance]')
        .replace(/\[PF Contribution\]/g, employeeData.pfContribution ? employeeData.pfContribution.toLocaleString() : '[PF Contribution]')
        .replace(/\[ESI Contribution\]/g, employeeData.esiContribution ? employeeData.esiContribution.toLocaleString() : '[ESI Contribution]')
        .replace(/\[Professional Tax\]/g, employeeData.professionalTax ? employeeData.professionalTax.toLocaleString() : '[Professional Tax]')
        .replace(/\[TDS\]/g, employeeData.tds ? employeeData.tds.toLocaleString() : '[TDS]')
        .replace(/\[Income Tax\]/g, employeeData.tds ? employeeData.tds.toLocaleString() : '[Income Tax]')
        .replace(/\[PF\]/g, employeeData.pfContribution ? employeeData.pfContribution.toLocaleString() : '[PF]')
        .replace(/\[ESI\]/g, employeeData.esiContribution ? employeeData.esiContribution.toLocaleString() : '[ESI]')
        .replace(/\[Total Deductions\]/g, employeeData.totalDeductions ? employeeData.totalDeductions.toLocaleString() : '[Total Deductions]')
        .replace(/\[Amount\]/g, employeeData.salary ? employeeData.salary.toLocaleString() : '[Amount]')
        .replace(/\[Salary\]/g, employeeData.salary ? employeeData.salary.toLocaleString() : '[Salary]')
        .replace(/\[Amount in Words\]/g, employeeData.salary ? numberToWordsIndian(employeeData.salary) : '[Amount in Words]')
        .replace(/\[Salary in Words\]/g, employeeData.salary ? numberToWordsIndian(employeeData.salary) : '[Salary in Words]')
        .replace(/\[CTC in Words\]/g, employeeData.salary ? numberToWordsIndian(employeeData.salary) : '[CTC in Words]')
        // Identity documents
        .replace(/\[PAN Number\]/g, employeeData.panNumber || '[PAN Number]')
        .replace(/\[PF Number\]/g, employeeData.pfNumber || '[PF Number]')
        .replace(/\[ESI Number\]/g, employeeData.esiNumber || '[ESI Number]')
        .replace(/\[UAN Number\]/g, employeeData.uanNumber || '[UAN Number]')
        .replace(/\[Aadhaar Number\]/g, employeeData.aadhaarNumber || '[Aadhaar Number]')
        .replace(/\[Passport Number\]/g, employeeData.passportNumber || '[Passport Number]')
        .replace(/\[Passport Expiry\]/g, employeeData.passportExpiry || '[Passport Expiry]')
        .replace(/\[Visa Status\]/g, employeeData.visaStatus || '[Visa Status]')
        // Personal/Family
        .replace(/\[Nationality\]/g, employeeData.nationality || '[Nationality]')
        .replace(/\[Marital Status\]/g, employeeData.maritalStatus || '[Marital Status]')
        .replace(/\[Spouse Name\]/g, employeeData.spouseName || '[Spouse Name]')
        .replace(/\[Father Name\]/g, employeeData.fatherName || '[Father Name]')
        .replace(/\[Mother Name\]/g, employeeData.motherName || '[Mother Name]')
        // Company info
        .replace(/\[Company Name\]/g, companyData.name || '[Company Name]')
        .replace(/\[Company Address\]/g, companyData.address || 'Company Address')
        .replace(/\[Company Email\]/g, companyData.email || 'hr@company.com')
        .replace(/\[Company Phone\]/g, companyData.phone || '+1234567890')
        .replace(/\[Company Website\]/g, companyData.website || '[Company Website]')
        .replace(/\[CEO Name\]/g, companyData.ceoName || '[CEO Name]')
        .replace(/\[HR Email\]/g, companyData.hrEmail || '[HR Email]')
        .replace(/\[Branch\]/g, companyData.branch || employeeData.branch || '[Branch]')
        .replace(/\[Currency\]/g, companyData.currency || 'INR')
        .replace(/\[HR Name\]/g, companyData.hrName || '[HR Name]')
        .replace(/\[HR Title\]/g, companyData.hrTitle || 'HR Manager')
        // Dates
        .replace(/\[DD\/MM\/YYYY\]/g, new Date().toLocaleDateString('en-GB'))
        .replace(/\[Date\]/g, new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }))
        .replace(/\[Today\]/g, new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }))
        .replace(/\[Month\]/g, employeeData.month || new Date().toLocaleString('default', { month: 'long' }))
        .replace(/\[Year\]/g, employeeData.year || new Date().getFullYear().toString())
        .replace(/\[Month Year\]/g, `${employeeData.month || new Date().toLocaleString('default', { month: 'long' })} ${employeeData.year || new Date().getFullYear()}`)
        .replace(/\[Joining Date\]/g, employeeData.joiningDate || '[Joining Date]')
        .replace(/\[Start Date\]/g, employeeData.startDate || employeeData.joiningDate || '[Start Date]')
        .replace(/\[End Date\]/g, employeeData.endDate || '[End Date]')
        .replace(/\[Last Working Day\]/g, employeeData.lastWorkingDay || '[Last Working Day]')
        .replace(/\[Confirmation Date\]/g, employeeData.confirmationDate || '[Confirmation Date]')
        .replace(/\[Effective Date\]/g, employeeData.effectiveDate || '[Effective Date]')
        .replace(/\[Issue Date\]/g, employeeData.issueDate || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }))
        .replace(/\[Expiry Date\]/g, employeeData.expiryDate || '[Expiry Date]')
        .replace(/\[Promotion Date\]/g, employeeData.promotionDate || '[Promotion Date]')
        // Periods
        .replace(/\[X\]/g, employeeData.probationMonths || '[X]')
        .replace(/\[Y\]/g, employeeData.noticePeriod || '[Y]')
        .replace(/\[Notice Period\]/g, employeeData.noticePeriod || '[Notice Period]')
        .replace(/\[Probation Period\]/g, employeeData.probationMonths || '[Probation Period]')
        // Years of service / Tenure
        .replace(/\[Years of Service\]/g, employeeData.yearsOfService || '[Years of Service]')
        .replace(/\[Tenure\]/g, employeeData.tenure || employeeData.yearsOfService || '[Tenure]')
        // Employee identifiers
        .replace(/\[Employee Code\]/g, employeeData.employeeCode || '[Employee Code]')
        .replace(/\[Email\]/g, employeeData.email || '[Email]')
        .replace(/\[Phone\]/g, employeeData.phone || '[Phone]')
        .replace(/\[Manager Designation\]/g, employeeData.managerDesignation || 'Manager')
        // Performance
        .replace(/\[Performance Rating\]/g, employeeData.performanceRating || '[Performance Rating]')
        .replace(/\[Review Period\]/g, employeeData.reviewPeriod || '[Review Period]')
        .replace(/\[Reviewer Name\]/g, employeeData.reviewerName || '[Reviewer Name]')
        .replace(/\[Goals Achieved\]/g, employeeData.goalsAchieved || '[Goals Achieved]')
        .replace(/\[New Designation\]/g, employeeData.newDesignation || '[New Designation]')
        // Misc
        .replace(/\[Reference Number\]/g, employeeData.referenceNumber || '[Reference Number]')
        .replace(/\[Reason\]/g, employeeData.reason || '[Reason]')
        .replace(/\[Remarks\]/g, employeeData.remarks || '[Remarks]')
        // Gender pronouns
        .replace(/\[his\/her\]/g, employeeData.gender === 'male' ? 'his' : employeeData.gender === 'female' ? 'her' : 'their')
        .replace(/\[His\/Her\]/g, employeeData.gender === 'male' ? 'His' : employeeData.gender === 'female' ? 'Her' : 'Their')
        .replace(/\[he\/she\]/g, employeeData.gender === 'male' ? 'he' : employeeData.gender === 'female' ? 'she' : 'they')
        .replace(/\[He\/She\]/g, employeeData.gender === 'male' ? 'He' : employeeData.gender === 'female' ? 'She' : 'They')
        .replace(/\[him\/her\]/g, employeeData.gender === 'male' ? 'him' : employeeData.gender === 'female' ? 'her' : 'them')
        .replace(/\[Him\/Her\]/g, employeeData.gender === 'male' ? 'Him' : employeeData.gender === 'female' ? 'Her' : 'Them');

    // Replace LOGO placeholder with company logo or text
    const logoHtml = companyData.logo
        ? `<img src="${companyData.logo}" alt="Company Logo" style="max-width: 150px; max-height: 60px; object-fit: contain;" />`
        : `<span style="font-weight: bold; font-size: 18px;">${companyData.name || 'LOGO'}</span>`;
    processedContent = processedContent.replace(/LOGO/g, logoHtml);

    return processedContent;
};

// Simple Rich Text Editor Component (React 19 compatible)
const RichTextEditor = ({ value, onChange, placeholder = 'Enter content...' }) => {
    const insertFormatting = (before, after = '') => {
        const textarea = document.getElementById('rich-text-editor');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = value || '';
        const selectedText = text.substring(start, end);

        let formattedText = selectedText;
        if (selectedText) {
            formattedText = before + selectedText + after;
        } else {
            formattedText = before + after;
        }

        const newText = text.substring(0, start) + formattedText + text.substring(end);
        onChange(newText);

        // Reset cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
        }, 0);
    };

    const insertHTML = (html) => {
        const textarea = document.getElementById('rich-text-editor');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const text = value || '';
        const newText = text.substring(0, start) + html + text.substring(start);
        onChange(newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + html.length, start + html.length);
        }, 0);
    };

    return (
        <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
                <button
                    type="button"
                    onClick={() => insertFormatting('**', '**')}
                    className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Bold"
                >
                    B
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('*', '*')}
                    className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Italic"
                >
                    I
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('<u>', '</u>')}
                    className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Underline"
                >
                    U
                </button>
                <div className="w-px bg-gray-300 mx-1" />
                <button
                    type="button"
                    onClick={() => insertHTML('\n')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Line Break"
                >
                    ↵
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('\n# ', '')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Heading"
                >
                    H1
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('\n## ', '')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Subheading"
                >
                    H2
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('\n- ', '')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Bullet Point"
                >
                    •
                </button>
                <button
                    type="button"
                    onClick={() => insertFormatting('\n1. ', '')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Numbered List"
                >
                    1.
                </button>
            </div>

            {/* Textarea */}
            <textarea
                id="rich-text-editor"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full p-3 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                rows={8}
                style={{ minHeight: '150px' }}
            />

            {/* Help Text */}
            <div className="bg-gray-50 border-t border-gray-300 px-3 py-2">
                <p className="text-xs text-gray-600">
                    Use toolbar buttons for formatting. Supported: **bold**, *italic*, &lt;u&gt;underline&lt;/u&gt;, #heading, ##subheading, - list, 1. numbered list
                </p>
            </div>
        </div>
    );
};

const DocumentBuilderEnhanced = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('templates'); // 'templates', 'generate', or 'custom'
    const [selectedCategory, setSelectedCategory] = useState('appointment');
    const [selectedTemplates, setSelectedTemplates] = useState({});
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // Preview States
    const [zoomLevel, setZoomLevel] = useState(0.8);
    const [previewFormat, setPreviewFormat] = useState('a4'); // 'a4' or 'letter'

    // Custom Template States
    const [customTemplates, setCustomTemplates] = useState({});
    const [showCreateCustomTemplate, setShowCreateCustomTemplate] = useState(false);

    // Missing Fields Validation Modal States
    const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
    const [missingFieldsData, setMissingFieldsData] = useState({ employee: null, fields: [], category: null });
    const [effectiveDateInput, setEffectiveDateInput] = useState(''); // For user to input effective date in modal
    const [probationPeriodInput, setProbationPeriodInput] = useState(''); // For user to input probation period (months) in modal
    const [noticePeriodInput, setNoticePeriodInput] = useState(''); // For user to input notice period (days) in modal

    // Document Preview Modal States (preview before sending)
    const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
    const [documentPreviewData, setDocumentPreviewData] = useState(null);

    // Add custom CSS for enhanced preview styling
    useEffect(() => {
        
        const style = document.createElement('style');
        style.textContent = `
            .document-preview-enhanced {
                background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            }

            .document-preview-container {
                background: white;
                border-radius: 8px;
                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                margin: 0 auto;
                position: relative;
                border: 1px solid #e5e7eb;
                overflow: hidden;
            }

            .document-preview-content {
                padding: 2cm;
                min-height: 100%;
                background: white;
                color: #111827;
                line-height: 1.6;
                font-size: 12pt;
                font-family: 'Times New Roman', serif;
            }

            .document-preview-content h1,
            .document-preview-content h2,
            .document-preview-content h3 {
                color: #111827;
                font-weight: 600;
                margin-bottom: 0.5em;
            }

            .document-preview-content h1 { font-size: 20pt; }
            .document-preview-content h2 { font-size: 16pt; }
            .document-preview-content h3 { font-size: 14pt; }

            .document-preview-content p {
                margin-bottom: 1em;
                text-align: justify;
            }

            .document-preview-content .header-section,
            .document-preview-content .letterhead {
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 1em;
                margin-bottom: 1.5em;
            }

            .document-preview-content .signature-section {
                margin-top: 3em;
                padding-top: 2em;
                border-top: 1px solid #e5e7eb;
            }

            .document-preview-content .company-logo {
                max-width: 200px;
                max-height: 80px;
                margin-bottom: 1em;
            }

            @media print {
                .document-preview-enhanced {
                    background: none !important;
                    padding: 0 !important;
                }

                .document-preview-container {
                    box-shadow: none !important;
                    border: none !important;
                    margin: 0 !important;
                }

                .document-preview-content {
                    padding: 2cm !important;
                    -webkit-print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }

                .document-preview-controls,
                .document-preview-header,
                .document-preview-footer {
                    display: none !important;
                }
            }

            @media (max-width: 768px) {
                .document-preview-container {
                    margin: 0 1rem;
                    transform: scale(0.7) !important;
                    transform-origin: top left;
                }
            }
        `;

        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const [editingCustomTemplate, setEditingCustomTemplate] = useState(null);
    const [customTemplateForm, setCustomTemplateForm] = useState({
        name: '',
        description: '',
        content: '',
        category: 'appointment'
    });

    // Custom Letter Tab States
    const [customLetterContent, setCustomLetterContent] = useState('');
    const [customLetterSubject, setCustomLetterSubject] = useState('');
    const [customLetterRecipients, setCustomLetterRecipients] = useState([]);
    const [customLetterPreview, setCustomLetterPreview] = useState(false);
    const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
    const [activeEditor, setActiveEditor] = useState(null); // Track which editor is active
    const [savedCustomLetters, setSavedCustomLetters] = useState(() => {
        // Load saved custom letters from localStorage on mount
        try {
            const saved = localStorage.getItem('savedCustomLetters');
            console.log('📜 Loading saved custom letters from localStorage:', saved ? `Found ${JSON.parse(saved).length} letters` : 'None found');
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            console.error('❌ Error loading custom letters from localStorage:', error);
            return [];
        }
    });
    const [selectedCustomLetter, setSelectedCustomLetter] = useState(null); // For Generate tab
    const [customLetterEmployee, setCustomLetterEmployee] = useState(null); // Selected employee for custom letter

    // Generate Document Tab States
    const [generatingDocument, setGeneratingDocument] = useState(null);
    const [editingDocument, setEditingDocument] = useState(null);
    const [documentContent, setDocumentContent] = useState('');
    const [draftDocuments, setDraftDocuments] = useState([]);
    const [generatedDocuments, setGeneratedDocuments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [companyData, setCompanyData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [documentSearchQueries, setDocumentSearchQueries] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState({});
    const [sendOptions, setSendOptions] = useState({});
    const [sendStatus, setSendStatus] = useState({});
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState({});

    // Document categories
    const categories = [
        { id: 'appointment', name: 'Appointment Letter', icon: '📋' },
        { id: 'confirmation', name: 'Confirmation Letter', icon: '✅' },
        { id: 'experience', name: 'Experience Letter', icon: '🏆' },
        { id: 'payslip', name: 'Payslip', icon: '💰' },
        { id: 'promotion', name: 'Promotion Letter', icon: '📈' },
        { id: 'termination', name: 'Termination Letter', icon: '📝' },
        // { id: 'relieving', name: 'Relieving Letter', icon: '👋' },
        // { id: 'salary', name: 'Salary Certificate', icon: '💵' },
        // { id: 'warning', name: 'Warning Letter', icon: '⚠️' },
        // { id: 'holiday', name: 'Holiday Notification', icon: '🎉' }
    ];

    // Templates for each category
    const templates = {
        appointment: [
            {
                id: 'apt-formal',
                name: 'Formal Appointment',
                description: 'Traditional corporate format with detailed terms',
                preview: 'Professional letterhead with comprehensive employment details, role description, and complete terms & conditions.',
                content: `
<div style="font-family: 'Times New Roman', serif; line-height: 1.8; color: #1a1a1a; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Company Header -->
    <div style="text-align: center; border-bottom: 3px double #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 80px; height: 80px; background-color: #f0f0f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #666;">
            LOGO
        </div>
        <div style="font-size: 28px; font-weight: bold; letter-spacing: 2px;">[Company Name]</div>
        <div style="font-size: 14px; color: #555; margin-top: 8px;">[Company Address]</div>
        <div style="font-size: 14px; color: #555;">Tel: [Company Phone] | Email: [Company Email]</div>
    </div>

    <!-- Reference & Date -->
    <div style="display: flex; justify-content: space-between; margin-bottom: 30px; font-size: 14px;">
        <div><strong>Ref:</strong> APT/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <!-- Recipient -->
    <div style="margin-bottom: 25px;">
        <div style="font-weight: bold; font-size: 16px;">[Employee Name]</div>
        <div>[Employee Address]</div>
    </div>

    <!-- Subject -->
    <div style="margin-bottom: 20px; font-weight: bold; text-decoration: underline;">
        Subject: Letter of Appointment
    </div>

    <!-- Salutation -->
    <div style="margin-bottom: 20px;">Dear [Employee Name],</div>

    <!-- Body -->
    <p style="text-align: justify; margin-bottom: 15px;">
        With reference to your application and subsequent interview, we are pleased to offer you the position of <strong>[Job Title]</strong>
        in the <strong>[Department Name]</strong> department at [Company Name], subject to the following terms and conditions:
    </p>

    <!-- Terms Section -->
    <div style="margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">1. APPOINTMENT DETAILS</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <tr><td style="padding: 8px 0; width: 35%;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 8px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 8px 0;"><strong>Reporting To:</strong></td><td>[Manager Name]</td></tr>
            <tr><td style="padding: 8px 0;"><strong>Work Location:</strong></td><td>[Location]</td></tr>
            <tr><td style="padding: 8px 0;"><strong>Date of Joining:</strong></td><td>[DD/MM/YYYY]</td></tr>
        </table>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">2. COMPENSATION</div>
        <p>Your annual Cost to Company (CTC) will be <strong>[Currency] [Amount]</strong> (Rupees [Amount in Words] only) per annum,
        payable monthly. A detailed breakdown of your compensation is attached herewith.</p>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">3. PROBATION PERIOD</div>
        <p>You will be on probation for a period of <strong>[X] months</strong> from the date of joining. Upon successful completion
        of the probation period, your services will be confirmed.</p>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">4. NOTICE PERIOD</div>
        <p>During probation, either party may terminate the employment by giving [Notice Period] days written notice or payment in lieu thereof.
        After confirmation, the notice period will be [Notice Period] days.</p>
    </div>

    <p style="margin-top: 25px; text-align: justify;">
        Please sign and return the duplicate copy of this letter as a token of your acceptance of the above terms and conditions.
    </p>

    <p style="margin-top: 15px;">We welcome you to [Company Name] and wish you a successful career with us.</p>

    <!-- Signature -->
    <div style="margin-top: 50px;">
        <div>Yours sincerely,</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>

    <!-- Acceptance -->
    <div style="margin-top: 60px; border-top: 2px solid #333; padding-top: 20px;">
        <div style="font-weight: bold; margin-bottom: 15px;">ACCEPTANCE</div>
        <p>I, [Employee Name], accept the appointment on the terms and conditions mentioned above.</p>
        <div style="margin-top: 30px; display: flex; justify-content: space-between;">
            <div>
                <div style="border-top: 1px solid #333; width: 200px; margin-top: 50px;"></div>
                <div>Signature</div>
            </div>
            <div>
                <div style="border-top: 1px solid #333; width: 150px; margin-top: 50px;"></div>
                <div>Date</div>
            </div>
        </div>
    </div>
</div>`
            },
            {
                id: 'apt-modern',
                name: 'Modern & Concise',
                description: 'Clean, contemporary layout with essential information',
                preview: 'Streamlined format focusing on key details with a welcoming tone and modern design elements.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <!-- Company Header with Logo -->
    <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 16px; margin-bottom: 24px;">
        <div style="display: flex; align-items: flex-start; gap: 16px;">
            <div style="width: 64px; height: 64px; background-color: #e5e7eb; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #6b7280; flex-shrink: 0;">
                LOGO
            </div>
            <div style="flex: 1;">
                <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">[Company Name]</div>
                <div style="font-size: 14px; margin-bottom: 2px;">[Company Address]</div>
                <div style="font-size: 14px;">Email: [Company Email] | Phone: [Company Phone]</div>
            </div>
        </div>
    </div>

    <!-- Date -->
    <div style="text-align: right; font-size: 14px; margin-bottom: 16px;">
        <div>Date: [DD/MM/YYYY]</div>
    </div>

    <!-- Recipient -->
    <div style="font-size: 14px; margin-bottom: 16px;">
        <div style="font-weight: 600;">[Employee Name]</div>
        <div>[Employee Address]</div>
    </div>

    <!-- Subject -->
    <div style="font-size: 14px; margin-bottom: 16px;">
        <div style="font-weight: 600;">Subject: [Letter Type]</div>
    </div>

    <!-- Salutation -->
    <div style="font-size: 14px; margin-bottom: 16px;">
        <div>Dear [Employee Name],</div>
    </div>

    <!-- Body Content -->
    <p>Congratulations! We're excited to offer you the position of <strong>[Job Title]</strong> at [Company Name]. Your skills and experience impressed us, and we believe you'll be a great addition to our team.</p>

    <div style="background-color: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
        <div style="font-weight: 600; margin-bottom: 8px;">Your Role:</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <div><span style="color: #6b7280;">Designation:</span> <strong>[Job Title]</strong></div>
            <div><span style="color: #6b7280;">Team:</span> <strong>[Department Name]</strong></div>
            <div><span style="color: #6b7280;">Reports to:</span> <strong>[Manager Name]</strong></div>
            <div><span style="color: #6b7280;">Start Date:</span> <strong>[DD/MM/YYYY]</strong></div>
            <div><span style="color: #6b7280;">Compensation:</span> <strong>[Currency] [Amount]/year</strong></div>
            <div><span style="color: #6b7280;">Location:</span> <strong>[Location]</strong></div>
        </div>
    </div>

    <p>You'll have a [X] month probation period to get settled in. During this time, we'll provide all the support you need to succeed.</p>

    <p>To accept this offer, simply sign the attached document and send it back to us. We can't wait to have you on board!</p>

    <div style="margin-top: 40px;">
        <p><strong>Sincerely,</strong></p>
        <p>[HR Name]<br/>
        [HR Title]<br/>
        [Company Name]</p>
    </div>
</div>`
            },
            {
                id: 'apt-startup',
                name: 'Startup Friendly',
                description: 'Casual yet professional with flexible terms',
                preview: 'Friendly language highlighting company culture, growth opportunities, and flexible work arrangements.',
                content: `
<div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.7; color: #2d3748; max-width: 750px; margin: 0 auto; padding: 30px;">
    <!-- Header with gradient accent -->
    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 25px; border-radius: 12px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 60px; height: 60px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #667eea;">
                LOGO
            </div>
            <div style="color: white;">
                <div style="font-size: 22px; font-weight: bold;">[Company Name]</div>
                <div style="font-size: 13px; opacity: 0.9;">Building something amazing together</div>
            </div>
        </div>
    </div>

    <!-- Date -->
    <div style="text-align: right; color: #718096; font-size: 14px; margin-bottom: 20px;">
        [DD/MM/YYYY]
    </div>

    <!-- Greeting -->
    <div style="font-size: 26px; font-weight: bold; color: #2d3748; margin-bottom: 20px;">
        Hey [Employee Name]! 👋
    </div>

    <p style="font-size: 16px; margin-bottom: 20px;">
        We're thrilled to officially welcome you to the <strong>[Company Name]</strong> family! After meeting you,
        we knew you'd be an incredible addition to our team.
    </p>

    <!-- Offer Card -->
    <div style="background: #f7fafc; border-left: 4px solid #667eea; padding: 20px; border-radius: 0 8px 8px 0; margin: 25px 0;">
        <div style="font-weight: bold; font-size: 18px; margin-bottom: 15px; color: #4a5568;">Your Role at [Company Name]</div>
        <div style="display: grid; gap: 12px;">
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">💼</span>
                <span><strong>Position:</strong> [Job Title]</span>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">👥</span>
                <span><strong>Team:</strong> [Department Name]</span>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">🎯</span>
                <span><strong>Reporting to:</strong> [Manager Name]</span>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">📅</span>
                <span><strong>Start Date:</strong> [DD/MM/YYYY]</span>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">📍</span>
                <span><strong>Location:</strong> [Location] (with flexible work options)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">💰</span>
                <span><strong>Compensation:</strong> [Currency] [Amount] per year</span>
            </div>
        </div>
    </div>

    <p style="margin: 20px 0;">
        You'll start with a <strong>[X] month</strong> getting-to-know-each-other period. Don't worry - we're here
        to support you every step of the way!
    </p>

    <!-- What's Next -->
    <div style="background: #e6fffa; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; color: #234e52; margin-bottom: 10px;">🚀 What's Next?</div>
        <ul style="margin: 0; padding-left: 20px; color: #285e61;">
            <li>Sign this letter and send it back to us</li>
            <li>We'll send you onboarding details</li>
            <li>Get ready to make an impact!</li>
        </ul>
    </div>

    <p style="margin: 20px 0;">
        We can't wait to see the amazing things you'll accomplish here. Let's build something great together!
    </p>

    <!-- Signature -->
    <div style="margin-top: 40px;">
        <div style="color: #718096;">Cheers,</div>
        <div style="font-weight: bold; margin-top: 15px;">[HR Name]</div>
        <div style="color: #718096;">[HR Title] at [Company Name]</div>
    </div>

    <!-- Acceptance -->
    <div style="margin-top: 50px; padding-top: 25px; border-top: 2px dashed #e2e8f0;">
        <div style="font-weight: bold; color: #4a5568; margin-bottom: 15px;">✍️ I'm In!</div>
        <p style="color: #718096; font-size: 14px;">
            By signing below, I accept this offer and am excited to join [Company Name]!
        </p>
        <div style="display: flex; gap: 50px; margin-top: 30px;">
            <div>
                <div style="border-bottom: 2px solid #cbd5e0; width: 180px; margin-bottom: 8px;">&nbsp;</div>
                <div style="color: #718096; font-size: 13px;">Signature</div>
            </div>
            <div>
                <div style="border-bottom: 2px solid #cbd5e0; width: 120px; margin-bottom: 8px;">&nbsp;</div>
                <div style="color: #718096; font-size: 13px;">Date</div>
            </div>
        </div>
    </div>
</div>`
            }
        ],
        confirmation: [
            {
                id: 'conf-standard',
                name: 'Standard Confirmation',
                description: 'Classic probation completion letter',
                preview: 'Formal confirmation of permanent employment after successful probation period with updated terms.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Company Header -->
    <div style="border-bottom: 2px solid #2c5282; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 70px; height: 70px; background-color: #e2e8f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #4a5568;">LOGO</div>
            <div>
                <div style="font-size: 24px; font-weight: bold; color: #2c5282;">[Company Name]</div>
                <div style="font-size: 14px; color: #718096;">[Company Address]</div>
            </div>
        </div>
    </div>

    <div style="text-align: right; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
        <div><strong>Ref:</strong> CONF/[Year]/[Employee Code]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold;">[Employee Name]</div>
        <div>Employee Code: [Employee Code]</div>
        <div>Department: [Department Name]</div>
    </div>

    <div style="font-weight: bold; margin-bottom: 20px; text-decoration: underline;">
        Subject: Confirmation of Employment
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        We are pleased to inform you that upon successful completion of your probation period, your services have been confirmed
        with effect from <strong>[Confirmation Date]</strong>.
    </p>

    <p style="text-align: justify;">
        During your probation period, you have demonstrated the skills, dedication, and professionalism that we value at [Company Name].
        We are confident that you will continue to excel in your role as <strong>[Job Title]</strong>.
    </p>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Confirmation Details:</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0; width: 40%;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Date of Joining:</strong></td><td>[DD/MM/YYYY]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Date of Confirmation:</strong></td><td>[Confirmation Date]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Annual CTC:</strong></td><td>[Currency] [Amount]</td></tr>
        </table>
    </div>

    <p>All other terms and conditions of your employment as per your appointment letter remain unchanged.</p>

    <p>Congratulations on your confirmation, and we wish you a successful and rewarding career with [Company Name].</p>

    <div style="margin-top: 50px;">
        <div>Best Regards,</div>
        <div style="margin-top: 30px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>
</div>`
            },
            {
                id: 'conf-performance',
                name: 'Performance-Based',
                description: 'Highlights achievements during probation',
                preview: 'Acknowledges specific contributions and achievements before confirming permanent position.',
                content: `
<div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.7; color: #2d3748; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="background: linear-gradient(135deg, #38a169 0%, #2f855a 100%); padding: 25px; border-radius: 12px; margin-bottom: 30px; color: white;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 60px; height: 60px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #38a169;">LOGO</div>
            <div>
                <div style="font-size: 22px; font-weight: bold;">[Company Name]</div>
                <div style="font-size: 13px; opacity: 0.9;">[Company Address]</div>
            </div>
        </div>
    </div>

    <div style="text-align: right; margin-bottom: 20px; color: #718096; font-size: 14px;">[DD/MM/YYYY]</div>

    <div style="font-size: 24px; font-weight: bold; color: #2f855a; margin-bottom: 25px;">
        Congratulations, [Employee Name]! 🎉
    </div>

    <p>
        It gives us immense pleasure to confirm your appointment as a permanent employee of <strong>[Company Name]</strong>
        with effect from <strong>[Confirmation Date]</strong>.
    </p>

    <div style="background: #f0fff4; border-left: 4px solid #38a169; padding: 20px; margin: 25px 0; border-radius: 0 8px 8px 0;">
        <div style="font-weight: bold; color: #22543d; margin-bottom: 15px;">🌟 Your Achievements During Probation</div>
        <p style="color: #276749;">
            During your probation period, you have consistently demonstrated exceptional performance and dedication.
            Your contributions to the [Department Name] team have been noteworthy, and your positive attitude has
            made you a valued member of our organization.
        </p>
    </div>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Your Confirmed Details:</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 14px;">
            <div><strong>Designation:</strong> [Job Title]</div>
            <div><strong>Department:</strong> [Department Name]</div>
            <div><strong>Date of Joining:</strong> [DD/MM/YYYY]</div>
            <div><strong>Confirmation Date:</strong> [Confirmation Date]</div>
            <div><strong>Reporting To:</strong> [Manager Name]</div>
            <div><strong>Annual CTC:</strong> [Currency] [Amount]</div>
        </div>
    </div>

    <p>
        We look forward to your continued growth and success at [Company Name]. Keep up the excellent work!
    </p>

    <div style="margin-top: 40px;">
        <div style="color: #718096;">Warm Regards,</div>
        <div style="margin-top: 20px; font-weight: bold;">[HR Name]</div>
        <div style="color: #718096;">[HR Title]</div>
    </div>
</div>`
            },
            {
                id: 'conf-detailed',
                name: 'Detailed Confirmation',
                description: 'Comprehensive with updated benefits',
                preview: 'Includes complete breakdown of updated salary, benefits, and long-term employment terms.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 3px double #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 80px; height: 80px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #666;">LOGO</div>
        <div style="font-size: 26px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; color: #666;">[Company Address]</div>
        <div style="font-size: 14px; color: #666;">Tel: [Company Phone] | Email: [Company Email]</div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> CONF/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; font-size: 16px;">[Employee Name]</div>
        <div>[Employee Address]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px;">
        Subject: Confirmation of Services & Revised Terms
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        With reference to your appointment dated [DD/MM/YYYY], we are pleased to confirm your services as a permanent
        employee of [Company Name] with effect from <strong>[Confirmation Date]</strong>.
    </p>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">
            1. EMPLOYMENT DETAILS
        </div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 6px 0; width: 40%;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Reporting Manager:</strong></td><td>[Manager Name]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Work Location:</strong></td><td>[Location]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Date of Joining:</strong></td><td>[DD/MM/YYYY]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Date of Confirmation:</strong></td><td>[Confirmation Date]</td></tr>
        </table>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">
            2. COMPENSATION (Post Confirmation)
        </div>
        <p>Your revised annual compensation with effect from [Confirmation Date] is as follows:</p>
        <div style="background: #f7fafc; padding: 15px; border-radius: 4px; margin-top: 10px;">
            <table style="width: 100%; font-size: 14px;">
                <tr><td style="padding: 5px 0;"><strong>Annual CTC:</strong></td><td style="text-align: right;"><strong>[Currency] [Amount]</strong></td></tr>
            </table>
        </div>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">
            3. NOTICE PERIOD
        </div>
        <p>As a confirmed employee, your notice period will be <strong>[Notice Period] days</strong> from either side.</p>
    </div>

    <p style="margin-top: 25px;">
        All other terms and conditions of your original appointment letter remain unchanged unless specifically modified above.
    </p>

    <p>We congratulate you on successful completion of your probation and look forward to your continued contributions.</p>

    <div style="margin-top: 50px;">
        <div>For [Company Name],</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
    </div>

    <div style="margin-top: 50px; border-top: 2px solid #333; padding-top: 20px;">
        <div style="font-weight: bold; margin-bottom: 10px;">ACKNOWLEDGEMENT</div>
        <p style="font-size: 14px;">I acknowledge receipt of this confirmation letter and accept the terms stated herein.</p>
        <div style="display: flex; justify-content: space-between; margin-top: 30px;">
            <div><div style="border-top: 1px solid #333; width: 180px; margin-top: 40px;"></div><div>Employee Signature</div></div>
            <div><div style="border-top: 1px solid #333; width: 120px; margin-top: 40px;"></div><div>Date</div></div>
        </div>
    </div>
</div>`
            }
        ],
        experience: [
            {
                id: 'exp-basic',
                name: 'Basic Experience',
                description: 'Simple employment verification letter',
                preview: 'Straightforward letter confirming employment duration, designation, and basic responsibilities.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #666;">LOGO</div>
        <div style="font-size: 24px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; color: #666;">[Company Address]</div>
        <div style="font-size: 14px; color: #666;">Tel: [Company Phone] | Email: [Company Email]</div>
    </div>

    <div style="text-align: right; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
        <div><strong>Ref:</strong> EXP/[Year]/[Employee Code]</div>
    </div>

    <div style="text-align: center; font-weight: bold; font-size: 18px; text-decoration: underline; margin-bottom: 30px;">
        EXPERIENCE CERTIFICATE
    </div>

    <div style="margin-bottom: 20px;">To Whom It May Concern,</div>

    <p style="text-align: justify;">
        This is to certify that <strong>[Employee Name]</strong> (Employee Code: [Employee Code]) was employed with
        <strong>[Company Name]</strong> from <strong>[DD/MM/YYYY]</strong> to <strong>[Last Working Day]</strong>.
    </p>

    <p style="text-align: justify;">
        During [his/her] tenure, [he/she] held the position of <strong>[Job Title]</strong> in the
        <strong>[Department Name]</strong> department.
    </p>

    <p style="text-align: justify;">
        We found [him/her] to be sincere, hardworking, and dedicated to [his/her] responsibilities.
        [He/She] is relieved of [his/her] duties on [Last Working Day] upon [his/her] request.
    </p>

    <p>We wish [him/her] all the best in [his/her] future endeavors.</p>

    <div style="margin-top: 60px;">
        <div>For <strong>[Company Name]</strong></div>
        <div style="margin-top: 50px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
    </div>
</div>`
            },
            {
                id: 'exp-detailed',
                name: 'Detailed Experience',
                description: 'Comprehensive with responsibilities and achievements',
                preview: 'In-depth letter covering role, key responsibilities, notable achievements, and skills demonstrated.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="border-bottom: 3px solid #2c5282; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #666;">LOGO</div>
            <div>
                <div style="font-size: 24px; font-weight: bold; color: #2c5282;">[Company Name]</div>
                <div style="font-size: 14px; color: #666;">[Company Address]</div>
                <div style="font-size: 14px; color: #666;">Tel: [Company Phone] | Email: [Company Email]</div>
            </div>
        </div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> EXP/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="text-align: center; font-weight: bold; font-size: 18px; text-decoration: underline; margin-bottom: 30px; color: #2c5282;">
        EXPERIENCE CERTIFICATE
    </div>

    <div style="margin-bottom: 20px;">To Whom It May Concern,</div>

    <p style="text-align: justify;">
        This is to certify that <strong>[Employee Name]</strong> was employed with [Company Name] from
        <strong>[DD/MM/YYYY]</strong> to <strong>[Last Working Day]</strong>, serving in the capacity of
        <strong>[Job Title]</strong> in our <strong>[Department Name]</strong> department.
    </p>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">
            Employment Details:
        </div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0; width: 40%;"><strong>Employee Code:</strong></td><td>[Employee Code]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Date of Joining:</strong></td><td>[DD/MM/YYYY]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Last Working Day:</strong></td><td>[Last Working Day]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Total Experience:</strong></td><td>[Duration]</td></tr>
        </table>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">
            Key Responsibilities:
        </div>
        <p style="text-align: justify;">
            During [his/her] tenure, [Employee Name] was responsible for various key functions within the
            [Department Name] department. [He/She] demonstrated strong professional skills and contributed
            effectively to the team's objectives and organizational goals.
        </p>
    </div>

    <p style="text-align: justify;">
        [Employee Name] was found to be sincere, dedicated, and committed to [his/her] work.
        [He/She] maintained excellent professional conduct and demonstrated good teamwork abilities throughout [his/her] employment.
    </p>

    <p style="text-align: justify;">
        [He/She] has been relieved of [his/her] duties on [Last Working Day] upon resignation.
        We wish [him/her] success in all future professional endeavors.
    </p>

    <div style="margin-top: 60px;">
        <div>For <strong>[Company Name]</strong></div>
        <div style="margin-top: 50px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>
</div>`
            },
            {
                id: 'exp-recommendation',
                name: 'With Recommendation',
                description: 'Includes performance remarks and recommendation',
                preview: 'Experience letter enhanced with positive performance feedback and recommendation for future employers.',
                content: `
<div style="font-family: 'Georgia', serif; line-height: 1.7; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 3px double #4a5568; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 80px; height: 80px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #666;">LOGO</div>
        <div style="font-size: 26px; font-weight: bold; letter-spacing: 1px;">[Company Name]</div>
        <div style="font-size: 14px; color: #666; margin-top: 5px;">[Company Address]</div>
        <div style="font-size: 14px; color: #666;">Tel: [Company Phone] | Email: [Company Email]</div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> EXP/REC/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="text-align: center; font-weight: bold; font-size: 18px; text-decoration: underline; margin-bottom: 30px;">
        EXPERIENCE CERTIFICATE WITH RECOMMENDATION
    </div>

    <div style="margin-bottom: 20px;">To Whom It May Concern,</div>

    <p style="text-align: justify;">
        This is to certify that <strong>[Employee Name]</strong> (Employee Code: [Employee Code]) was a valued member
        of our organization from <strong>[DD/MM/YYYY]</strong> to <strong>[Last Working Day]</strong>, serving as
        <strong>[Job Title]</strong> in the <strong>[Department Name]</strong> department.
    </p>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Employment Summary:</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0; width: 40%;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Duration:</strong></td><td>[DD/MM/YYYY] to [Last Working Day]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Reporting To:</strong></td><td>[Manager Name]</td></tr>
        </table>
    </div>

    <div style="border-left: 4px solid #4a5568; padding-left: 20px; margin: 25px 0;">
        <div style="font-weight: bold; font-style: italic; margin-bottom: 10px;">Performance & Conduct:</div>
        <p style="text-align: justify;">
            During [his/her] tenure, [Employee Name] consistently demonstrated exceptional professionalism,
            dedication, and technical competence. [He/She] was recognized for [his/her] positive attitude,
            excellent problem-solving abilities, and collaborative approach to teamwork.
        </p>
        <p style="text-align: justify;">
            [Employee Name] made significant contributions to the team and was regarded highly by both
            colleagues and management. [His/Her] work ethic and commitment to excellence were exemplary.
        </p>
    </div>

    <div style="background: #edf2f7; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; color: #2d3748; margin-bottom: 10px;">⭐ RECOMMENDATION</div>
        <p style="text-align: justify; font-style: italic;">
            Based on [his/her] outstanding performance and professional conduct, we highly recommend
            [Employee Name] for any suitable position. [He/She] would be a valuable asset to any organization
            and we have no hesitation in endorsing [his/her] candidature.
        </p>
    </div>

    <p style="text-align: justify;">
        [Employee Name] left our organization on [his/her] own volition to pursue other career opportunities.
        We wish [him/her] all the very best for future endeavors.
    </p>

    <div style="margin-top: 60px;">
        <div>With Best Wishes,</div>
        <div style="margin-top: 50px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>
</div>`
            }
        ],
        payslip: [
            {
                id: 'pay-standard',
                name: 'Standard Payslip',
                description: 'Traditional salary slip format',
                preview: 'Classic payslip with earnings, deductions, and net pay breakdown. Company logo and details.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.4; color: #333; max-width: 800px; margin: 0 auto; padding: 30px; border: 1px solid #ddd;">
    <!-- Header -->
    <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 60px; height: 60px; background: #e2e8f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 10px;">LOGO</div>
            <div>
                <div style="font-size: 20px; font-weight: bold;">[Company Name]</div>
                <div style="font-size: 12px; color: #666;">[Company Address]</div>
            </div>
        </div>
        <div style="text-align: right;">
            <div style="font-size: 16px; font-weight: bold;">PAYSLIP</div>
            <div style="font-size: 12px; color: #666;">[Month] [Year]</div>
        </div>
    </div>

    <!-- Employee Details -->
    <div style="background: #f7fafc; padding: 15px; border-radius: 4px; margin-bottom: 20px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 13px;">
            <div><strong>Employee Name:</strong> [Employee Name]</div>
            <div><strong>Employee Code:</strong> [Employee Code]</div>
            <div><strong>Designation:</strong> [Job Title]</div>
            <div><strong>Department:</strong> [Department Name]</div>
            <div><strong>Date of Joining:</strong> [DD/MM/YYYY]</div>
            <div><strong>Bank Account:</strong> XXXX-XXXX-[Last 4]</div>
        </div>
    </div>

    <!-- Earnings & Deductions -->
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
        <!-- Earnings -->
        <div>
            <div style="background: #48bb78; color: white; padding: 10px; font-weight: bold; border-radius: 4px 4px 0 0;">EARNINGS</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Basic Salary</td><td style="text-align: right; padding: 8px;">[Basic Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">HRA</td><td style="text-align: right; padding: 8px;">[HRA Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Conveyance</td><td style="text-align: right; padding: 8px;">[Conveyance Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Special Allowance</td><td style="text-align: right; padding: 8px;">[Special Amount]</td></tr>
                <tr style="background: #f0fff4; font-weight: bold;"><td style="padding: 10px;">Gross Earnings</td><td style="text-align: right; padding: 10px;">[Gross Earnings]</td></tr>
            </table>
        </div>
        <!-- Deductions -->
        <div>
            <div style="background: #fc8181; color: white; padding: 10px; font-weight: bold; border-radius: 4px 4px 0 0;">DEDUCTIONS</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">PF (Employee)</td><td style="text-align: right; padding: 8px;">[PF Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Professional Tax</td><td style="text-align: right; padding: 8px;">[PT Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Income Tax (TDS)</td><td style="text-align: right; padding: 8px;">[TDS Amount]</td></tr>
                <tr style="border-bottom: 1px solid #e2e8f0;"><td style="padding: 8px;">Other Deductions</td><td style="text-align: right; padding: 8px;">[Other Amount]</td></tr>
                <tr style="background: #fff5f5; font-weight: bold;"><td style="padding: 10px;">Total Deductions</td><td style="text-align: right; padding: 10px;">[Total Deductions]</td></tr>
            </table>
        </div>
    </div>

    <!-- Net Pay -->
    <div style="background: #2c5282; color: white; padding: 15px; border-radius: 4px; text-align: center; margin-bottom: 20px;">
        <div style="font-size: 14px;">NET PAY</div>
        <div style="font-size: 28px; font-weight: bold;">[Currency] [Net Amount]</div>
        <div style="font-size: 12px; opacity: 0.9;">(Rupees [Amount in Words] Only)</div>
    </div>

    <div style="font-size: 11px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 15px;">
        This is a computer-generated document. No signature required.
    </div>
</div>`
            },
            {
                id: 'pay-detailed',
                name: 'Detailed Breakdown',
                description: 'Comprehensive with all components',
                preview: 'Detailed breakdown of all salary components, tax calculations, and statutory deductions.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.4; color: #333; max-width: 850px; margin: 0 auto; padding: 25px; border: 1px solid #ccc;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 3px solid #333; padding-bottom: 15px; margin-bottom: 20px;">
        <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 10px; display: flex; align-items: center; justify-content: center; font-size: 11px;">LOGO</div>
        <div style="font-size: 22px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 13px; color: #666;">[Company Address]</div>
        <div style="font-size: 16px; font-weight: bold; margin-top: 10px; background: #333; color: white; padding: 5px;">SALARY SLIP - [Month] [Year]</div>
    </div>

    <!-- Employee Info Grid -->
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; font-size: 12px;">
        <div style="background: #f7fafc; padding: 12px; border-radius: 4px;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #2c5282;">Employee Information</div>
            <table style="width: 100%;"><tr><td>Name:</td><td><strong>[Employee Name]</strong></td></tr>
            <tr><td>Employee ID:</td><td>[Employee Code]</td></tr>
            <tr><td>Designation:</td><td>[Job Title]</td></tr>
            <tr><td>Department:</td><td>[Department Name]</td></tr></table>
        </div>
        <div style="background: #f7fafc; padding: 12px; border-radius: 4px;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #2c5282;">Payment Information</div>
            <table style="width: 100%;"><tr><td>Pay Period:</td><td>[Month] [Year]</td></tr>
            <tr><td>Working Days:</td><td>[Working Days]</td></tr>
            <tr><td>Days Paid:</td><td>[Days Paid]</td></tr>
            <tr><td>LOP Days:</td><td>[LOP Days]</td></tr></table>
        </div>
    </div>

    <!-- Detailed Breakdown Table -->
    <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px;">
        <thead><tr style="background: #2c5282; color: white;"><th style="padding: 10px; text-align: left; width: 50%;">EARNINGS</th><th style="padding: 10px; text-align: right;">Amount ([Currency])</th><th style="padding: 10px; text-align: left; width: 35%;">DEDUCTIONS</th><th style="padding: 10px; text-align: right;">Amount ([Currency])</th></tr></thead>
        <tbody>
            <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">Basic Salary</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[Basic Amount]</td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">Provident Fund</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[PF Amount]</td></tr>
            <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">House Rent Allowance</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[HRA Amount]</td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">Professional Tax</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[PT Amount]</td></tr>
            <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">Conveyance Allowance</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[Conveyance Amount]</td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">TDS/Income Tax</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[TDS Amount]</td></tr>
            <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">Special Allowance</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[Special Amount]</td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">ESI</td><td style="padding: 8px; text-align: right; border-bottom: 1px solid #e2e8f0;">[ESI Amount]</td></tr>
            <tr style="background: #f0f4f8; font-weight: bold;"><td style="padding: 10px;">GROSS EARNINGS</td><td style="padding: 10px; text-align: right;">[Gross Amount]</td><td style="padding: 10px;">TOTAL DEDUCTIONS</td><td style="padding: 10px; text-align: right;">[Total Deductions]</td></tr>
        </tbody>
    </table>

    <!-- Net Pay Section -->
    <div style="background: linear-gradient(135deg, #2c5282 0%, #1a365d 100%); color: white; padding: 20px; border-radius: 8px; text-align: center;">
        <div style="font-size: 14px; opacity: 0.9;">NET SALARY PAYABLE</div>
        <div style="font-size: 32px; font-weight: bold; margin: 8px 0;">[Currency] [Net Amount]</div>
        <div style="font-size: 12px; opacity: 0.8;">([Amount in Words] Only)</div>
    </div>

    <div style="margin-top: 15px; font-size: 10px; color: #888; text-align: center;">
        This is a system generated payslip and does not require signature. For queries, contact HR at [Company Email]
    </div>
</div>`
            },
            {
                id: 'pay-cognizant',
                name: 'Cognizant Style',
                description: 'Cognizant-style format with 4-column employee info',
                preview: 'Clean format with complete employee details, 2-column earnings/deductions tables, and net pay in words.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.4; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ddd;">
    <!-- Header -->
    <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px;">
        <div style="font-size: 20px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; font-weight: bold; text-align: right;">PAYSLIP FOR THE MONTH OF [Month] [Year]</div>
    </div>

    <!-- Employee Info (4 Columns) -->
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 15px; margin-bottom: 15px; font-size: 11px;">
        <div>
            <div style="font-weight: bold; margin-bottom: 5px; border-bottom: 1px solid #ccc; padding-bottom: 3px;">Personal</div>
            <div>Employee Number: [Employee Code]</div>
            <div>Date Joined: [Joining Date]</div>
            <div>Department: [Department]</div>
        </div>
        <div>
            <div style="font-weight: bold; margin-bottom: 5px; border-bottom: 1px solid #ccc; padding-bottom: 3px;">Professional</div>
            <div>Designation: [Job Title]</div>
            <div>Sub Department: [Sub Department]</div>
            <div>Payment Mode: [Payment Mode]</div>
        </div>
        <div>
            <div style="font-weight: bold; margin-bottom: 5px; border-bottom: 1px solid #ccc; padding-bottom: 3px;">Financial</div>
            <div>Bank: [Bank Name]</div>
            <div>Bank IFSC: [Bank IFSC]</div>
            <div>Bank Account: [Account Number]</div>
        </div>
        <div>
            <div style="font-weight: bold; margin-bottom: 5px; border-bottom: 1px solid #ccc; padding-bottom: 3px;">Identity</div>
            <div>UAN: [UAN Number]</div>
            <div>PF Number: [PF Number]</div>
            <div>PAN Number: [PAN Number]</div>
        </div>
    </div>

    <!-- Attendance Row -->
    <div style="display: flex; gap: 30px; margin-bottom: 20px; font-size: 11px; padding: 8px; background: #f5f5f5;">
        <div><strong>Days Paid:</strong> [Days Paid]</div>
        <div><strong>LOP Days:</strong> [LOP Days]</div>
        <div><strong>Total Working Days:</strong> [Working Days]</div>
    </div>

    <!-- EARNINGS Table (2 columns) -->
    <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 20px; border: 1px solid #000;">
        <thead>
            <tr style="background: #f0f0f0; border-bottom: 1px solid #000;">
                <th colspan="4" style="padding: 8px; text-align: left; border-left: 1px solid #000;">EARNINGS</th>
                <th style="padding: 8px; text-align: right; border-left: 1px solid #000;">Amount</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td colspan="4" style="padding: 6px; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">Basic</td>
                <td style="padding: 6px; text-align: right; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">[Basic Salary]</td>
            </tr>
            <tr>
                <td colspan="4" style="padding: 6px; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">HRA</td>
                <td style="padding: 6px; text-align: right; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">[HRA]</td>
            </tr>
            <tr>
                <td colspan="4" style="padding: 6px; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">Special Allowance</td>
                <td style="padding: 6px; text-align: right; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">[Special Allowance]</td>
            </tr>
            <tr style="background: #f9f9f9; font-weight: bold;">
                <td colspan="4" style="padding: 8px; border-left: 1px solid #000;">Total Earnings (A)</td>
                <td style="padding: 8px; text-align: right; border-left: 1px solid #000;">[Gross Salary]</td>
            </tr>
        </tbody>
    </table>

    <!-- TAXES & DEDUCTIONS Table (2 columns) -->
    <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 20px; border: 1px solid #000;">
        <thead>
            <tr style="background: #f0f0f0; border-bottom: 1px solid #000;">
                <th colspan="2" style="padding: 8px; text-align: left; border-left: 1px solid #000;">TAXES & DEDUCTIONS</th>
                <th style="padding: 8px; text-align: right; border-left: 1px solid #000;">Amount</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td colspan="2" style="padding: 6px; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">Professional Tax</td>
                <td style="padding: 6px; text-align: right; border-left: 1px solid #000; border-bottom: 1px solid #ddd;">[Professional Tax]</td>
            </tr>
            <tr style="background: #f9f9f9; font-weight: bold;">
                <td colspan="2" style="padding: 8px; border-left: 1px solid #000;">Total Taxes & Deductions (B)</td>
                <td style="padding: 8px; text-align: right; border-left: 1px solid #000;">[Total Deductions]</td>
            </tr>
        </tbody>
    </table>

    <!-- Net Pay Section -->
    <div style="padding: 15px; text-align: center; margin-bottom: 10px; background: #f9f9f9; border: 1px solid #ddd;">
        <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">Net Salary Payable ( A - B )</div>
        <div style="font-size: 18px; font-weight: bold;">[Currency] [Net Salary]</div>
    </div>

    <!-- Amount in Words -->
    <div style="padding: 10px; text-align: center; margin-bottom: 20px; font-size: 11px; background: #f9f9f9; border: 1px solid #ddd;">
        <div>Net Salary in words</div>
        <div style="font-weight: bold; margin-top: 5px;">[Amount in Words]</div>
    </div>

    <!-- Footer -->
    <div style="font-size: 10px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 10px;">
        This is a system generated payslip and does not require signature.
    </div>
</div>`
            },
            {
                id: 'pay-modern',
                name: 'Modern Visual',
                description: 'Contemporary design with visual elements',
                preview: 'Modern layout with visual indicators, charts, and easy-to-read salary breakdown.',
                content: `
<div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.5; color: #2d3748; max-width: 800px; margin: 0 auto; padding: 30px; background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%); border-radius: 12px;">
    <!-- Header -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px;">
        <div style="display: flex; align-items: center; gap: 12px;">
            <div style="width: 50px; height: 50px; background: white; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; font-size: 9px;">LOGO</div>
            <div><div style="font-size: 18px; font-weight: bold;">[Company Name]</div><div style="font-size: 11px; color: #718096;">Payslip for [Month] [Year]</div></div>
        </div>
        <div style="background: #4299e1; color: white; padding: 8px 16px; border-radius: 20px; font-size: 12px; font-weight: bold;">PAYSLIP</div>
    </div>

    <!-- Employee Card -->
    <div style="background: white; border-radius: 12px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 50px; height: 50px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: bold;">[Initials]</div>
            <div style="flex: 1;"><div style="font-size: 16px; font-weight: bold;">[Employee Name]</div><div style="font-size: 12px; color: #718096;">[Job Title] • [Department Name]</div></div>
            <div style="text-align: right; font-size: 11px; color: #718096;"><div>ID: [Employee Code]</div><div>Since: [DD/MM/YYYY]</div></div>
        </div>
    </div>

    <!-- Earnings & Deductions Cards -->
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px;">
        <div style="background: white; border-radius: 12px; padding: 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 15px;"><div style="width: 8px; height: 8px; background: #48bb78; border-radius: 50%;"></div><span style="font-weight: bold; color: #2f855a;">Earnings</span></div>
            <div style="font-size: 13px;"><div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>Basic</span><span>[Basic Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>HRA</span><span>[HRA Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>Allowances</span><span>[Allowance Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0; font-weight: bold; color: #2f855a;"><span>Total</span><span>[Gross Amount]</span></div></div>
        </div>
        <div style="background: white; border-radius: 12px; padding: 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 15px;"><div style="width: 8px; height: 8px; background: #fc8181; border-radius: 50%;"></div><span style="font-weight: bold; color: #c53030;">Deductions</span></div>
            <div style="font-size: 13px;"><div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>PF</span><span>[PF Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>Tax</span><span>[TDS Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;"><span>Others</span><span>[Other Amount]</span></div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0; font-weight: bold; color: #c53030;"><span>Total</span><span>[Total Deductions]</span></div></div>
        </div>
    </div>

    <!-- Net Pay -->
    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; padding: 25px; text-align: center; color: white;">
        <div style="font-size: 12px; opacity: 0.9; margin-bottom: 5px;">Take Home Pay</div>
        <div style="font-size: 36px; font-weight: bold;">[Currency] [Net Amount]</div>
    </div>
</div>`
            }
        ],
        promotion: [
            {
                id: 'prom-formal',
                name: 'Formal Promotion',
                description: 'Traditional promotion announcement',
                preview: 'Official letter announcing promotion with new designation, salary, and responsibilities.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 11px;">LOGO</div>
        <div style="font-size: 24px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; color: #666;">[Company Address]</div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> PROM/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold;">[Employee Name]</div>
        <div>Employee Code: [Employee Code]</div>
        <div>Department: [Department Name]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px;">
        Subject: Promotion Letter
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        We are pleased to inform you that based on your performance, dedication, and contributions to the organization,
        the management has decided to promote you to the position of <strong>[New Designation]</strong> in the
        <strong>[Department Name]</strong> department, effective <strong>[Effective Date]</strong>.
    </p>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Promotion Details:</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0; width: 40%;"><strong>Previous Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>New Designation:</strong></td><td>[New Designation]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Effective Date:</strong></td><td>[Effective Date]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Revised CTC:</strong></td><td>[Currency] [New Amount] per annum</td></tr>
        </table>
    </div>

    <p>We congratulate you on this achievement and expect you to continue your excellent performance in your new role.</p>

    <div style="margin-top: 50px;">
        <div>Best Regards,</div>
        <div style="margin-top: 30px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>
</div>`
            },
            {
                id: 'prom-appreciative',
                name: 'Appreciative Tone',
                description: 'Highlights achievements and contributions',
                preview: 'Warm letter recognizing past achievements before announcing well-deserved promotion.',
                content: `
<div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.7; color: #2d3748; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%); padding: 25px; border-radius: 12px; margin-bottom: 30px; color: white;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 60px; height: 60px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #ed8936;">LOGO</div>
            <div><div style="font-size: 22px; font-weight: bold;">[Company Name]</div><div style="font-size: 13px; opacity: 0.9;">[Company Address]</div></div>
        </div>
    </div>

    <div style="text-align: right; color: #718096; font-size: 14px; margin-bottom: 20px;">[DD/MM/YYYY]</div>

    <div style="font-size: 26px; font-weight: bold; color: #c05621; margin-bottom: 25px;">
        Congratulations on Your Promotion! 🎉
    </div>

    <div style="margin-bottom: 20px;">Dear [Employee Name],</div>

    <p>It brings us great joy to recognize your exceptional contributions and announce your well-deserved promotion!</p>

    <div style="background: #fffaf0; border-left: 4px solid #ed8936; padding: 20px; margin: 25px 0; border-radius: 0 8px 8px 0;">
        <div style="font-weight: bold; color: #c05621; margin-bottom: 10px;">🌟 Your Journey So Far</div>
        <p style="color: #744210;">
            Since joining us as [Job Title], you have consistently demonstrated outstanding performance,
            leadership qualities, and commitment to excellence. Your hard work has not gone unnoticed, and
            we are thrilled to elevate you to a role that matches your capabilities.
        </p>
    </div>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <div style="font-weight: bold; margin-bottom: 15px;">Your New Role:</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 14px;">
            <div><strong>New Title:</strong> [New Designation]</div>
            <div><strong>Effective From:</strong> [Effective Date]</div>
            <div><strong>Department:</strong> [Department Name]</div>
            <div><strong>Revised Package:</strong> [Currency] [New Amount]/year</div>
        </div>
    </div>

    <p>We have full confidence in your abilities and look forward to your continued success in this new chapter!</p>

    <div style="margin-top: 40px;">
        <div style="color: #718096;">With appreciation,</div>
        <div style="margin-top: 20px; font-weight: bold;">[HR Name]</div>
        <div style="color: #718096;">[HR Title]</div>
    </div>
</div>`
            },
            {
                id: 'prom-comprehensive',
                name: 'Comprehensive Package',
                description: 'Detailed with complete updated package',
                preview: 'Complete details of new role, updated compensation package, benefits, and future expectations.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 3px double #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 80px; height: 80px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 12px;">LOGO</div>
        <div style="font-size: 26px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; color: #666;">[Company Address]</div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> PROM/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; font-size: 16px;">[Employee Name]</div>
        <div>[Employee Address]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px;">
        Subject: Promotion and Revised Compensation
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        In recognition of your outstanding performance and valuable contributions, we are pleased to announce your
        promotion with revised compensation effective <strong>[Effective Date]</strong>.
    </p>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">1. PROMOTION DETAILS</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 6px 0; width: 40%;"><strong>Previous Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>New Designation:</strong></td><td>[New Designation]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Reporting To:</strong></td><td>[Manager Name]</td></tr>
            <tr><td style="padding: 6px 0;"><strong>Effective Date:</strong></td><td>[Effective Date]</td></tr>
        </table>
    </div>

    <div style="margin: 25px 0;">
        <div style="font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px;">2. REVISED COMPENSATION</div>
        <div style="background: #f7fafc; padding: 15px; border-radius: 4px;">
            <table style="width: 100%; font-size: 14px;">
                <tr><td style="padding: 6px 0;"><strong>Previous CTC:</strong></td><td style="text-align: right;">[Currency] [Amount]</td></tr>
                <tr><td style="padding: 6px 0;"><strong>New CTC:</strong></td><td style="text-align: right; font-weight: bold; color: #2f855a;">[Currency] [New Amount]</td></tr>
                <tr><td style="padding: 6px 0;"><strong>Increment:</strong></td><td style="text-align: right;">[Increment Percentage]%</td></tr>
            </table>
        </div>
    </div>

    <p style="margin-top: 25px;">Congratulations on this achievement! We look forward to your continued excellence.</p>

    <div style="margin-top: 50px;">
        <div>For [Company Name],</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
    </div>

    <div style="margin-top: 50px; border-top: 2px solid #333; padding-top: 20px;">
        <div style="font-weight: bold; margin-bottom: 10px;">ACKNOWLEDGEMENT</div>
        <p style="font-size: 14px;">I accept the promotion and revised terms as mentioned above.</p>
        <div style="display: flex; justify-content: space-between; margin-top: 30px;">
            <div><div style="border-top: 1px solid #333; width: 180px; margin-top: 40px;"></div><div>Employee Signature</div></div>
            <div><div style="border-top: 1px solid #333; width: 120px; margin-top: 40px;"></div><div>Date</div></div>
        </div>
    </div>
</div>`
            }
        ],
        termination: [
            {
                id: 'term-standard',
                name: 'Standard Termination',
                description: 'Professional separation letter',
                preview: 'Formal termination letter with notice period, final settlement details, and clearance process.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px;">LOGO</div>
            <div><div style="font-size: 24px; font-weight: bold;">[Company Name]</div><div style="font-size: 14px; color: #666;">[Company Address]</div></div>
        </div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> TERM/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold;">[Employee Name]</div>
        <div>Employee Code: [Employee Code]</div>
        <div>[Employee Address]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px;">
        Subject: Acceptance of Resignation / Termination of Employment
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        This letter is to confirm that your employment with [Company Name] will end on <strong>[Last Working Day]</strong>.
    </p>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Separation Details:</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0; width: 40%;"><strong>Designation:</strong></td><td>[Job Title]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Department:</strong></td><td>[Department Name]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Date of Joining:</strong></td><td>[DD/MM/YYYY]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Last Working Day:</strong></td><td>[Last Working Day]</td></tr>
        </table>
    </div>

    <p style="text-align: justify;">
        Please ensure to complete the exit formalities, including handover of company assets, completion of
        pending work, and clearance from all departments. Your full and final settlement will be processed
        after completion of the clearance process.
    </p>

    <p>We wish you all the best in your future endeavors.</p>

    <div style="margin-top: 50px;">
        <div>For [Company Name],</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
    </div>
</div>`
            },
            {
                id: 'term-mutual',
                name: 'Mutual Separation',
                description: 'Amicable parting with appreciation',
                preview: 'Respectful letter acknowledging contributions before mutually agreed separation terms.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 2px solid #718096; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; font-size: 11px;">LOGO</div>
        <div style="font-size: 24px; font-weight: bold;">[Company Name]</div>
        <div style="font-size: 14px; color: #666;">[Company Address]</div>
    </div>

    <div style="text-align: right; margin-bottom: 25px; font-size: 14px;">[DD/MM/YYYY]</div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; font-size: 16px;">[Employee Name]</div>
        <div>[Employee Address]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px;">
        Subject: Mutual Separation Agreement
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        This letter confirms the mutual agreement to end your employment with [Company Name] effective
        <strong>[Last Working Day]</strong>.
    </p>

    <div style="background: #f7fafc; border-left: 4px solid #718096; padding: 20px; margin: 25px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Appreciation</div>
        <p>
            We would like to express our gratitude for your contributions during your tenure at [Company Name].
            Your dedication and professionalism have been valued by the team and organization.
        </p>
    </div>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Separation Terms:</div>
        <table style="width: 100%; font-size: 14px;">
            <tr><td style="padding: 5px 0;"><strong>Last Working Day:</strong></td><td>[Last Working Day]</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Notice Period Served:</strong></td><td>[Notice Period] days</td></tr>
            <tr><td style="padding: 5px 0;"><strong>Full & Final Settlement:</strong></td><td>Within 45 days of last working day</td></tr>
        </table>
    </div>

    <p>We wish you success in your future career and personal endeavors.</p>

    <div style="margin-top: 50px;">
        <div>With best wishes,</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
        <div>[Company Name]</div>
    </div>
</div>`
            },
            {
                id: 'term-immediate',
                name: 'Immediate Termination',
                description: 'For cause with policy reference',
                preview: 'Direct termination letter citing policy violations with immediate effect and settlement terms.',
                content: `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px;">
    <!-- Header -->
    <div style="border-bottom: 3px solid #c53030; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px;">
            <div style="width: 70px; height: 70px; background: #e2e8f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px;">LOGO</div>
            <div><div style="font-size: 24px; font-weight: bold;">[Company Name]</div><div style="font-size: 14px; color: #666;">[Company Address]</div></div>
        </div>
    </div>

    <div style="background: #fff5f5; border: 1px solid #fc8181; padding: 10px 15px; border-radius: 4px; margin-bottom: 20px; font-size: 14px; color: #c53030;">
        <strong>CONFIDENTIAL</strong>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 25px; font-size: 14px;">
        <div><strong>Ref:</strong> TERM/[Year]/[Employee Code]</div>
        <div><strong>Date:</strong> [DD/MM/YYYY]</div>
    </div>

    <div style="margin-bottom: 20px;">
        <div style="font-weight: bold;">[Employee Name]</div>
        <div>Employee Code: [Employee Code]</div>
        <div>[Employee Address]</div>
    </div>

    <div style="font-weight: bold; text-decoration: underline; margin-bottom: 20px; color: #c53030;">
        Subject: Termination of Employment - With Immediate Effect
    </div>

    <div style="margin-bottom: 15px;">Dear [Employee Name],</div>

    <p style="text-align: justify;">
        This letter serves as formal notice that your employment with [Company Name] stands terminated
        with immediate effect from <strong>[DD/MM/YYYY]</strong>.
    </p>

    <div style="background: #fff5f5; padding: 20px; border-radius: 8px; margin: 25px 0; border: 1px solid #feb2b2;">
        <div style="font-weight: bold; margin-bottom: 10px; color: #c53030;">Reason for Termination:</div>
        <p>This action is being taken due to violation of company policies and code of conduct as outlined
        in the employee handbook.</p>
    </div>

    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <div style="font-weight: bold; margin-bottom: 10px;">Immediate Requirements:</div>
        <ul style="margin: 0; padding-left: 20px; font-size: 14px;">
            <li>Return all company property including ID card, laptop, and any other assets</li>
            <li>Complete exit clearance process</li>
            <li>Handover any pending work or documentation</li>
        </ul>
    </div>

    <p style="text-align: justify;">
        Your full and final settlement will be processed as per company policy after completion of
        all clearance formalities and return of company property.
    </p>

    <div style="margin-top: 50px;">
        <div>For [Company Name],</div>
        <div style="margin-top: 40px; font-weight: bold;">[HR Name]</div>
        <div>[HR Title]</div>
    </div>
</div>`
            }
        ]
    };


    // Quill editor modules configuration
    // ReactQuill modules and formats removed - using RichTextEditor instead

    // Load data on component mount
    useEffect(() => {
        fetchSelectedTemplates();
        fetchEmployees();
        fetchCompany();
        loadDraftDocuments();
        loadGeneratedDocuments();
        loadCustomTemplates();
        loadCustomLettersFromBackend();
    }, []);

    // Load custom letters from backend API (persisted across sessions)
    const loadCustomLettersFromBackend = async () => {
        try {
            console.log('📜 Loading custom letters from backend API...');
            const response = await api.get('/api/documents/custom-letters');
            if (response.data && response.data.success && response.data.letters) {
                const backendLetters = response.data.letters;
                console.log(`✅ Loaded ${backendLetters.length} custom letters from backend`);

                if (backendLetters.length > 0) {
                    // Backend has letters - use them as source of truth
                    setSavedCustomLetters(backendLetters);
                    // Also update localStorage for offline access
                    localStorage.setItem('savedCustomLetters', JSON.stringify(backendLetters));
                    console.log('📜 Custom letters synced to localStorage');
                }
            } else {
                console.log('📜 No custom letters found in backend');
            }
        } catch (error) {
            console.warn('⚠️ Failed to load custom letters from backend:', error.message);
            // Keep localStorage data as fallback (already loaded via useState initializer)
        }
    };

    const fetchSelectedTemplates = async () => {
        const localTemplates = documentTemplateService.getLocalStorageTemplates();
        if (Object.keys(localTemplates).length > 0) {
            setSelectedTemplates(localTemplates);
        }

        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        try {
            setLoading(true);
            const backendTemplates = await documentTemplateService.getSelectedTemplates();
            if (backendTemplates && Object.keys(backendTemplates).length > 0) {
                setSelectedTemplates(backendTemplates);
                documentTemplateService.saveToLocalStorage(backendTemplates);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            console.log('🔄 Fetching employees...');
            const employeesData = await employeeService.getAllEmployees();
            console.log('📊 Employees data received:', employeesData?.length || 0, 'employees');

            if (Array.isArray(employeesData) && employeesData.length > 0) {
                console.log('✅ Employee data loaded successfully:', employeesData.map(e => `${e.first_name} ${e.last_name}`).slice(0, 3));
                setEmployees(employeesData);
            } else {
                console.warn('⚠️ No employees received, ensuring test data is available...');
                // Force the service to create test employees if API returns empty
                const testEmployees = employeeService.getLocalStorageEmployees();
                console.log('🧪 Using test employees:', testEmployees?.length || 0, 'employees');
                setEmployees(testEmployees || []);
            }
        } catch (error) {
            console.error('❌ Error fetching employees:', error);
            // Fallback to test employees on error
            const testEmployees = employeeService.getLocalStorageEmployees();
            console.log('🧪 Fallback to test employees:', testEmployees?.length || 0, 'employees');
            setEmployees(testEmployees || []);
        }
    };

    const fetchCompany = async () => {
        try {
            console.log('🔄 Fetching company data...');
            const response = await api.get('/api/companies/me');
            if (response.data && response.data.success) {
                const company = response.data.data;
                console.log('✅ Company data loaded:', company.name);
                console.log('📸 Company logo_url from DB:', company.logo_url);
                console.log('📦 Full company data:', company);

                // ALWAYS fetch company settings for logo AND address
                try {
                    const settingsResponse = await api.get('/api/company/settings');
                    const settings = settingsResponse.data.data || {};
                    console.log('📋 Company settings loaded:', { hasLogo: !!settings.logo, hasAddress: !!settings.address });

                    // Use logo from settings if not in companies table
                    if (!company.logo_url && settings.logo) {
                        console.log('✅ Using logo from company settings');
                        company.logo_url = settings.logo;
                    }

                    // Use address from settings (format object to string)
                    if (settings.address && typeof settings.address === 'object') {
                        const addr = settings.address;
                        company.address = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean).join(', ');
                        console.log('✅ Address from company settings:', company.address);
                    } else if (settings.address) {
                        company.address = settings.address;
                        console.log('✅ Address from company settings (string):', company.address);
                    }

                    // Also get other settings if needed
                    if (!company.email && settings.email) company.email = settings.email;
                    if (!company.phone && settings.phone) company.phone = settings.phone;
                } catch (settingsError) {
                    console.error('❌ Error fetching company settings:', settingsError);
                }

                // Final fallback to localStorage
                if (!company.logo_url) {
                    const localLogo = localStorage.getItem('companyLogo');
                    console.log('📸 Company logo from localStorage:', localLogo ? 'Found' : 'Not found');
                    if (localLogo) {
                        console.log('⚠️ Using localStorage logo as final fallback');
                        company.logo_url = localLogo;
                    }
                }

                // Ensure company name has a value - fallback to localStorage or domain
                if (!company.name) {
                    const localName = localStorage.getItem('companyName');
                    if (localName) {
                        console.log('⚠️ Using localStorage company name as fallback');
                        company.name = localName;
                    } else if (company.domain) {
                        // Use domain as fallback (capitalize first letter)
                        company.name = company.domain.charAt(0).toUpperCase() + company.domain.slice(1);
                        console.log('⚠️ Using domain as company name fallback:', company.name);
                    }
                }

                // Also store to localStorage for future use (with error handling for quota)
                try {
                    if (company.name) {
                        localStorage.setItem('companyName', company.name);
                    }
                    if (company.address) {
                        localStorage.setItem('companyAddress', company.address);
                    }
                } catch (storageError) {
                    console.warn('Could not save company data to localStorage:', storageError.message);
                }

                // Try to save logo separately (large base64 strings can exceed quota)
                if (company.logo_url) {
                    try {
                        localStorage.setItem('companyLogo', company.logo_url);
                    } catch (logoStorageError) {
                        console.warn('Could not save logo to localStorage (too large):', logoStorageError.message);
                        // Logo will still be in companyData state, just not cached
                    }
                }

                setCompanyData(company);
            } else {
                console.warn('⚠️ API returned success=false or no data');
                throw new Error('No company data returned');
            }
        } catch (error) {
            console.error('❌ Error fetching company data:', error);
            // Fallback to localStorage for backward compatibility
            setCompanyData({
                name: localStorage.getItem('companyName') || 'Company Name',
                address: localStorage.getItem('companyAddress') || 'Company Address Line 1, City, State - PIN',
                email: localStorage.getItem('companyEmail') || 'hr@company.com',
                phone: localStorage.getItem('companyPhone') || '+1234567890',
                logo_url: localStorage.getItem('companyLogo') || null
            });
        }
    };

    const loadDraftDocuments = () => {
        const drafts = JSON.parse(localStorage.getItem('draftDocuments') || '[]');
        setDraftDocuments(drafts);
    };

    const loadGeneratedDocuments = async () => {
        try {
            console.log('📦 Loading generated documents from backend API...');

            // Fetch generated documents from backend API only
            const result = await documentDatabaseService.getGeneratedDocuments();
            let generated = [];

            if (result && result.success !== false) {
                generated = result.documents || result || [];
                console.log('📄 Loaded generated documents from API:', generated.length);
            } else {
                console.warn('⚠️ No documents returned from API');
                generated = [];
            }

            // Ensure generated is an array
            if (!Array.isArray(generated)) {
                console.warn('⚠️ generatedDocuments is not an array, converting...');
                generated = Object.values(generated).filter(item => item && typeof item === 'object');
            }

            // Sort by newest first (reverse chronological order)
            generated.sort((a, b) => {
                const dateA = new Date(a.created_at || a.createdAt || a.generatedAt || 0);
                const dateB = new Date(b.created_at || b.createdAt || b.generatedAt || 0);
                return dateB - dateA;
            });

            setGeneratedDocuments(generated);
            console.log('✅ Generated documents sorted and set:', generated.length);

            // Initialize empty state - no localStorage for generated documents
            setSelectedEmployee({});
            setSendOptions({});
            setSendStatus({});

            console.log('✅ Document state loaded successfully:', {
                documents: generated.length
            });

        } catch (error) {
            console.error('❌ Critical error loading generated documents:', error);
            // Set empty state as fallback
            setGeneratedDocuments([]);
            setSelectedEmployee({});
            setSendOptions({});
            setSendStatus({});
        }
    };

    // Custom Template Helper Functions
    const loadCustomTemplates = () => {
        try {
            const saved = localStorage.getItem('customTemplates');
            if (saved) {
                setCustomTemplates(JSON.parse(saved));
            }
        } catch (error) {
            console.error('Error loading custom templates:', error);
        }
    };

    const saveCustomTemplates = (templates) => {
        try {
            localStorage.setItem('customTemplates', JSON.stringify(templates));
            setCustomTemplates(templates);
        } catch (error) {
            console.error('Error saving custom templates:', error);
        }
    };

    const handleCreateCustomTemplate = () => {
        if (!customTemplateForm.name || !customTemplateForm.content) {
            alert('Please fill in all required fields');
            return;
        }

        const newTemplate = {
            id: `custom_${generateUUID()}`,
            name: customTemplateForm.name,
            description: customTemplateForm.description,
            content: customTemplateForm.content,
            category: customTemplateForm.category,
            isCustom: true,
            createdAt: new Date().toISOString()
        };

        const updatedTemplates = {
            ...customTemplates,
            [customTemplateForm.category]: [
                ...(customTemplates[customTemplateForm.category] || []),
                newTemplate
            ]
        };

        saveCustomTemplates(updatedTemplates);
        handleCloseCustomTemplateModal();
        alert('Custom template created successfully!');
    };

    const handleEditCustomTemplate = (template) => {
        setEditingCustomTemplate(template);
        setCustomTemplateForm({
            name: template.name,
            description: template.description,
            content: template.content,
            category: template.category
        });
        setShowCreateCustomTemplate(true);
    };

    const handleUpdateCustomTemplate = () => {
        if (!editingCustomTemplate) return;

        const updatedTemplates = { ...customTemplates };

        // Find and update the template
        Object.keys(updatedTemplates).forEach(category => {
            updatedTemplates[category] = updatedTemplates[category].map(template =>
                template.id === editingCustomTemplate.id
                    ? {
                        ...template,
                        name: customTemplateForm.name,
                        description: customTemplateForm.description,
                        content: customTemplateForm.content,
                        category: customTemplateForm.category,
                        updatedAt: new Date().toISOString()
                    }
                    : template
            );
        });

        // If category changed, move template to new category
        if (editingCustomTemplate.category !== customTemplateForm.category) {
            // Remove from old category
            updatedTemplates[editingCustomTemplate.category] = updatedTemplates[editingCustomTemplate.category].filter(
                t => t.id !== editingCustomTemplate.id
            );

            // Add to new category
            const templateToUpdate = updatedTemplates[editingCustomTemplate.category].find(t => t.id === editingCustomTemplate.id);
            if (templateToUpdate) {
                templateToUpdate.category = customTemplateForm.category;
                if (!updatedTemplates[customTemplateForm.category]) {
                    updatedTemplates[customTemplateForm.category] = [];
                }
                updatedTemplates[customTemplateForm.category].push(templateToUpdate);
            }
        }

        saveCustomTemplates(updatedTemplates);
        handleCloseCustomTemplateModal();
        alert('Custom template updated successfully!');
    };

    const handleDeleteCustomTemplate = (templateId, category) => {
        if (!window.confirm('Are you sure you want to delete this custom template?')) return;

        const updatedTemplates = { ...customTemplates };
        updatedTemplates[category] = updatedTemplates[category].filter(t => t.id !== templateId);

        // Remove empty category arrays
        if (updatedTemplates[category].length === 0) {
            delete updatedTemplates[category];
        }

        saveCustomTemplates(updatedTemplates);
        alert('Custom template deleted successfully!');
    };

    const handleDuplicateCustomTemplate = (template) => {
        const duplicatedTemplate = {
            ...template,
            id: `custom_${generateUUID()}`,
            name: `${template.name} (Copy)`,
            isCustom: true,
            createdAt: new Date().toISOString(),
            updatedAt: undefined
        };

        const updatedTemplates = {
            ...customTemplates,
            [template.category]: [
                ...(customTemplates[template.category] || []),
                duplicatedTemplate
            ]
        };

        saveCustomTemplates(updatedTemplates);
        alert('Custom template duplicated successfully!');
    };

    const handleCloseCustomTemplateModal = () => {
        setShowCreateCustomTemplate(false);
        setEditingCustomTemplate(null);
        setCustomTemplateForm({
            name: '',
            description: '',
            content: '',
            category: 'appointment'
        });
    };

    const handleSelectTemplate = async (categoryId, templateId) => {
        const newTemplates = {
            ...selectedTemplates,
            [categoryId]: templateId
        };
        setSelectedTemplates(newTemplates);

        // Save to localStorage as cache
        documentTemplateService.saveToLocalStorage(newTemplates);
        console.log(`📄 Template selected: ${categoryId} → ${templateId}`);

        // Auto-save to backend database for cross-system availability
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            try {
                await documentTemplateService.updateSelectedTemplates(newTemplates);
                console.log(`✅ Template saved to database: ${categoryId} → ${templateId}`);
            } catch (error) {
                console.error('❌ Failed to save template to database:', error);
            }
        }
    };

    // UUID generation utility
    const generateUUID = () => {
        // Use browser's crypto API to generate RFC4122 compliant UUID
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const handleSaveTemplates = async () => {
        documentTemplateService.saveToLocalStorage(selectedTemplates);

        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            try {
                setLoading(true);
                await documentTemplateService.updateSelectedTemplates(selectedTemplates);
            } catch (error) {
                console.error('Error saving to backend:', error);
            } finally {
                setLoading(false);
            }
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
    };

    const handleGenerateDocument = (categoryId, templateId = null) => {
        // If no templateId provided, use the selected template
        const actualTemplateId = templateId || selectedTemplates[categoryId];

        // Check predefined templates first
        let template = templates[categoryId]?.find(t => t.id === actualTemplateId);

        // If not found, check custom templates
        if (!template && customTemplates[categoryId]) {
            template = customTemplates[categoryId].find(t => t.id === actualTemplateId);
        }

        if (template) {
            setGeneratingDocument({
                category: categoryId,
                templateId: actualTemplateId,
                templateName: template.name,
                isCustom: template.isCustom || false
            });
            setDocumentContent(template.content || '');
            setEditingDocument({
                id: generateUUID(),
                category: categoryId,
                templateId: actualTemplateId,
                templateName: template.name,
                content: template.content || '',
                isCustom: template.isCustom || false,
                status: 'draft',
                createdAt: new Date().toISOString()
            });
        }
    };

    const handleSaveAsDraft = () => {
        if (!editingDocument) return;

        const draft = {
            ...editingDocument,
            content: documentContent,
            status: 'draft',
            updatedAt: new Date().toISOString()
        };

        const existingDrafts = draftDocuments.filter(d => d.id !== draft.id);
        const updatedDrafts = [...existingDrafts, draft];

        setDraftDocuments(updatedDrafts);
        localStorage.setItem('draftDocuments', JSON.stringify(updatedDrafts));

        alert('Document saved as draft successfully!');
    };

    const handleGenerateFinal = async () => {
        if (!editingDocument) return;

        console.log('📄 Generating final document and saving to backend...');

        try {
            // Create document object with UUID
            const documentData = {
                ...editingDocument,
                id: editingDocument.id || generateUUID(), // Use existing UUID or generate new one
                content: documentContent,
                status: 'generated',
                generatedAt: new Date().toISOString(),
                // Add additional metadata for better tracking
                metadata: {
                    generatedBy: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'Admin',
                    generatedFrom: editingDocument.templateId || 'custom',
                    version: '1.0'
                }
            };

            // Save document to backend API first
            console.log('📤 Saving document to backend API...');
            const generatedDoc = await documentDatabaseService.saveDocument(documentData);

            if (!generatedDoc) {
                throw new Error('Failed to save document to backend - no document returned');
            }

            console.log('✅ Document saved to backend successfully:', generatedDoc.id);

            console.log('✅ Document created:', {
                id: generatedDoc.id,
                type: generatedDoc.type,
                title: generatedDoc.title || generatedDoc.templateName
            });

            // Reload documents from backend to get fresh data
            await loadGeneratedDocuments();

            // Clear editing state
            setEditingDocument(null);
            setGeneratingDocument(null);
            setDocumentContent('');

            // Show success message
            const successMessage = `Document generated and saved successfully!\n\nTitle: ${generatedDoc.title || generatedDoc.templateName}\nType: ${generatedDoc.type}\nID: ${generatedDoc.id}`;
            alert(successMessage);

            console.log('🎉 Document generation completed successfully:', {
                newDocumentId: generatedDoc.id,
                timestamp: generatedDoc.generatedAt
            });

        } catch (error) {
            console.error('❌ Critical error in document generation:', error);

            // Provide helpful error messages
            let errorMessage = 'Error: Document could not be saved to backend.';
            if (error.message.includes('network') || error.message.includes('connection')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (error.message.includes('authentication') || error.message.includes('unauthorized')) {
                errorMessage = 'Authentication error. Please log in again.';
            } else {
                errorMessage = `Error: ${error.message}`;
            }

            alert(errorMessage + '\n\nPlease try again or contact support if the issue persists.');
        }
    };

    // localStorage functionality removed - using API-only approach

    

    const handleEmployeeSelect = (docId, employee) => {
        // Normalize employee ID - ensure we have a consistent 'id' field
        const normalizedEmployee = {
            ...employee,
            id: employee.id || employee.employee_id // Ensure 'id' field exists
        };

        console.log('👤 Employee selected for document:', {
            docId,
            originalId: employee.id,
            originalEmployeeId: employee.employee_id,
            normalizedId: normalizedEmployee.id,
            employeeName: `${employee.first_name} ${employee.last_name}`,
            allKeys: Object.keys(employee)
        });

        const updatedSelection = {
            ...selectedEmployee,
            [docId]: normalizedEmployee
        };
        setSelectedEmployee(updatedSelection);

        console.log('💾 Employee selection saved:', {
            totalSelections: Object.keys(updatedSelection).length,
            documentIds: Object.keys(updatedSelection),
            savedId: updatedSelection[docId]?.id
        });

        setShowEmployeeDropdown(prev => ({
            ...prev,
            [docId]: false
        }));
    };

    // Handle direct send from template (generate + send in one step)
    // Show preview before sending document
    // employeeOverride: Optional employee to use (for custom letters where state may not be updated yet)
    // optionsOverride: Optional send options to use (for custom letters where state may not be updated yet)
    const handlePreviewBeforeSend = async (categoryId, templateId, isCustom = false, employeeOverride = null, optionsOverride = null) => {
        const templateKey = `${categoryId}_${templateId}`;
        // Use override if provided (for custom letters), otherwise read from state
        const employee = employeeOverride || selectedEmployee[templateKey];
        const options = optionsOverride || sendOptions[templateKey] || {};

        console.log('🔍 Preview validation - templateKey:', templateKey);
        console.log('🔍 Preview validation - employee:', employee);
        console.log('🔍 Preview validation - employeeOverride:', employeeOverride);
        console.log('🔍 Preview validation - selectedEmployee state:', selectedEmployee);

        // Validation - check both 'id' and 'employee_id' since backend may use either
        const employeeId = employee?.id || employee?.employee_id;
        if (!employee || !employeeId) {
            alert('Please select an employee first');
            return;
        }

        if (!options.email && !options.oakhr) {
            alert('Please select at least one delivery method (Email or Oak HR)');
            return;
        }

        // CRITICAL: Fetch fresh employee data from API to get latest values (pan_number, uan_number, etc.)
        // The cached employee list may be stale and missing recently updated fields
        let freshEmployee = employee;
        try {
            console.log('🔄 Fetching fresh employee data for document generation...');
            const freshEmployeeResponse = await employeeService.getEmployeeById(employeeId);
            if (freshEmployeeResponse.success && freshEmployeeResponse.employee) {
                freshEmployee = freshEmployeeResponse.employee;
                console.log('✅ Fresh employee data fetched successfully:', {
                    pan_number: freshEmployee.pan_number,
                    uan_number: freshEmployee.uan_number,
                    pf_number: freshEmployee.pf_number,
                    aadhaar_number: freshEmployee.aadhaar_number
                });
            } else {
                console.warn('⚠️ Could not fetch fresh employee data, using cached data');
            }
        } catch (fetchError) {
            console.warn('⚠️ Error fetching fresh employee data:', fetchError);
            // Continue with cached data if fetch fails
        }

        // Validate employee has all required fields for this document type
        const validation = await validateEmployeeForDocument(freshEmployee, categoryId);
        if (!validation.isValid) {
            setMissingFieldsData({
                employee: employee,
                fields: validation.missingFields,
                category: categoryId
            });
            setShowMissingFieldsModal(true);
            return;
        }

        // Find template - search in multiple locations
        console.log('🔍 Searching for template:', { categoryId, templateId, isCustom });

        let template = null;

        // 1. Check predefined templates for this category
        if (!template && templates[categoryId]) {
            template = templates[categoryId].find(t => t.id === templateId);
            if (template) console.log('✅ Found in predefined templates');
        }

        // 2. Check customTemplates for this category (custom templates within a category)
        if (!template && customTemplates[categoryId]) {
            template = customTemplates[categoryId].find(t => t.id === templateId);
            if (template) console.log('✅ Found in customTemplates');
        }

        // 3. For custom letters (isCustom=true with categoryId='custom'), check savedCustomLetters
        if (!template && isCustom && categoryId === 'custom') {
            template = savedCustomLetters.find(l => l.id === templateId);
            if (template) console.log('✅ Found in savedCustomLetters');
        }

        // 4. Also check savedCustomLetters by name/subject match as fallback
        if (!template && savedCustomLetters.length > 0) {
            template = savedCustomLetters.find(l => l.id === templateId);
            if (template) console.log('✅ Found in savedCustomLetters (fallback search)');
        }

        if (!template) {
            console.error('❌ Template not found:', {
                categoryId,
                templateId,
                isCustom,
                availableTemplates: templates[categoryId]?.map(t => t.id),
                availableCustomTemplates: customTemplates[categoryId]?.map(t => t.id),
                savedCustomLetterIds: savedCustomLetters.map(l => l.id)
            });
            alert('Template not found');
            return;
        }

        // Fetch compensation data for the employee to get CTC
        let compensationData = null;
        try {
            const compensationResponse = await compensationService.getEmployeeCompensation(employeeId);
            if (compensationResponse.success && compensationResponse.compensation) {
                compensationData = compensationResponse.compensation;
                console.log('💰 Compensation data fetched for preview:', compensationData);
            }
        } catch (error) {
            console.warn('⚠️ Could not fetch compensation data:', error);
        }

        // Fetch onboarding data for blood group
        let onboardingData = null;
        try {
            onboardingData = await onboardingService.getByEmployeeId(employeeId);
            if (onboardingData) {
                console.log('🩸 Onboarding data fetched for blood group:', onboardingData?.fields?.blood_group);
            }
        } catch (error) {
            console.warn('⚠️ Could not fetch onboarding data:', error);
        }

        // Fetch employee's shift assignment
        let employeeShiftName = null;
        try {
            const allShifts = await shiftService.getAllShifts();
            // Find which shift this employee is assigned to
            for (const shift of allShifts) {
                try {
                    const shiftEmployees = await shiftService.getShiftEmployees(shift.id);
                    const employees = shiftEmployees?.employees || shiftEmployees || [];
                    if (Array.isArray(employees) && employees.some(e => String(e.id) === String(employeeId))) {
                        employeeShiftName = shift.name;
                        console.log('⏰ Employee shift found:', employeeShiftName);
                        break;
                    }
                } catch (e) {
                    // Continue checking other shifts
                }
            }
        } catch (error) {
            console.warn('⚠️ Could not fetch shift data:', error);
        }

        // Fetch exit request for last working day
        let exitRequestData = null;
        try {
            const exitResponse = await exitService.getExitByEmployee(employeeId);
            if (exitResponse?.success && exitResponse?.data) {
                exitRequestData = exitResponse.data;
                console.log('🚪 Exit request data fetched:', {
                    approved_exit_date: exitRequestData.approved_exit_date,
                    requested_exit_date: exitRequestData.requested_exit_date
                });
            }
        } catch (error) {
            console.warn('⚠️ Could not fetch exit request data:', error);
        }

        // Get probation period from employee profile (in months) - no default, will trigger warning if missing
        // Check all possible field names and filter out empty strings
        // Fields: probation_period_months, probation_policy, probationPolicy, probation_period, probation_months
        const getProbationValue = () => {
            const values = [
                freshEmployee.probation_period_months,
                freshEmployee.probation_policy,
                freshEmployee.probationPolicy,
                freshEmployee.probation_period,
                freshEmployee.probation_months,
                freshEmployee.probationPeriod
            ];
            // Find first non-null, non-empty value
            for (const val of values) {
                if (val !== null && val !== undefined && val !== '' && !isNaN(parseInt(val, 10))) {
                    return parseInt(val, 10);
                }
            }
            return null;
        };
        const probationMonths = getProbationValue();
        console.log('📋 Probation period lookup:', {
            probation_period_months: freshEmployee.probation_period_months,
            probation_policy: freshEmployee.probation_policy,
            probationPolicy: freshEmployee.probationPolicy,
            result: probationMonths
        });

        // Get notice period from employee profile (in days) - no default, will trigger warning if missing
        // Check all possible field names and filter out empty strings
        const getNoticePeriodValue = () => {
            const values = [
                freshEmployee.notice_period,
                freshEmployee.noticePeriod,
                freshEmployee.notice_period_days,
                freshEmployee.noticePeriodDays
            ];
            // Find first non-null, non-empty value
            for (const val of values) {
                if (val !== null && val !== undefined && val !== '' && !isNaN(parseInt(val, 10))) {
                    return parseInt(val, 10);
                }
            }
            return null;
        };
        const noticePeriodDays = getNoticePeriodValue();
        console.log('📋 Notice period lookup:', {
            notice_period: freshEmployee.notice_period,
            noticePeriod: freshEmployee.noticePeriod,
            result: noticePeriodDays
        });

        // Calculate confirmation date = Joining Date + Probation Period
        const calculateConfirmationDate = () => {
            // If confirmation_date is already set in employee profile, use it
            if (freshEmployee.confirmation_date) {
                return new Date(freshEmployee.confirmation_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
            }
            // Use probation months from employee profile OR user-entered value
            const effectiveProbationMonths = probationMonths || (probationPeriodInput ? parseInt(probationPeriodInput, 10) : null);
            // Otherwise calculate from joining_date + probation_months
            if (freshEmployee.joining_date && effectiveProbationMonths) {
                const joiningDate = new Date(freshEmployee.joining_date);
                const confirmDate = new Date(joiningDate);
                confirmDate.setMonth(confirmDate.getMonth() + effectiveProbationMonths);
                return confirmDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
            }
            return null;
        };

        // Calculate years of service from joining date to last working day (or current date)
        const calculateYearsOfService = () => {
            const joiningDate = freshEmployee.joining_date;
            if (!joiningDate) return null;

            // Use exit request date, employee exit_date, or current date
            const lastWorkingDate = exitRequestData?.approved_exit_date ||
                exitRequestData?.requested_exit_date ||
                freshEmployee.exit_date ||
                freshEmployee.last_working_day;

            const startDate = new Date(joiningDate);
            const endDate = lastWorkingDate ? new Date(lastWorkingDate) : new Date();

            // Calculate difference in years and months
            let years = endDate.getFullYear() - startDate.getFullYear();
            let months = endDate.getMonth() - startDate.getMonth();
            let days = endDate.getDate() - startDate.getDate();

            // Adjust for negative days
            if (days < 0) {
                months--;
            }

            // Adjust for negative months
            if (months < 0) {
                years--;
                months += 12;
            }

            // Format output
            if (years > 0 && months > 0) {
                return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
            } else if (years > 0) {
                return `${years} year${years > 1 ? 's' : ''}`;
            } else if (months > 0) {
                return `${months} month${months > 1 ? 's' : ''}`;
            } else {
                return 'Less than a month';
            }
        };

        // Prepare employee data for preview
        // Enhanced salary lookup - check multiple sources
        const getSalaryValue = () => {
            const sources = {
                'compensationData.pay_package.ctc': compensationData?.pay_package?.ctc,
                'compensationData.annual_ctc': compensationData?.annual_ctc,
                'compensationData.ctc': compensationData?.ctc,
                'compensationData.gross_salary': compensationData?.gross_salary,
                'freshEmployee.pay_package.ctc': freshEmployee.pay_package?.ctc,
                'freshEmployee.ctc': freshEmployee.ctc,
                'freshEmployee.salary': freshEmployee.salary,
                'freshEmployee.gross_salary': freshEmployee.gross_salary,
                'freshEmployee.annual_ctc': freshEmployee.annual_ctc
            };
            console.log('💰 Salary lookup sources:', sources);
            // Return first non-null, non-zero value
            for (const [key, value] of Object.entries(sources)) {
                if (value && value > 0) {
                    console.log(`✅ Using salary from ${key}:`, value);
                    return value;
                }
            }
            console.log('⚠️ No salary value found');
            return null;
        };
        const salaryValue = getSalaryValue();

        // Helper to capitalize first letter
        const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

        // Helper to format date
        const formatDate = (dateStr) => {
            if (!dateStr) return null;
            return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        };

        // Get salary components - searches pay_package.salary_components from freshEmployee
        // The salary_components are stored in employee's pay_package after pay group is applied
        const getSalaryComponent = (name, isMonthly = false) => {
            // First try compensationData (API response)
            let components = compensationData?.salary_components || [];

            // If not in compensationData, try freshEmployee.pay_package.salary_components
            if (components.length === 0 && freshEmployee.pay_package?.salary_components) {
                components = freshEmployee.pay_package.salary_components;
            }

            // Search by name (case-insensitive, partial match)
            const comp = components.find(c => c.name?.toLowerCase().includes(name.toLowerCase()));

            if (comp) {
                return isMonthly ? (comp.monthly_amount || Math.round(comp.amount / 12)) : comp.amount;
            }
            return null;
        };

        // Get deduction component (type = 'deduction')
        const getDeductionComponent = (name, isMonthly = false) => {
            let components = compensationData?.salary_components || [];
            if (components.length === 0 && freshEmployee.pay_package?.salary_components) {
                components = freshEmployee.pay_package.salary_components;
            }
            const comp = components.find(c =>
                c.name?.toLowerCase().includes(name.toLowerCase()) &&
                (c.type === 'deduction' || c.category === 'statutory')
            );
            if (comp) {
                return isMonthly ? (comp.monthly_amount || Math.round(comp.amount / 12)) : comp.amount;
            }
            return null;
        };

        const employeeDataForPreview = {
            id: employeeId, // Use resolved employeeId (supports both 'id' and 'employee_id' from backend)
            // Basic employee info
            employeeName: `${freshEmployee.first_name || ''} ${freshEmployee.last_name || ''}`.trim(),
            displayName: freshEmployee.display_name || freshEmployee.displayName || `${freshEmployee.first_name || ''} ${freshEmployee.last_name || ''}`.trim(),
            firstName: freshEmployee.first_name,
            middleName: freshEmployee.middle_name || '',
            lastName: freshEmployee.last_name,
            genderDisplay: freshEmployee.gender ? capitalize(freshEmployee.gender) : null,
            gender: freshEmployee.gender ? freshEmployee.gender.toLowerCase() : null,
            // Salutation derived from gender: Mr. for male, Ms. for female
            salutation: freshEmployee.gender ? (freshEmployee.gender.toLowerCase() === 'male' ? 'Mr.' : freshEmployee.gender.toLowerCase() === 'female' ? 'Ms.' : '') : null,
            dateOfBirth: formatDate(freshEmployee.date_of_birth || freshEmployee.dob),
            mobileNumber: freshEmployee.mobile_number || freshEmployee.phone || freshEmployee.contact_number,
            personalEmail: freshEmployee.personal_email,
            workEmail: freshEmployee.work_email || freshEmployee.email,
            emergencyContactName: freshEmployee.emergency_contact_name,
            emergencyContactNumber: freshEmployee.emergency_contact_number || freshEmployee.emergency_contact_phone,
            emergencyContactRelationship: freshEmployee.emergency_contact_relationship,
            // Blood group: prioritize onboarding data, fallback to employee record
            bloodGroup: onboardingData?.fields?.blood_group || onboardingData?.fields?.bloodGroup || freshEmployee.blood_group,
            // Address fields
            address: (() => {
                if (freshEmployee.address && freshEmployee.address.trim()) return freshEmployee.address;
                const parts = [freshEmployee.address_line1, freshEmployee.address_line2, freshEmployee.city, freshEmployee.state, freshEmployee.country, freshEmployee.postal_code].filter(p => p && p.trim());
                return parts.length > 0 ? parts.join(', ') : null;
            })(),
            fullAddress: (() => {
                const parts = [freshEmployee.address_line1, freshEmployee.address_line2, freshEmployee.city, freshEmployee.state, freshEmployee.country, freshEmployee.postal_code].filter(p => p && p.trim());
                return parts.length > 0 ? parts.join(', ') : null;
            })(),
            addressLine1: freshEmployee.address_line1,
            addressLine2: freshEmployee.address_line2 || '',
            city: freshEmployee.city,
            state: freshEmployee.state,
            country: freshEmployee.country,
            pinCode: freshEmployee.postal_code || freshEmployee.pin_code || freshEmployee.zip_code,
            postalCode: freshEmployee.postal_code || freshEmployee.pin_code || freshEmployee.zip_code,
            // Permanent address
            permanentAddress: (() => {
                const parts = [freshEmployee.permanent_address_line1, freshEmployee.permanent_address_line2, freshEmployee.permanent_city, freshEmployee.permanent_state, freshEmployee.permanent_country, freshEmployee.permanent_postal_code].filter(p => p && p.trim());
                return parts.length > 0 ? parts.join(', ') : null;
            })(),
            permanentAddressLine1: freshEmployee.permanent_address_line1,
            permanentAddressLine2: freshEmployee.permanent_address_line2 || '',
            permanentCity: freshEmployee.permanent_city,
            permanentState: freshEmployee.permanent_state,
            permanentCountry: freshEmployee.permanent_country,
            permanentPinCode: freshEmployee.permanent_postal_code || freshEmployee.permanent_pin_code,
            // Employment details
            designation: freshEmployee.designation || freshEmployee.position || freshEmployee.job_title,
            department: freshEmployee.department || freshEmployee.department_name,
            subDepartment: freshEmployee.sub_department || freshEmployee.subDepartment || 'N/A',
            paymentMode: freshEmployee.payment_mode || freshEmployee.paymentMode || 'Bank Transfer',
            managerName: freshEmployee.manager_name || freshEmployee.reporting_manager || 'N/A',
            managerDesignation: freshEmployee.manager_designation || freshEmployee.manager_title || 'Manager',
            location: freshEmployee.location || freshEmployee.work_location,
            workLocation: freshEmployee.work_location || freshEmployee.location,
            employmentType: freshEmployee.employment_type || freshEmployee.employmentType,
            employmentStatus: freshEmployee.employment_status || freshEmployee.status,
            // Shift: prioritize fetched shift assignment, fallback to employee record
            shift: employeeShiftName || (freshEmployee.shift && freshEmployee.shift.trim()) || freshEmployee.work_shift || freshEmployee.shift_name,
            workingHours: freshEmployee.working_hours,
            employeeCode: freshEmployee.employee_code || freshEmployee.emp_code || freshEmployee.employee_id,
            email: freshEmployee.email || freshEmployee.work_email,
            phone: freshEmployee.phone || freshEmployee.mobile_number,
            // Bank details
            bankName: freshEmployee.bank_name,
            accountNumber: freshEmployee.account_number || freshEmployee.bank_account_number,
            ifscCode: freshEmployee.ifsc_code || freshEmployee.ifsc,
            branchName: freshEmployee.branch_name || freshEmployee.bank_branch,
            accountHolderName: freshEmployee.account_holder_name || `${freshEmployee.first_name || ''} ${freshEmployee.last_name || ''}`.trim(),
            accountType: freshEmployee.account_type || 'Savings',
            // Compensation - Annual amounts
            salary: salaryValue,
            monthlyCTC: salaryValue ? Math.round(salaryValue / 12) : null,
            grossSalary: compensationData?.gross_salary || freshEmployee.gross_salary,
            netSalary: compensationData?.net_salary || freshEmployee.net_salary,
            basicSalary: getSalaryComponent('basic') || freshEmployee.basic_salary,
            monthlySalary: getSalaryComponent('basic', true) || (freshEmployee.basic_salary ? Math.round(freshEmployee.basic_salary / 12) : null),
            hra: getSalaryComponent('hra') || freshEmployee.hra,
            specialAllowance: getSalaryComponent('special') || freshEmployee.special_allowance,
            transportAllowance: getSalaryComponent('transport') || getSalaryComponent('conveyance') || freshEmployee.transport_allowance,
            conveyance: getSalaryComponent('conveyance') || getSalaryComponent('transport') || freshEmployee.conveyance_allowance,
            medicalAllowance: getSalaryComponent('medical') || freshEmployee.medical_allowance,
            // Deductions
            pfContribution: getDeductionComponent('pf') || getDeductionComponent('provident') || freshEmployee.pf_contribution,
            esiContribution: getDeductionComponent('esi') || freshEmployee.esi_contribution,
            professionalTax: getDeductionComponent('professional tax') || getDeductionComponent('pt') || freshEmployee.professional_tax,
            tds: getDeductionComponent('tds') || getDeductionComponent('tax') || freshEmployee.tds,
            // Calculate total deductions
            totalDeductions: (
                (getDeductionComponent('pf') || getDeductionComponent('provident') || freshEmployee.pf_contribution || 0) +
                (getDeductionComponent('esi') || freshEmployee.esi_contribution || 0) +
                (getDeductionComponent('professional tax') || getDeductionComponent('pt') || freshEmployee.professional_tax || 0) +
                (getDeductionComponent('tds') || getDeductionComponent('tax') || freshEmployee.tds || 0)
            ),
            // Identity documents
            panNumber: freshEmployee.pan_number || freshEmployee.pan || freshEmployee.panNumber || freshEmployee.pan_no,
            pfNumber: freshEmployee.pf_number || freshEmployee.pf_account_number || freshEmployee.pfNumber || freshEmployee.pf_no,
            esiNumber: freshEmployee.esi_number || freshEmployee.esiNumber || freshEmployee.esi_no,
            uanNumber: freshEmployee.uan_number || freshEmployee.uan || freshEmployee.uanNumber || freshEmployee.uan_no,
            aadhaarNumber: freshEmployee.aadhaar_number || freshEmployee.aadhar_number || freshEmployee.aadhaar,
            passportNumber: freshEmployee.passport_number,
            passportExpiry: formatDate(freshEmployee.passport_expiry || freshEmployee.passport_expiry_date),
            visaStatus: freshEmployee.visa_status,
            // Personal/Family
            nationality: freshEmployee.nationality,
            maritalStatus: freshEmployee.marital_status ? capitalize(freshEmployee.marital_status) : null,
            spouseName: freshEmployee.spouse_name,
            fatherName: freshEmployee.father_name,
            motherName: freshEmployee.mother_name,
            // Dates
            joiningDate: formatDate(freshEmployee.joining_date),
            startDate: formatDate(freshEmployee.joining_date),
            // End date and last working day: prioritize exit request data, fallback to employee record
            endDate: formatDate(exitRequestData?.approved_exit_date || exitRequestData?.requested_exit_date || freshEmployee.exit_date),
            lastWorkingDay: formatDate(exitRequestData?.approved_exit_date || exitRequestData?.requested_exit_date || freshEmployee.exit_date || freshEmployee.last_working_day),
            confirmationDate: calculateConfirmationDate(),
            effectiveDate: effectiveDateInput ? formatDate(effectiveDateInput) : null,
            month: new Date().toLocaleString('default', { month: 'long' }),
            year: new Date().getFullYear().toString(),
            // Periods
            probationMonths: probationMonths ? probationMonths.toString() : (probationPeriodInput || null),
            noticePeriod: noticePeriodDays ? noticePeriodDays.toString() : (noticePeriodInput || null),
            // Years of service: calculated from joining date to last working day (or current date)
            yearsOfService: calculateYearsOfService(),
            tenure: calculateYearsOfService() // Alias for years of service
        };

        // Prepare company data for preview
        // Debug: Log all company data sources
        console.log('🏢 Company data sources for preview:', {
            'companyData state': companyData ? { name: companyData.name, logo_url: companyData.logo_url } : 'null',
            'localStorage companyName': localStorage.getItem('companyName'),
            'localStorage companyLogo': localStorage.getItem('companyLogo') ? 'Present' : 'Not found'
        });

        // Try to load company settings from localStorage
        let savedCompanySettings = {};
        try {
            const stored = localStorage.getItem('companySettings');
            if (stored) savedCompanySettings = JSON.parse(stored);
        } catch (e) {
            console.warn('Could not parse company settings from localStorage');
        }

        const companyDataForPreview = {
            name: companyData?.name || savedCompanySettings?.name || localStorage.getItem('companyName') || 'Company Name',
            location: companyData?.city || companyData?.location || savedCompanySettings?.address?.city || localStorage.getItem('companyLocation') || 'Location',
            address: companyData?.address || (() => {
                if (savedCompanySettings?.address) {
                    const addr = savedCompanySettings.address;
                    return [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean).join(', ');
                }
                return localStorage.getItem('companyAddress') || 'Company Address Line 1, City, State - PIN';
            })(),
            email: companyData?.email || savedCompanySettings?.email || localStorage.getItem('companyEmail') || 'hr@company.com',
            phone: companyData?.phone || savedCompanySettings?.phone || localStorage.getItem('companyPhone') || '+1234567890',
            logo: companyData?.logo_url || savedCompanySettings?.logo || localStorage.getItem('companyLogo') || null,
            currency: 'INR',
            hrName: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'HR Team',
            hrTitle: 'HR Manager',
            // New required fields
            website: savedCompanySettings?.website || companyData?.website || null,
            ceoName: savedCompanySettings?.ceoName || companyData?.ceo_name || null,
            hrEmail: savedCompanySettings?.hrEmail || companyData?.hr_email || null,
            branch: savedCompanySettings?.branch || companyData?.branch || null
        };

        console.log('🏢 Final company data for preview:', companyDataForPreview);

        // Debug: Log employee data for preview to see what fields are populated
        console.log('👤 Employee data for preview:', {
            firstName: employeeDataForPreview.firstName,
            lastName: employeeDataForPreview.lastName,
            displayName: employeeDataForPreview.displayName,
            gender: employeeDataForPreview.gender,
            genderDisplay: employeeDataForPreview.genderDisplay,
            mobileNumber: employeeDataForPreview.mobileNumber,
            shift: employeeDataForPreview.shift,
            panNumber: employeeDataForPreview.panNumber,
            uanNumber: employeeDataForPreview.uanNumber,
            pfNumber: employeeDataForPreview.pfNumber,
            aadhaarNumber: employeeDataForPreview.aadhaarNumber
        });
        console.log('👤 Raw freshEmployee object fields:', {
            first_name: freshEmployee.first_name,
            last_name: freshEmployee.last_name,
            display_name: freshEmployee.display_name,
            gender: freshEmployee.gender,
            mobile_number: freshEmployee.mobile_number,
            shift: freshEmployee.shift,
            pan_number: freshEmployee.pan_number,
            pan: freshEmployee.pan,
            uan_number: freshEmployee.uan_number,
            uan: freshEmployee.uan,
            pf_number: freshEmployee.pf_number,
            aadhaar_number: freshEmployee.aadhaar_number
        });

        // Generate preview content
        const previewContent = replaceDocumentPlaceholders(template.content, employeeDataForPreview, companyDataForPreview);

        // Check for unreplaced placeholders in the generated content
        const unreplacedPlaceholders = detectUnreplacedPlaceholders(previewContent);

        // If there are unreplaced placeholders, show the missing fields modal instead of preview
        if (unreplacedPlaceholders.length > 0) {
            console.log('⚠️ Unreplaced placeholders found:', unreplacedPlaceholders);
            setMissingFieldsData({
                employee: freshEmployee,
                fields: unreplacedPlaceholders.map(p => ({
                    field: p.field,
                    label: p.label,
                    placeholder: p.placeholder,
                    editPath: p.editPath
                })),
                category: categoryId,
                isPlaceholderBased: true // Flag to indicate this is from placeholder detection
            });
            setShowMissingFieldsModal(true);
            return;
        }

        // Set preview data and show modal
        setDocumentPreviewData({
            categoryId,
            templateId,
            isCustom,
            templateKey,
            template,
            employee,
            options,
            compensationData,
            employeeData: employeeDataForPreview,
            companyData: companyDataForPreview,
            previewContent
        });
        setShowDocumentPreviewModal(true);
    };

    // Actually send the document after preview confirmation
    const handleConfirmSendDocument = async () => {
        if (!documentPreviewData) return;

        const { categoryId, templateId, isCustom, templateKey, template, employee, options, compensationData, employeeData, companyData: companyDataForSend } = documentPreviewData;

        // Close the preview modal
        setShowDocumentPreviewModal(false);
        setDocumentPreviewData(null);

        // Now call the actual send function
        await executeDocumentSend(categoryId, templateId, isCustom, templateKey, template, employee, options, compensationData, employeeData, companyDataForSend);
    };

    // Execute the actual document generation and sending
    const executeDocumentSend = async (categoryId, templateId, isCustom, templateKey, template, employee, options, compensationData, employeeDataPrepped, companyDataForSend) => {
        try {
            // Update status to generating
            setSendStatus(prev => ({ ...prev, [templateKey]: 'generating' }));

            // Step 1: Generate PDF and upload to Firebase
            console.log('📄 Generating PDF and uploading to Firebase...');
            setSendStatus(prev => ({ ...prev, [templateKey]: 'generating-pdf' }));

            console.log('🏢 Company data being sent to PDF generation:', companyDataForSend);
            console.log('👤 Employee data being sent:', employeeDataPrepped);

            const pdfResult = await documentDatabaseService.generateAndUploadPDF({
                content: template.content || '',
                templateName: template.name,
                employeeName: employeeDataPrepped.employeeName,
                metadata: {
                    generatedBy: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'Admin',
                    employeeId: employee.id,
                    category: categoryId,
                    categoryName: categories.find(cat => cat.id === categoryId)?.name || categoryId,
                    templateId: templateId,
                    // Use pre-prepared employee data from preview
                    employeeData: {
                        jobTitle: employeeDataPrepped.designation,
                        designation: employeeDataPrepped.designation,
                        department: employeeDataPrepped.department,
                        managerName: employeeDataPrepped.managerName,
                        location: employeeDataPrepped.location,
                        salary: employeeDataPrepped.salary,
                        probationMonths: employeeDataPrepped.probationMonths,
                        noticePeriod: employeeDataPrepped.noticePeriod,
                        address: employeeDataPrepped.address,
                        employeeCode: employeeDataPrepped.employeeCode,
                        email: employeeDataPrepped.email,
                        phone: employeeDataPrepped.phone,
                        joiningDate: employeeDataPrepped.joiningDate,
                        startDate: employeeDataPrepped.startDate,
                        month: employeeDataPrepped.month,
                        year: employeeDataPrepped.year,
                        confirmationDate: employeeDataPrepped.confirmationDate,
                        endDate: employeeDataPrepped.endDate,
                        lastWorkingDay: employeeDataPrepped.lastWorkingDay,
                        currentDesignation: employeeDataPrepped.designation,
                        basicSalary: employeeDataPrepped.basicSalary,
                        gender: employeeDataPrepped.gender,
                        managerDesignation: employeeDataPrepped.managerDesignation
                    },
                    // Company data for placeholder replacement
                    companyData: companyDataForSend
                }
            });

            console.log('✅ PDF generated and uploaded:', pdfResult.pdfUrl);

            // Step 2: Create document data WITH PDF URL
            const documentData = {
                id: pdfResult.documentId || generateUUID(),
                category: categoryId,
                templateId: templateId,
                templateName: template.name,
                type: categoryId,
                content: template.content || '',
                firebaseUrl: pdfResult.pdfUrl,           // PDF URL from Firebase
                firebasePath: pdfResult.firebasePath,    // Storage path
                pdfUrl: pdfResult.pdfUrl,                // Also as pdfUrl for compatibility
                status: 'generated',
                generatedAt: new Date().toISOString(),
                isCustom: isCustom,
                employeeId: employee.id,
                employeeName: employeeDataPrepped.employeeName,
                metadata: {
                    generatedBy: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'Admin',
                    generatedFrom: templateId,
                    version: '1.0',
                    storageType: 'firebase'
                }
            };

            // Step 3: Save document metadata to backend
            console.log('📤 Saving document metadata to backend...');
            setSendStatus(prev => ({ ...prev, [templateKey]: 'saving' }));

            const generatedDoc = await documentDatabaseService.saveDocument(documentData);

            if (!generatedDoc) {
                throw new Error('Failed to save document - no document returned');
            }

            console.log('✅ Document saved with PDF URL:', generatedDoc.id);

            // Update status to sending
            setSendStatus(prev => ({ ...prev, [templateKey]: 'sending' }));

            // Send document
            const deliveryResults = {
                email: { success: false, error: null },
                oakhr: { success: false, error: null }
            };

            if (options.email) {
                try {
                    await documentService.sendDocumentEmail(generatedDoc.id, employee.id, {
                        to: employee.work_email,
                        subject: `Document: ${template.name}`,
                        documentTitle: template.name,
                        recipientName: `${employee.first_name} ${employee.last_name}`
                    });
                    deliveryResults.email.success = true;
                } catch (emailError) {
                    deliveryResults.email.error = emailError.message;
                    console.error('❌ Email delivery failed:', emailError);
                }
            }

            if (options.oakhr) {
                try {
                    await documentService.sendToOakHR(generatedDoc.id, employee.id);
                    deliveryResults.oakhr.success = true;
                } catch (oakhrError) {
                    deliveryResults.oakhr.error = oakhrError.message;
                    console.error('❌ Oak HR delivery failed:', oakhrError);
                }
            }

            const anySuccess = deliveryResults.email.success || deliveryResults.oakhr.success;

            if (anySuccess) {
                // Success
                setSendStatus(prev => ({ ...prev, [templateKey]: 'sent' }));

                const successMethods = [];
                if (deliveryResults.email.success) successMethods.push('Email');
                if (deliveryResults.oakhr.success) successMethods.push('Oak HR');

                alert(`Document sent successfully via: ${successMethods.join(', ')}\n\nEmployee: ${employee.first_name} ${employee.last_name}\nDocument: ${template.name}`);

                // Reset selections
                setSelectedEmployee(prev => {
                    const updated = { ...prev };
                    delete updated[templateKey];
                    return updated;
                });
                setSendOptions(prev => {
                    const updated = { ...prev };
                    delete updated[templateKey];
                    return updated;
                });
                setTimeout(() => {
                    setSendStatus(prev => {
                        const updated = { ...prev };
                        delete updated[templateKey];
                        return updated;
                    });
                }, 3000);
            } else {
                throw new Error('All delivery methods failed');
            }

        } catch (error) {
            console.error('❌ Error in direct send:', error);
            setSendStatus(prev => ({ ...prev, [templateKey]: 'failed' }));
            alert(`Error: ${error.message}\n\nPlease try again.`);

            // Reset failed status after 3 seconds
            setTimeout(() => {
                setSendStatus(prev => {
                    const updated = { ...prev };
                    delete updated[templateKey];
                    return updated;
                });
            }, 3000);
        }
    };

    const handleSendDocument = async (docId) => {
        const doc = generatedDocuments.find(d => d.id === docId);
        const employee = selectedEmployee[docId];
        const options = sendOptions[docId] || {};

        // Enhanced validation
        if (!doc) {
            alert('Document not found. Please regenerate the document.');
            return;
        }

        if (!employee || !employee.id) {
            console.error('❌ Employee selection error:', {
                docId,
                employee,
                allSelectedEmployees: selectedEmployee,
                selectedEmployeeKeys: Object.keys(selectedEmployee)
            });
            alert('Please select an employee first before sending the document.');
            return;
        }

        if (!options.email && !options.oakhr) {
            alert('Please select at least one delivery method (Email or Oak HR)');
            return;
        }
// console.log('check data check',employeeData);
        console.log('📤 Starting document delivery:', {
            docId,
            documentTitle: doc.title || doc.templateName,
            employeeId: employee.id,
            employeeName: `${employee.first_name} ${employee.last_name}`,
            methods: options,
            debug: {
                fullEmployeeObject: employee,
                allSelectedEmployees: selectedEmployee,
                docExistsInSelection: docId in selectedEmployee,
                employeeKeys: Object.keys(selectedEmployee)
            }
        });

        // Update status to sending
        const updatedStatusSending = {
            ...sendStatus,
            [docId]: 'sending'
        };
        setSendStatus(updatedStatusSending);

        // Enhanced document delivery with comprehensive tracking
        const sendDocument = async () => {
            const deliveryResults = {
                email: { success: false, error: null },
                oakhr: { success: false, error: null }
            };

            try {
                // Send via email if selected
                if (options.email) {
                    try {
                        await documentService.sendDocumentEmail(doc.id, employee.id, {
                            to: employee.work_email,
                            subject: `Document: ${doc.templateName || doc.type}`,
                            documentTitle: doc.templateName || doc.type,
                            documentContent: doc.content,
                            recipientName: `${employee.first_name} ${employee.last_name}`
                        });
                        deliveryResults.email.success = true;
                        console.log(`✅ Document sent to ${employee.work_email} via email`);
                    } catch (emailError) {
                        deliveryResults.email.error = emailError.message;
                        console.error('❌ Email delivery failed:', emailError);
                        // Don't throw here, continue with Oak HR if selected
                    }
                }

                // Send to Oak HR if selected
                if (options.oakhr) {
                    try {
                        await documentService.sendToOakHR(doc.id, employee.id);
                        deliveryResults.oakhr.success = true;
                        console.log(`✅ Document delivery to Oak HR completed for employee ${employee.id}`);

                    } catch (oakhrError) {
                        deliveryResults.oakhr.error = oakhrError.message;
                        console.error('❌ Oak HR delivery failed:', oakhrError);
                        // Don't throw here, continue with status updates
                    }
                }

                // Determine overall success
                const anySuccess = deliveryResults.email.success || deliveryResults.oakhr.success;
                const allSuccess = deliveryResults.email.success && deliveryResults.oakhr.success;

                if (anySuccess) {
                    // Update status to success
                    const updatedStatusSuccess = {
                        ...sendStatus,
                        [docId]: 'sent'
                    };
                    setSendStatus(updatedStatusSuccess);

                    // Update document status locally
                    const updatedDocs = generatedDocuments.map(d =>
                        d.id === docId ? {
                            ...d,
                            status: 'sent',
                            sentTo: employee.id,
                            sentAt: new Date().toISOString(),
                            deliveryMethods: Object.keys(deliveryResults).filter(key => deliveryResults[key].success)
                        } : d
                    );
                    setGeneratedDocuments(updatedDocs);

                    // Create success message
                    const successMethods = [];
                    if (deliveryResults.email.success) successMethods.push('Email');
                    if (deliveryResults.oakhr.success) successMethods.push('Oak HR');

                    const successMessage = `Document successfully delivered via: ${successMethods.join(', ')}\n\n` +
                        `Employee: ${employee.first_name} ${employee.last_name}\n` +
                        `Document: ${doc.templateName || doc.type}`;

                    alert(successMessage);

                    console.log('🎉 Document delivery completed successfully:', {
                        docId,
                        employee: employee.id,
                        methods: successMethods,
                        results: deliveryResults
                    });

                } else {
                    // All delivery methods failed
                    const updatedStatusFailed = {
                        ...sendStatus,
                        [docId]: 'failed'
                    };
                    setSendStatus(updatedStatusFailed);

                    throw new Error('All delivery methods failed');
                }

            } catch (error) {
                console.error('❌ Critical error in document delivery:', error);

                // Update status to failed
                const updatedStatusFailed = {
                    ...sendStatus,
                    [docId]: 'failed'
                };
                setSendStatus(updatedStatusFailed);

                alert(`Error delivering document: ${error.message}\n\nPlease check your network connection and try again.`);
            }
        };

        // Execute the send with error handling
        try {
            await sendDocument();
        } catch (error) {
            console.error('❌ Send operation failed:', error);
        }
      };

    // Enhanced function to save document to employee's Documents section
    const saveDocumentToEmployeeSection = async (employeeId, document, deliveryMethod = 'oakhr') => {
        try {
            console.log(`📁 Saving document to employee ${employeeId}'s Documents section:`, document.id);

            const employeeKey = `employeeDocuments_${employeeId}`;

            // Get existing employee documents
            let existingDocs = [];
            try {
                const existingData = localStorage.getItem(employeeKey);
                if (existingData) {
                    existingDocs = JSON.parse(existingData);
                }
            } catch (parseError) {
                console.warn(`⚠️ Error parsing existing employee documents for ${employeeId}, starting fresh:`, parseError.message);
                existingDocs = [];
            }

            // Ensure it's an array
            if (!Array.isArray(existingDocs)) {
                existingDocs = Object.values(existingDocs).filter(item => item && typeof item === 'object');
            }

            // Create employee document record with comprehensive metadata
            const employeeDoc = {
                ...document,
                // Keep original document info
                id: document.id,
                title: document.title || document.templateName || document.type,
                content: document.content,
                type: document.type,
                generatedAt: document.generatedAt,

                // Add delivery and status metadata
                deliveredAt: new Date().toISOString(),
                deliveryMethod: deliveryMethod,
                status: deliveryMethod === 'oakhr-fallback' ? 'delivered-fallback' : 'delivered',
                source: 'document-builder',
                recipientId: employeeId,

                // Add metadata for better tracking and display
                metadata: {
                    ...document.metadata,
                    deliveryInfo: {
                        method: deliveryMethod,
                        timestamp: new Date().toISOString(),
                        deliveredBy: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'Admin'
                    }
                },

                // Add flags for UI
                isCompanyDocument: true,
                requiresAcknowledgment: ['appointment', 'confirmation', 'termination'].includes(document.type),
                category: document.category || 'general'
            };

            // Remove any existing version of this document for this employee
            const filteredDocs = existingDocs.filter(doc => doc.id !== document.id);

            // Add new document at the beginning (newest first)
            const updatedEmployeeDocs = [employeeDoc, ...filteredDocs];

            // Save to localStorage with verification
            try {
                localStorage.setItem(employeeKey, JSON.stringify(updatedEmployeeDocs));

                // Verify the save was successful
                const verificationData = JSON.parse(localStorage.getItem(employeeKey) || '[]');
                if (!verificationData.some(doc => doc.id === document.id)) {
                    throw new Error('Document not found after save verification');
                }

                console.log(`✅ Successfully saved document to employee ${employeeId}'s collection:`, {
                    employeeKey,
                    totalEmployeeDocs: updatedEmployeeDocs.length,
                    documentTitle: employeeDoc.title,
                    deliveryMethod: deliveryMethod
                });

                return true;

            } catch (saveError) {
                console.error(`❌ Error saving document to employee ${employeeId}'s localStorage:`, saveError);
                throw saveError;
            }

        } catch (error) {
            console.error(`❌ Critical error saving document to employee ${employeeId}'s Documents section:`, error);
            throw error;
        }
    };

    // Helper function to get filtered employees for a specific document
    const getFilteredEmployeesForDocument = (docId) => {
        const query = (documentSearchQueries[docId] || '').toLowerCase();
        return employees.filter(emp => {
            const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
            const code = (emp.employee_code || '').toLowerCase();
            return name.includes(query) || code.includes(query);
        });
    };

    // Keep the original filteredEmployees for backward compatibility
    const filteredEmployees = employees.filter(emp => {
        const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        const code = (emp.employee_code || '').toLowerCase();
        const query = searchQuery.toLowerCase();
        return name.includes(query) || code.includes(query);
    });

    const isTemplateSelected = (categoryId, templateId) => {
        return selectedTemplates[categoryId] === templateId;
    };

    const getCategoryName = (categoryId) => {
        return categories.find(c => c.id === categoryId)?.name || categoryId;
    };

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="p-6 space-y-6">
                {/* Header with Tabs */}
                <div>
                    <h1 className={`text-3xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Document Builder
                    </h1>

                    {/* Tab Navigation */}
                    <div className="tour-docbuilder-tabs flex gap-4 border-b border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`px-4 py-2 font-medium transition-colors relative ${
                                activeTab === 'templates'
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                            }`}
                        >
                            Document Templates
                            {activeTab === 'templates' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('generate')}
                            className={`px-4 py-2 font-medium transition-colors relative ${
                                activeTab === 'generate'
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                            }`}
                        >
                            Generate Document
                            {activeTab === 'generate' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('custom')}
                            className={`px-4 py-2 font-medium transition-colors relative ${
                                activeTab === 'custom'
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                            }`}
                        >
                            Custom Letter
                            {activeTab === 'custom' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'templates' ? (
                    /* Document Templates Tab - Original Content */
                    <>
                        <div className="flex items-center justify-between">
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                Select templates for your organization's documents
                            </p>
                            <button
                                onClick={handleSaveTemplates}
                                className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                                    saveSuccess
                                        ? 'bg-green-600 text-white'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                <Save className="w-4 h-4" />
                                {saveSuccess ? 'Saved!' : 'Save Selection'}
                            </button>
                        </div>

                        {/* Category Tabs */}
                        <div className={`rounded-lg overflow-hidden ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-sm'
                        }`}>
                            <div className={`flex overflow-x-auto ${
                                theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'
                            }`}>
                                {categories.map((category) => (
                                    <button
                                        key={category.id}
                                        onClick={() => setSelectedCategory(category.id)}
                                        className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap transition-colors relative ${
                                            selectedCategory === category.id
                                                ? theme === 'dark'
                                                    ? 'text-blue-400 bg-gray-700/50'
                                                    : 'text-blue-600 bg-blue-50'
                                                : theme === 'dark'
                                                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="text-xl">{category.icon}</span>
                                        <span>{category.name}</span>
                                        {selectedTemplates[category.id] && (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Templates Grid */}
                            <div className="p-6">
                                <div className="tour-docbuilder-templates grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Predefined Templates */}
                                    {templates[selectedCategory]?.map((template) => (
                                        <div
                                            key={template.id}
                                            className={`rounded-lg border-2 p-5 transition-all cursor-pointer ${
                                                isTemplateSelected(selectedCategory, template.id)
                                                    ? theme === 'dark'
                                                        ? 'border-blue-500 bg-blue-900/20'
                                                        : 'border-blue-500 bg-blue-50'
                                                    : theme === 'dark'
                                                        ? 'border-gray-700 hover:border-gray-600 bg-gray-700/30'
                                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                            }`}
                                            onClick={() => handleSelectTemplate(selectedCategory, template.id)}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <h3 className={`font-semibold mb-1 ${
                                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                    }`}>
                                                        {template.name}
                                                    </h3>
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {template.description}
                                                    </p>
                                                </div>
                                                {isTemplateSelected(selectedCategory, template.id) && (
                                                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                                                )}
                                            </div>

                                            <p className={`text-xs mb-4 ${
                                                theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                            }`}>
                                                {template.preview}
                                            </p>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPreviewTemplate(template);
                                                }}
                                                className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-600 hover:bg-gray-500 text-white'
                                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                }`}
                                            >
                                                <Eye className="w-4 h-4 inline mr-2" />
                                                Preview Template
                                            </button>
                                        </div>
                                    ))}

                                    {/* Custom Templates */}
                                    {customTemplates[selectedCategory]?.map((template) => (
                                        <div
                                            key={template.id}
                                            className={`rounded-lg border-2 p-5 transition-all cursor-pointer relative ${
                                                isTemplateSelected(selectedCategory, template.id)
                                                    ? theme === 'dark'
                                                        ? 'border-purple-500 bg-purple-900/20'
                                                        : 'border-purple-500 bg-purple-50'
                                                    : theme === 'dark'
                                                        ? 'border-purple-700 hover:border-purple-600 bg-purple-700/30'
                                                        : 'border-purple-200 hover:border-purple-300 bg-purple-50'
                                            }`}
                                            onClick={() => handleSelectTemplate(selectedCategory, template.id)}
                                        >
                                            <div className="absolute top-2 right-2">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                    Custom
                                                </span>
                                            </div>

                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1 pr-16">
                                                    <h3 className={`font-semibold mb-1 ${
                                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                    }`}>
                                                        {template.name}
                                                    </h3>
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {template.description}
                                                    </p>
                                                </div>
                                                {isTemplateSelected(selectedCategory, template.id) && (
                                                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 ml-2" />
                                                )}
                                            </div>

                                            <div className="flex gap-2 mt-4">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreviewTemplate(template);
                                                    }}
                                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                                                        theme === 'dark'
                                                            ? 'bg-gray-600 hover:bg-gray-500 text-white'
                                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                    }`}
                                                >
                                                    <Eye className="w-3 h-3 inline mr-1" />
                                                    Preview
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditCustomTemplate(template);
                                                    }}
                                                    className="p-2 rounded-lg text-xs bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                                >
                                                    <Edit2 className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDuplicateCustomTemplate(template);
                                                    }}
                                                    className="p-2 rounded-lg text-xs bg-green-600 text-white hover:bg-green-700 transition-colors"
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteCustomTemplate(template.id, template.category);
                                                    }}
                                                    className="p-2 rounded-lg text-xs bg-red-600 text-white hover:bg-red-700 transition-colors"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Create Custom Template Button */}
                                    <button
                                        onClick={() => {
                                            setCustomTemplateForm(prev => ({ ...prev, category: selectedCategory }));
                                            setShowCreateCustomTemplate(true);
                                        }}
                                        className={`tour-docbuilder-create rounded-lg border-2 border-dashed p-5 transition-all hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 ${
                                            theme === 'dark'
                                                ? 'border-gray-700 bg-gray-700/30 text-gray-400 hover:text-purple-400'
                                                : 'border-gray-300 bg-gray-50 text-gray-600 hover:text-purple-600'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center justify-center h-full">
                                            <FilePlus className="w-8 h-8 mb-2" />
                                            <span className="font-medium">Create Custom Template</span>
                                            <span className="text-xs mt-1">Design your own template</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : activeTab === 'generate' ? (
                    /* Send Documents Tab - Simplified */
                    <div className="space-y-6">
                        {/* Send Documents Directly from Templates */}
                        <div className={`rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-sm'
                        } p-6`}>
                            <h2 className={`text-xl font-semibold mb-4 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Send Documents to Employees
                            </h2>

                            <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                Select an employee, choose delivery method, and send documents instantly
                            </p>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className={`border-b ${
                                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                        }`}>
                                            <th className={`text-left py-3 px-4 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Document Type</th>
                                            <th className={`text-left py-3 px-4 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Employee</th>
                                            <th className={`text-left py-3 px-4 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Send Via</th>
                                            <th className={`text-left py-3 px-4 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(category => {
                                            const selectedTemplateId = selectedTemplates[category.id];
                                            const selectedTemplate = templates[category.id]?.find(t => t.id === selectedTemplateId);
                                            const categoryCustomTemplates = customTemplates[category.id] || [];

                                            // Get all templates for this category (predefined + custom)
                                            const allTemplates = [];
                                            if (selectedTemplate) {
                                                allTemplates.push({ ...selectedTemplate, isCustom: false });
                                            }
                                            categoryCustomTemplates.forEach(ct => {
                                                allTemplates.push({ ...ct, isCustom: true });
                                            });

                                            return allTemplates.map((template) => {
                                                const templateKey = `${category.id}_${template.id}`;
                                                const status = sendStatus[templateKey];

                                                return (
                                                    <tr key={templateKey} className={`border-b ${
                                                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                                    }`}>
                                                        <td className={`py-3 px-4 ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-2xl">{category.icon}</span>
                                                                <div>
                                                                    <p className="font-medium">{category.name}</p>
                                                                    {template.isCustom && (
                                                                        <p className={`text-xs ${
                                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                                        }`}>
                                                                            Custom Template
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td className="py-3 px-4">
                                                            <div className="relative">
                                                                <button
                                                                    onClick={() => setShowEmployeeDropdown(prev => ({
                                                                        ...prev,
                                                                        [templateKey]: !prev[templateKey]
                                                                    }))}
                                                                    disabled={status === 'generating' || status === 'sending'}
                                                                    className={`w-full min-w-[200px] px-3 py-2 rounded-lg border text-left flex items-center justify-between ${
                                                                        theme === 'dark'
                                                                            ? 'border-gray-600 bg-gray-700 text-white'
                                                                            : 'border-gray-300 bg-white text-gray-900'
                                                                    } ${(status === 'generating' || status === 'sending') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    <span className="truncate">
                                                                        {selectedEmployee[templateKey]
                                                                            ? `${selectedEmployee[templateKey].first_name} ${selectedEmployee[templateKey].last_name}`
                                                                            : 'Select Employee'}
                                                                    </span>
                                                                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                                                                </button>

                                                                {showEmployeeDropdown[templateKey] && (
                                                                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-10 ${
                                                                        theme === 'dark' ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
                                                                    }`}>
                                                                        <div className="p-2">
                                                                            <input
                                                                                type="text"
                                                                                placeholder="Search employee..."
                                                                                value={documentSearchQueries[templateKey] || ''}
                                                                                onChange={(e) => setDocumentSearchQueries(prev => ({
                                                                                    ...prev,
                                                                                    [templateKey]: e.target.value
                                                                                }))}
                                                                                className={`w-full px-3 py-2 rounded border ${
                                                                                    theme === 'dark'
                                                                                        ? 'bg-gray-800 border-gray-600 text-white'
                                                                                        : 'bg-white border-gray-300 text-gray-900'
                                                                                }`}
                                                                            />
                                                                        </div>
                                                                        <div className="max-h-48 overflow-y-auto">
                                                                            {getFilteredEmployeesForDocument(templateKey).slice(0, 10).map(emp => (
                                                                                <button
                                                                                    key={emp.employee_id || emp.id}
                                                                                    onClick={() => {
                                                                                        handleEmployeeSelect(templateKey, emp);
                                                                                        setShowEmployeeDropdown(prev => ({
                                                                                            ...prev,
                                                                                            [templateKey]: false
                                                                                        }));
                                                                                    }}
                                                                                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 ${
                                                                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                                                    }`}
                                                                                >
                                                                                    <div>
                                                                                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                                                                                        <p className={`text-xs ${
                                                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                                                        }`}>
                                                                                            {emp.employee_code || emp.id} • {emp.email || emp.work_email || 'No email'}
                                                                                        </p>
                                                                                    </div>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="py-3 px-4">
                                                            <div className="flex items-center gap-3">
                                                                <label className="flex items-center gap-1 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={sendOptions[templateKey]?.email || false}
                                                                        onChange={(e) => {
                                                                            const updatedOptions = {
                                                                                ...sendOptions,
                                                                                [templateKey]: {
                                                                                    ...sendOptions[templateKey],
                                                                                    email: e.target.checked
                                                                                }
                                                                            };
                                                                            setSendOptions(updatedOptions);
                                                                        }}
                                                                        disabled={status === 'generating' || status === 'sending'}
                                                                        className="rounded"
                                                                    />
                                                                    <Mail className="w-4 h-4" />
                                                                    <span className={`text-sm ${
                                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                                    }`}>Email</span>
                                                                </label>

                                                                <label className="flex items-center gap-1 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={sendOptions[templateKey]?.oakhr || false}
                                                                        onChange={(e) => {
                                                                            const updatedOptions = {
                                                                                ...sendOptions,
                                                                                [templateKey]: {
                                                                                    ...sendOptions[templateKey],
                                                                                    oakhr: e.target.checked
                                                                                }
                                                                            };
                                                                            setSendOptions(updatedOptions);
                                                                        }}
                                                                        disabled={status === 'generating' || status === 'sending'}
                                                                        className="rounded"
                                                                    />
                                                                    <FileText className="w-4 h-4" />
                                                                    <span className={`text-sm ${
                                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                                    }`}>OakHR</span>
                                                                </label>
                                                            </div>
                                                        </td>

                                                        <td className="py-3 px-4">
                                                            {status === 'generating' && (
                                                                <div className="flex items-center gap-2 text-blue-600">
                                                                    <Clock className="w-4 h-4 animate-spin" />
                                                                    <span className="text-sm">Generating...</span>
                                                                </div>
                                                            )}
                                                            {status === 'sending' && (
                                                                <div className="flex items-center gap-2 text-blue-600">
                                                                    <Send className="w-4 h-4 animate-pulse" />
                                                                    <span className="text-sm">Sending...</span>
                                                                </div>
                                                            )}
                                                            {status === 'sent' && (
                                                                <div className="flex items-center gap-2 text-green-600">
                                                                    <CheckCircle className="w-4 h-4" />
                                                                    <span className="text-sm">Sent!</span>
                                                                </div>
                                                            )}
                                            {status === 'failed' && (
                                                                <div className="flex items-center gap-2 text-red-600">
                                                                    <AlertCircle className="w-4 h-4" />
                                                                    <span className="text-sm">Failed</span>
                                                                </div>
                                                            )}
                                                            {!status && (
                                                                <button
                                                                    onClick={() => handlePreviewBeforeSend(category.id, template.id, template.isCustom)}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                                                >
                                                                    <Send className="w-4 h-4" />
                                                                    Send
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Custom Letters Section */}
                            {savedCustomLetters.length > 0 && (
                                <div className={`mt-8 rounded-lg ${
                                    theme === 'dark' ? 'bg-gray-750 border border-gray-600' : 'bg-gray-50 border border-gray-200'
                                } p-6`}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <FilePlus className={`w-5 h-5 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`} />
                                        <h3 className={`text-lg font-semibold ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            Custom Letters
                                        </h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            theme === 'dark' ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'
                                        }`}>
                                            {savedCustomLetters.length} saved
                                        </span>
                                    </div>
                                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Send your custom created letters to employees
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Custom Letter Dropdown */}
                                        <div>
                                            <label className={`block text-sm font-medium mb-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                Select Custom Letter
                                            </label>
                                            <select
                                                value={selectedCustomLetter?.id || ''}
                                                onChange={(e) => {
                                                    const letter = savedCustomLetters.find(l => l.id === e.target.value);
                                                    setSelectedCustomLetter(letter || null);
                                                }}
                                                className={`w-full px-3 py-2 rounded-lg border ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-700 border-gray-600 text-white'
                                                        : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                            >
                                                <option value="">Choose a letter...</option>
                                                {savedCustomLetters.map(letter => (
                                                    <option key={letter.id} value={letter.id}>
                                                        {letter.name || letter.subject}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Employee Selector */}
                                        <div>
                                            <label className={`block text-sm font-medium mb-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                Select Employee
                                            </label>
                                            <div className="relative">
                                                <button
                                                    onClick={() => setShowEmployeeDropdown(prev => ({
                                                        ...prev,
                                                        'custom-letter': !prev['custom-letter']
                                                    }))}
                                                    className={`w-full px-3 py-2 rounded-lg border text-left flex items-center justify-between ${
                                                        theme === 'dark'
                                                            ? 'border-gray-600 bg-gray-700 text-white'
                                                            : 'border-gray-300 bg-white text-gray-900'
                                                    }`}
                                                >
                                                    <span className="truncate">
                                                        {customLetterEmployee
                                                            ? `${customLetterEmployee.first_name} ${customLetterEmployee.last_name}`
                                                            : 'Choose an employee...'}
                                                    </span>
                                                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                                                </button>

                                                {showEmployeeDropdown['custom-letter'] && (
                                                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-10 ${
                                                        theme === 'dark' ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
                                                    }`}>
                                                        <div className="p-2">
                                                            <input
                                                                type="text"
                                                                placeholder="Search employee..."
                                                                value={documentSearchQueries['custom-letter-emp'] || ''}
                                                                onChange={(e) => setDocumentSearchQueries(prev => ({
                                                                    ...prev,
                                                                    'custom-letter-emp': e.target.value
                                                                }))}
                                                                className={`w-full px-3 py-2 rounded border ${
                                                                    theme === 'dark'
                                                                        ? 'bg-gray-800 border-gray-600 text-white'
                                                                        : 'bg-white border-gray-300 text-gray-900'
                                                                }`}
                                                            />
                                                        </div>
                                                        <div className="max-h-48 overflow-y-auto">
                                                            {employees
                                                                .filter(emp => {
                                                                    const query = (documentSearchQueries['custom-letter-emp'] || '').toLowerCase();
                                                                    const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                                                                    const code = (emp.employee_code || '').toLowerCase();
                                                                    return name.includes(query) || code.includes(query);
                                                                })
                                                                .slice(0, 10)
                                                                .map(emp => (
                                                                    <button
                                                                        key={emp.employee_id || emp.id}
                                                                        onClick={() => {
                                                                            // Normalize employee ID
                                                                            const normalizedEmp = {
                                                                                ...emp,
                                                                                id: emp.id || emp.employee_id
                                                                            };
                                                                            setCustomLetterEmployee(normalizedEmp);
                                                                            setShowEmployeeDropdown(prev => ({
                                                                                ...prev,
                                                                                'custom-letter': false
                                                                            }));
                                                                        }}
                                                                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 ${
                                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                                        }`}
                                                                    >
                                                                        <div>
                                                                            <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                                                                            <p className={`text-xs ${
                                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                                            }`}>
                                                                                {emp.employee_code || emp.id} • {emp.email || emp.work_email || 'No email'}
                                                                            </p>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Send Options & Button */}
                                        <div>
                                            <label className={`block text-sm font-medium mb-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                Send Via
                                            </label>
                                            <div className="flex items-center gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={sendOptions['custom-letter']?.email || false}
                                                        onChange={(e) => {
                                                            setSendOptions(prev => ({
                                                                ...prev,
                                                                'custom-letter': {
                                                                    ...prev['custom-letter'],
                                                                    email: e.target.checked
                                                                }
                                                            }));
                                                        }}
                                                        className="rounded"
                                                    />
                                                    <Mail className="w-4 h-4" />
                                                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Email</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={sendOptions['custom-letter']?.oakhr || false}
                                                        onChange={(e) => {
                                                            setSendOptions(prev => ({
                                                                ...prev,
                                                                'custom-letter': {
                                                                    ...prev['custom-letter'],
                                                                    oakhr: e.target.checked
                                                                }
                                                            }));
                                                        }}
                                                        className="rounded"
                                                    />
                                                    <FileText className="w-4 h-4" />
                                                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>OakHR</span>
                                                </label>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    if (!selectedCustomLetter) {
                                                        alert('Please select a custom letter');
                                                        return;
                                                    }
                                                    if (!customLetterEmployee) {
                                                        alert('Please select an employee');
                                                        return;
                                                    }
                                                    const options = sendOptions['custom-letter'] || {};
                                                    if (!options.email && !options.oakhr) {
                                                        alert('Please select at least one delivery method (Email or OakHR)');
                                                        return;
                                                    }

                                                    // Set up the employee in selectedEmployee for the preview/send flow
                                                    const templateKey = `custom_${selectedCustomLetter.id}`;
                                                    setSelectedEmployee(prev => ({
                                                        ...prev,
                                                        [templateKey]: customLetterEmployee
                                                    }));
                                                    setSendOptions(prev => ({
                                                        ...prev,
                                                        [templateKey]: options
                                                    }));

                                                    // Use handlePreviewBeforeSend which handles the full flow
                                                    // Pass employee and options directly to avoid state timing issues
                                                    await handlePreviewBeforeSend('custom', selectedCustomLetter.id, true, customLetterEmployee, options);

                                                    // Reset selections after the preview modal is shown
                                                    setSelectedCustomLetter(null);
                                                    setCustomLetterEmployee(null);
                                                    setSendOptions(prev => {
                                                        const updated = { ...prev };
                                                        delete updated['custom-letter'];
                                                        return updated;
                                                    });
                                                }}
                                                disabled={!selectedCustomLetter || !customLetterEmployee || sendStatus['custom-letter'] === 'generating' || sendStatus['custom-letter'] === 'sending'}
                                                className={`mt-3 w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                                                    selectedCustomLetter && customLetterEmployee && sendStatus['custom-letter'] !== 'generating' && sendStatus['custom-letter'] !== 'sending'
                                                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                            >
                                                {sendStatus['custom-letter'] === 'generating' ? (
                                                    <>
                                                        <Clock className="w-4 h-4 animate-spin" />
                                                        Generating...
                                                    </>
                                                ) : sendStatus['custom-letter'] === 'sending' ? (
                                                    <>
                                                        <Send className="w-4 h-4 animate-pulse" />
                                                        Sending...
                                                    </>
                                                ) : sendStatus['custom-letter'] === 'sent' ? (
                                                    <>
                                                        <CheckCircle className="w-4 h-4" />
                                                        Sent!
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4" />
                                                        Send Custom Letter
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Letter Preview */}
                                    {selectedCustomLetter && (
                                        <div className={`mt-4 p-4 rounded-lg ${
                                            theme === 'dark' ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-200'
                                        }`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    Preview: {selectedCustomLetter.name || selectedCustomLetter.subject}
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        // Delete the custom letter
                                                        if (window.confirm('Are you sure you want to delete this custom letter?')) {
                                                            const updatedLetters = savedCustomLetters.filter(l => l.id !== selectedCustomLetter.id);
                                                            setSavedCustomLetters(updatedLetters);
                                                            localStorage.setItem('savedCustomLetters', JSON.stringify(updatedLetters));
                                                            setSelectedCustomLetter(null);
                                                        }
                                                    }}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                    title="Delete this letter"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div
                                                className={`text-sm max-h-32 overflow-y-auto ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                }`}
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedCustomLetter.content?.substring(0, 500) + (selectedCustomLetter.content?.length > 500 ? '...' : '')
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {savedCustomLetters.length === 0 && (
                                <div className={`mt-8 text-center py-8 rounded-lg border-2 border-dashed ${
                                    theme === 'dark' ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'
                                }`}>
                                    <FilePlus className={`w-12 h-12 mx-auto mb-3 ${
                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                    }`} />
                                    <p className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        No Custom Letters Yet
                                    </p>
                                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                        Go to the "Custom Letter" tab to create your own letter templates
                                    </p>
                                    <button
                                        onClick={() => setActiveTab('custom')}
                                        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create Custom Letter
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : activeTab === 'custom' ? (
                    /* Custom Letter Tab */
                    <div className="space-y-6">
                        <div className={`rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-sm'
                        } p-6`}>
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className={`text-xl font-semibold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        Create Custom Letter
                                    </h2>
                                    <p className={`text-sm mt-1 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        Design and send custom letters to employees
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setCustomLetterPreview(!customLetterPreview)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 text-white hover:bg-gray-600'
                                                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                        }`}
                                    >
                                        <Eye className="w-4 h-4" />
                                        {customLetterPreview ? 'Hide' : 'Show'} Preview
                                    </button>
                                    <button
                                        onClick={() => {
                                            const draft = {
                                                id: generateUUID(),
                                                subject: customLetterSubject,
                                                content: customLetterContent,
                                                recipients: customLetterRecipients,
                                                type: 'custom',
                                                status: 'draft',
                                                createdAt: new Date().toISOString()
                                            };
                                            const updatedDrafts = [...draftDocuments, draft];
                                            setDraftDocuments(updatedDrafts);
                                            localStorage.setItem('draftDocuments', JSON.stringify(updatedDrafts));
                                            alert('Custom letter saved as draft!');
                                        }}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 text-white hover:bg-gray-600'
                                                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                        }`}
                                    >
                                        <Save className="w-4 h-4" />
                                        Save Draft
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (!customLetterSubject.trim()) {
                                                alert('Please enter a subject for your custom letter');
                                                return;
                                            }
                                            if (!customLetterContent.trim()) {
                                                alert('Please enter content for your custom letter');
                                                return;
                                            }
                                            const newLetter = {
                                                id: generateUUID(),
                                                name: customLetterSubject,
                                                subject: customLetterSubject,
                                                content: customLetterContent,
                                                type: 'custom',
                                                createdAt: new Date().toISOString(),
                                                updatedAt: new Date().toISOString()
                                            };
                                            const updatedLetters = [...savedCustomLetters, newLetter];
                                            console.log('📝 Saving custom letter:', newLetter.name);
                                            console.log('📜 Updated letters count:', updatedLetters.length);
                                            setSavedCustomLetters(updatedLetters);
                                            try {
                                                localStorage.setItem('savedCustomLetters', JSON.stringify(updatedLetters));
                                                console.log('✅ Custom letters saved to localStorage successfully');
                                                // Also save to backend for persistence
                                                api.post('/api/documents/custom-letters', { letters: updatedLetters })
                                                    .then(res => console.log('✅ Custom letters synced to backend'))
                                                    .catch(err => console.warn('⚠️ Backend sync failed (letters still saved locally):', err.message));
                                            } catch (error) {
                                                console.error('❌ Error saving custom letters:', error);
                                            }
                                            // Clear the form
                                            setCustomLetterSubject('');
                                            setCustomLetterContent('');
                                            alert('Custom letter created successfully! You can now use it in the Generate Document tab.');
                                        }}
                                        disabled={!customLetterSubject.trim() || !customLetterContent.trim()}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                            customLetterSubject.trim() && customLetterContent.trim()
                                                ? 'bg-green-600 text-white hover:bg-green-700'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        <FilePlus className="w-4 h-4" />
                                        Create Letter
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Letter Details */}
                                <div className="space-y-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={customLetterSubject}
                                            onChange={(e) => setCustomLetterSubject(e.target.value)}
                                            className={`w-full px-4 py-2 rounded-lg border ${
                                                theme === 'dark'
                                                    ? 'bg-gray-700 border-gray-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                            placeholder="Enter letter subject..."
                                        />
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Letter Content
                                        </label>
                                        <RichTextEditorEnhanced
                                            value={customLetterContent}
                                            onChange={setCustomLetterContent}
                                            placeholder="Write your custom letter content here. Click on variables from the right panel to insert them..."
                                            showPlaceholderButton={false}
                                        />
                                    </div>
                                </div>

                                {/* Available Variables Panel */}
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <label className={`block text-sm font-medium ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                <Type className="w-4 h-4 inline mr-2" />
                                                Available Variables
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Search variables..."
                                                value={documentSearchQueries['custom-letter-vars'] || ''}
                                                onChange={(e) => setDocumentSearchQueries(prev => ({
                                                    ...prev,
                                                    'custom-letter-vars': e.target.value
                                                }))}
                                                className={`w-48 px-3 py-1.5 text-sm rounded border ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-800 border-gray-600 text-white'
                                                        : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                            />
                                        </div>
                                        <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Click on a variable to insert it into your letter
                                        </p>
                                        <div className={`border rounded-lg max-h-[60vh] overflow-y-auto ${
                                            theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                                        }`}>
                                            {/* Variable Categories */}
                                            {[
                                                {
                                                    id: 'basic',
                                                    name: 'Employee Basic Info',
                                                    icon: User,
                                                    variables: [
                                                        { name: '[First Name]', description: 'Employee\'s first name' },
                                                        { name: '[Middle Name]', description: 'Employee\'s middle name' },
                                                        { name: '[Last Name]', description: 'Employee\'s last name' },
                                                        { name: '[Employee Name]', description: 'Full name (First + Last)' },
                                                        { name: '[Display Name]', description: 'Preferred display name' },
                                                        { name: '[Gender]', description: 'Employee gender' },
                                                        { name: '[Employee Code]', description: 'Unique employee number/ID' },
                                                        { name: '[Date of Birth]', description: 'Employee date of birth' },
                                                        { name: '[Blood Group]', description: 'Blood group type' }
                                                    ]
                                                },
                                                {
                                                    id: 'contact',
                                                    name: 'Employee Contact Info',
                                                    icon: Mail,
                                                    variables: [
                                                        { name: '[Email]', description: 'Work email address' },
                                                        { name: '[Personal Email]', description: 'Personal email address' },
                                                        { name: '[Phone]', description: 'Primary phone number' },
                                                        { name: '[Mobile Number]', description: 'Mobile/cell number' },
                                                        { name: '[Emergency Contact]', description: 'Emergency contact name' },
                                                        { name: '[Emergency Phone]', description: 'Emergency contact number' },
                                                        { name: '[Employee Address]', description: 'Full residential address' },
                                                        { name: '[City]', description: 'City of residence' },
                                                        { name: '[State]', description: 'State/province' },
                                                        { name: '[Postal Code]', description: 'ZIP/postal code' },
                                                        { name: '[Country]', description: 'Country of residence' }
                                                    ]
                                                },
                                                {
                                                    id: 'job',
                                                    name: 'Employee Job Info',
                                                    icon: Building,
                                                    variables: [
                                                        { name: '[Designation]', description: 'Job title/designation' },
                                                        { name: '[Job Title]', description: 'Official job title' },
                                                        { name: '[Department]', description: 'Department name' },
                                                        { name: '[Department Name]', description: 'Full department name' },
                                                        { name: '[Manager Name]', description: 'Reporting manager name' },
                                                        { name: '[Manager Designation]', description: 'Manager\'s job title' },
                                                        { name: '[Location]', description: 'Work location/office' },
                                                        { name: '[Branch]', description: 'Branch/office name' },
                                                        { name: '[Employment Type]', description: 'Full-time/Part-time/Contract' },
                                                        { name: '[Employment Status]', description: 'Active/Inactive/Probation' },
                                                        { name: '[Joining Date]', description: 'Date of joining' },
                                                        { name: '[Start Date]', description: 'Employment start date' },
                                                        { name: '[Confirmation Date]', description: 'Date of confirmation' },
                                                        { name: '[Probation Period]', description: 'Probation period in months' },
                                                        { name: '[Notice Period]', description: 'Notice period in days' },
                                                        { name: '[End Date]', description: 'Last working day' },
                                                        { name: '[Last Working Day]', description: 'Final day of employment' },
                                                        { name: '[Years of Service]', description: 'Total tenure with company' },
                                                        { name: '[Shift]', description: 'Assigned shift/schedule' }
                                                    ]
                                                },
                                                {
                                                    id: 'salary',
                                                    name: 'Employee Salary Info',
                                                    icon: FileText,
                                                    variables: [
                                                        { name: '[Amount]', description: 'CTC/Annual salary amount' },
                                                        { name: '[Salary]', description: 'Annual salary figure' },
                                                        { name: '[Amount in Words]', description: 'Salary in words (Indian format)' },
                                                        { name: '[Salary in Words]', description: 'Salary amount in words' },
                                                        { name: '[CTC in Words]', description: 'CTC in words format' },
                                                        { name: '[Basic Salary]', description: 'Basic salary component' },
                                                        { name: '[Monthly Salary]', description: 'Monthly gross salary' },
                                                        { name: '[Net Salary]', description: 'Take-home salary' },
                                                        { name: '[HRA]', description: 'House Rent Allowance' },
                                                        { name: '[Conveyance]', description: 'Conveyance allowance' },
                                                        { name: '[Special Allowance]', description: 'Special allowance amount' },
                                                        { name: '[PF Contribution]', description: 'Provident fund contribution' },
                                                        { name: '[ESI Contribution]', description: 'ESI deduction amount' },
                                                        { name: '[Professional Tax]', description: 'Professional tax deduction' },
                                                        { name: '[TDS]', description: 'Tax deducted at source' },
                                                        { name: '[Bank Name]', description: 'Salary bank name' },
                                                        { name: '[Account Number]', description: 'Bank account number' },
                                                        { name: '[IFSC Code]', description: 'Bank IFSC code' },
                                                        { name: '[PAN Number]', description: 'Employee PAN number' },
                                                        { name: '[UAN Number]', description: 'Universal Account Number' }
                                                    ]
                                                },
                                                {
                                                    id: 'org',
                                                    name: 'Organization Info',
                                                    icon: Building,
                                                    variables: [
                                                        { name: '[Company Name]', description: 'Organization name' },
                                                        { name: '[Company Address]', description: 'Registered office address' },
                                                        { name: '[Company Email]', description: 'Company email address' },
                                                        { name: '[Company Phone]', description: 'Company contact number' },
                                                        { name: '[Company Website]', description: 'Company website URL' },
                                                        { name: '[HR Name]', description: 'HR representative name' },
                                                        { name: '[HR Title]', description: 'HR designation' },
                                                        { name: '[HR Email]', description: 'HR email address' },
                                                        { name: '[CEO Name]', description: 'Chief Executive name' },
                                                        { name: '[Currency]', description: 'Currency symbol (INR/USD)' }
                                                    ]
                                                },
                                                {
                                                    id: 'pronouns',
                                                    name: 'Pronouns & Titles',
                                                    icon: User,
                                                    variables: [
                                                        { name: '[he/she]', description: 'Subject pronoun (lowercase)' },
                                                        { name: '[He/She]', description: 'Subject pronoun (capitalized)' },
                                                        { name: '[his/her]', description: 'Possessive pronoun (lowercase)' },
                                                        { name: '[His/Her]', description: 'Possessive pronoun (capitalized)' },
                                                        { name: '[him/her]', description: 'Object pronoun (lowercase)' },
                                                        { name: '[Him/Her]', description: 'Object pronoun (capitalized)' },
                                                        { name: '[Mr/Ms]', description: 'Title prefix based on gender' },
                                                        { name: '[Title]', description: 'Employee salutation/title' }
                                                    ]
                                                },
                                                {
                                                    id: 'dates',
                                                    name: 'Date & Time',
                                                    icon: Clock,
                                                    variables: [
                                                        { name: '[Date]', description: 'Current date (DD Month YYYY)' },
                                                        { name: '[Today]', description: 'Today\'s date formatted' },
                                                        { name: '[DD/MM/YYYY]', description: 'Current date numeric' },
                                                        { name: '[Month]', description: 'Current month name' },
                                                        { name: '[Year]', description: 'Current year' },
                                                        { name: '[Month Year]', description: 'Month and year combined' },
                                                        { name: '[Effective Date]', description: 'Document effective date' },
                                                        { name: '[Issue Date]', description: 'Document issue date' },
                                                        { name: '[Expiry Date]', description: 'Document expiry date' }
                                                    ]
                                                },
                                                {
                                                    id: 'performance',
                                                    name: 'Performance Review',
                                                    icon: CheckCircle,
                                                    variables: [
                                                        { name: '[Performance Rating]', description: 'Overall performance rating' },
                                                        { name: '[Review Period]', description: 'Assessment period dates' },
                                                        { name: '[Reviewer Name]', description: 'Performance reviewer name' },
                                                        { name: '[Goals Achieved]', description: 'Number of goals met' },
                                                        { name: '[Increment Percentage]', description: 'Salary increment %' },
                                                        { name: '[New Designation]', description: 'Promoted designation' },
                                                        { name: '[New Salary]', description: 'Revised salary amount' },
                                                        { name: '[Promotion Date]', description: 'Date of promotion' }
                                                    ]
                                                },
                                                {
                                                    id: 'personal',
                                                    name: 'Personal Information',
                                                    icon: User,
                                                    variables: [
                                                        { name: '[Nationality]', description: 'Employee nationality' },
                                                        { name: '[Marital Status]', description: 'Married/Single/etc.' },
                                                        { name: '[Spouse Name]', description: 'Spouse\'s name' },
                                                        { name: '[Father Name]', description: 'Father\'s name' },
                                                        { name: '[Mother Name]', description: 'Mother\'s name' },
                                                        { name: '[Aadhaar Number]', description: 'Aadhaar card number' },
                                                        { name: '[Passport Number]', description: 'Passport number' },
                                                        { name: '[Passport Expiry]', description: 'Passport expiry date' },
                                                        { name: '[Visa Status]', description: 'Work visa status' }
                                                    ]
                                                },
                                                {
                                                    id: 'custom',
                                                    name: 'Document Custom Fields',
                                                    icon: FilePlus,
                                                    variables: [
                                                        { name: '[X]', description: 'Probation period (months)' },
                                                        { name: '[Y]', description: 'Notice period (days)' },
                                                        { name: '[Reference Number]', description: 'Document reference/ID' },
                                                        { name: '[Reason]', description: 'Reason/justification text' },
                                                        { name: '[Remarks]', description: 'Additional remarks' },
                                                        { name: '[Attachments]', description: 'List of attachments' }
                                                    ]
                                                }
                                            ].filter(category => {
                                                const searchQuery = (documentSearchQueries['custom-letter-vars'] || '').toLowerCase();
                                                if (!searchQuery) return true;
                                                return category.name.toLowerCase().includes(searchQuery) ||
                                                    category.variables.some(v =>
                                                        v.name.toLowerCase().includes(searchQuery) ||
                                                        v.description.toLowerCase().includes(searchQuery)
                                                    );
                                            }).map(category => {
                                                const CategoryIcon = category.icon;
                                                const searchQuery = (documentSearchQueries['custom-letter-vars'] || '').toLowerCase();
                                                const filteredVariables = searchQuery
                                                    ? category.variables.filter(v =>
                                                        v.name.toLowerCase().includes(searchQuery) ||
                                                        v.description.toLowerCase().includes(searchQuery)
                                                    )
                                                    : category.variables;

                                                return (
                                                    <div key={category.id} className="border-b last:border-b-0 border-gray-200 dark:border-gray-600">
                                                        <button
                                                            onClick={() => setDocumentSearchQueries(prev => ({
                                                                ...prev,
                                                                [`custom-letter-expand-${category.id}`]: !prev[`custom-letter-expand-${category.id}`]
                                                            }))}
                                                            className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                                                                theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <CategoryIcon className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                                                                <span className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                                    {category.name}
                                                                </span>
                                                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                    theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                                                                }`}>
                                                                    {filteredVariables.length}
                                                                </span>
                                                            </div>
                                                            <ChevronDown className={`w-4 h-4 transition-transform ${
                                                                documentSearchQueries[`custom-letter-expand-${category.id}`] ? 'rotate-180' : ''
                                                            } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                                        </button>
                                                        {(documentSearchQueries[`custom-letter-expand-${category.id}`] || searchQuery) && (
                                                            <div className={`px-3 pb-3 space-y-1 ${theme === 'dark' ? 'bg-gray-750' : 'bg-white'}`}>
                                                                {filteredVariables.map((variable, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => {
                                                                            // Insert variable into custom letter content
                                                                            setCustomLetterContent(prev =>
                                                                                prev + variable.name
                                                                            );
                                                                        }}
                                                                        className={`w-full flex items-start gap-2 p-2 rounded text-left transition-colors group ${
                                                                            theme === 'dark'
                                                                                ? 'hover:bg-gray-600 bg-gray-700'
                                                                                : 'hover:bg-blue-50 bg-gray-50'
                                                                        }`}
                                                                        title={`Click to insert ${variable.name}`}
                                                                    >
                                                                        <code className={`text-xs px-1.5 py-0.5 rounded font-mono whitespace-nowrap ${
                                                                            theme === 'dark'
                                                                                ? 'bg-blue-900/50 text-blue-300 group-hover:bg-blue-800'
                                                                                : 'bg-blue-100 text-blue-700 group-hover:bg-blue-200'
                                                                        }`}>
                                                                            {variable.name}
                                                                        </code>
                                                                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                            {variable.description}
                                                                        </span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Preview Section */}
                                    {customLetterPreview && (
                                        <div>
                                            <label className={`block text-sm font-medium mb-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                Letter Preview
                                            </label>
                                            <div className={`border rounded-lg p-4 ${
                                                theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                                            }`}>
                                                <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                                                    <h3 className="font-semibold">Subject: {customLetterSubject || 'No subject'}</h3>
                                                </div>
                                                <div
                                                    className="prose prose-sm max-w-none"
                                                    dangerouslySetInnerHTML={{
                                                        __html: customLetterContent || '<p>No content</p>'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Preview Modal */}
            {previewTemplate && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
                <div
                    className={`rounded-xl shadow-2xl max-w-2xl w-full ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {previewTemplate.name}
                                </h3>
                                <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {previewTemplate.description}
                                </p>
                            </div>
                            <button
                                onClick={() => setPreviewTemplate(null)}
                                className={`p-2 rounded-lg transition-colors ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                }`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="p-8 rounded-lg border border-gray-300 bg-white max-h-[70vh] overflow-y-auto">
                            {/* Template Preview Content */}
                            <div className="space-y-4 text-gray-800">
                                {/* Letter wrapper - only for non-payslip templates */}
                                {!previewTemplate.id.startsWith('pay-') && (
                                    <>
                                        {/* Company Header */}
                                        <div className="border-b pb-4 mb-6">
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                                                    LOGO
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-2xl font-bold">COMPANY NAME</div>
                                                    <div className="text-sm mt-1">Company Address Line 1, City, State - PIN</div>
                                                    <div className="text-sm">Email: hr@company.com | Phone: +1234567890</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Date */}
                                        <div className="text-right text-sm">
                                            <div>Date: DD/MM/YYYY</div>
                                        </div>

                                        {/* Recipient */}
                                        <div className="text-sm">
                                            <div className="font-semibold">[Employee Name]</div>
                                            <div>[Employee Address]</div>
                                        </div>

                                        {/* Subject */}
                                        <div className="text-sm">
                                            <div className="font-semibold">Subject: {previewTemplate.name}</div>
                                        </div>

                                        {/* Salutation */}
                                        <div className="text-sm">
                                            <div>Dear [Employee Name],</div>
                                        </div>
                                    </>
                                )}

                                {/* Body - Different for each template type */}
                                {previewTemplate.id === 'apt-formal' && (
                                    <div className="text-sm space-y-3">
                                        <p>Further to our discussions and your successful completion of the interview process, we are pleased to formally offer you employment with [Company Name] in the capacity of <strong>[Job Title]</strong>.</p>
                                        <div className="font-semibold mt-4">TERMS OF EMPLOYMENT:</div>
                                        <div className="pl-4 space-y-1">
                                            <div><strong>1. Designation:</strong> [Job Title]</div>
                                            <div><strong>2. Department:</strong> [Department Name]</div>
                                            <div><strong>3. Reporting Authority:</strong> [Manager Name], [Manager Designation]</div>
                                            <div><strong>4. Date of Commencement:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>5. Annual Compensation:</strong> [Currency] [Amount] per annum (Cost to Company)</div>
                                            <div><strong>6. Work Location:</strong> [Location]</div>
                                            <div><strong>7. Working Hours:</strong> [X] hours per week as per company policy</div>
                                            <div><strong>8. Probation Period:</strong> [X] months from date of joining</div>
                                            <div><strong>9. Notice Period:</strong> [X] days during probation, [Y] days post confirmation</div>
                                        </div>
                                        <p className="mt-3">This offer is contingent upon verification of your credentials, background check, and medical examination as per company policy. You will be governed by the company's policies, rules, and regulations as amended from time to time.</p>
                                        <p>Please confirm your acceptance by signing and returning the duplicate copy of this letter along with the required documents listed in Annexure A.</p>
                                        <p>We look forward to welcoming you to our organization.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'apt-modern' && (
                                    <div className="text-sm space-y-3">
                                        <p>Congratulations! We're excited to offer you the position of <strong>[Job Title]</strong> at [Company Name]. Your skills and experience impressed us, and we believe you'll be a great addition to our team.</p>
                                        <div className="bg-gray-100 p-4 rounded-lg mt-3">
                                            <div className="font-semibold mb-2">Your Role:</div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div><span className="text-gray-600">Designation:</span> <strong>[Job Title]</strong></div>
                                                <div><span className="text-gray-600">Team:</span> <strong>[Department Name]</strong></div>
                                                <div><span className="text-gray-600">Reports to:</span> <strong>[Manager Name]</strong></div>
                                                <div><span className="text-gray-600">Start Date:</span> <strong>[DD/MM/YYYY]</strong></div>
                                                <div><span className="text-gray-600">Compensation:</span> <strong>[Currency] [Amount]/year</strong></div>
                                                <div><span className="text-gray-600">Location:</span> <strong>[Location]</strong></div>
                                            </div>
                                        </div>
                                        <p>You'll have a [X] month probation period to get settled in. During this time, we'll provide all the support you need to succeed.</p>
                                        <p>To accept this offer, simply sign the attached document and send it back to us. We can't wait to have you on board!</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'apt-startup' && (
                                    <div className="text-sm space-y-3">
                                        <p>Hey [Employee Name]! 👋</p>
                                        <p>We're thrilled to officially offer you a spot on our team as <strong>[Job Title]</strong>! After meeting you, we're confident you're the perfect fit for what we're building here at [Company Name].</p>
                                        <div className="border-l-4 border-blue-500 pl-4 my-4">
                                            <div className="font-semibold mb-2">Here's what you need to know:</div>
                                            <div className="space-y-1">
                                                <div>🎯 <strong>Role:</strong> [Job Title] in the [Department Name] team</div>
                                                <div>👥 <strong>You'll work with:</strong> [Manager Name] and the amazing [Department] crew</div>
                                                <div>📅 <strong>Start Date:</strong> [DD/MM/YYYY] (flexible if you need to adjust)</div>
                                                <div>💰 <strong>Compensation:</strong> [Currency] [Amount] annually</div>
                                                <div>📍 <strong>Location:</strong> [Location] (hybrid/remote options available)</div>
                                                <div>⏰ <strong>Probation:</strong> [X] months - we'll check in regularly</div>
                                            </div>
                                        </div>
                                        <p>We value flexibility and work-life balance. You'll have access to our flexible working hours, remote work options, learning budget, and all the perks that come with being part of our growing team.</p>
                                        <p>If this sounds good, sign the acceptance form and let's get started on this journey together!</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'conf-standard' && (
                                    <div className="text-sm space-y-3">
                                        <p>We are pleased to inform you that you have successfully completed your probation period of [X] months. Effective [DD/MM/YYYY], your employment with [Company Name] is confirmed as a permanent employee.</p>
                                        <div className="pl-4 mt-3 space-y-1">
                                            <div><strong>Confirmation Date:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>Designation:</strong> [Job Title]</div>
                                            <div><strong>Department:</strong> [Department Name]</div>
                                            <div><strong>Reporting To:</strong> [Manager Name]</div>
                                        </div>
                                        <p className="mt-3">All terms and conditions of your employment as stated in your appointment letter dated [Date] remain unchanged and continue to be in effect.</p>
                                        <p>We look forward to your continued contributions to the organization.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'conf-performance' && (
                                    <div className="text-sm space-y-3">
                                        <p>We are delighted to confirm your permanent employment with [Company Name] effective [DD/MM/YYYY], following the successful completion of your probation period.</p>
                                        <p>During your probation, you have demonstrated exceptional performance in the following areas:</p>
                                        <div className="pl-4 mt-2">
                                            <div>• Successfully delivered [Project/Task Name]</div>
                                            <div>• Demonstrated strong collaboration with the [Department] team</div>
                                            <div>• Shown initiative in [specific achievement]</div>
                                            <div>• Consistently met/exceeded performance expectations</div>
                                        </div>
                                        <div className="bg-gray-100 p-3 rounded mt-3">
                                            <div><strong>Designation:</strong> [Job Title]</div>
                                            <div><strong>Department:</strong> [Department Name]</div>
                                            <div><strong>Confirmation Date:</strong> [DD/MM/YYYY]</div>
                                        </div>
                                        <p className="mt-3">Your dedication and contributions have been invaluable. We are excited to see your continued growth and success with our organization.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'conf-detailed' && (
                                    <div className="text-sm space-y-3">
                                        <p>Congratulations! We are pleased to confirm your permanent employment with [Company Name] effective [DD/MM/YYYY].</p>
                                        <div className="font-semibold mt-4">UPDATED EMPLOYMENT TERMS:</div>
                                        <div className="pl-4 space-y-1">
                                            <div><strong>Designation:</strong> [Job Title]</div>
                                            <div><strong>Department:</strong> [Department Name]</div>
                                            <div><strong>Confirmation Date:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>Annual CTC:</strong> [Currency] [Amount]</div>
                                            <div><strong>Notice Period:</strong> [X] days (as per confirmed employee policy)</div>
                                        </div>
                                        <div className="font-semibold mt-4">BENEFITS EFFECTIVE FROM CONFIRMATION:</div>
                                        <div className="pl-4 space-y-1">
                                            <div>• Annual Leave: [X] days per year</div>
                                            <div>• Health Insurance: Coverage for self and dependents</div>
                                            <div>• Provident Fund: As per company policy</div>
                                            <div>• Performance Bonus: Eligible for annual performance review</div>
                                            <div>• Professional Development: Access to training and certification programs</div>
                                        </div>
                                        <p className="mt-3">All other terms and conditions as mentioned in your appointment letter continue to remain in effect. We look forward to a long and mutually beneficial association.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'exp-basic' && (
                                    <div className="text-sm space-y-3">
                                        <p>This is to certify that <strong>[Employee Name]</strong> was employed with [Company Name].</p>
                                        <div className="pl-4 mt-3 space-y-1">
                                            <div><strong>Designation:</strong> [Job Title]</div>
                                            <div><strong>Department:</strong> [Department Name]</div>
                                            <div><strong>Employment Period:</strong> [Start Date] to [End Date]</div>
                                            <div><strong>Employee ID:</strong> [ID Number]</div>
                                        </div>
                                        <p className="mt-3">During the tenure, [he/she] was found to be sincere and hardworking.</p>
                                        <p>We wish [him/her] all the best in [his/her] future endeavors.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'exp-detailed' && (
                                    <div className="text-sm space-y-3">
                                        <p>This is to certify that <strong>[Employee Name]</strong> worked with [Company Name] from <strong>[Start Date]</strong> to <strong>[End Date]</strong> as <strong>[Job Title]</strong> in the [Department Name] department.</p>
                                        <div className="font-semibold mt-4">KEY RESPONSIBILITIES:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Led and coordinated [specific responsibility area]</div>
                                            <div>• Managed [team/project/function] with focus on [key area]</div>
                                            <div>• Developed and implemented [process/system/strategy]</div>
                                            <div>• Collaborated with cross-functional teams on [projects/initiatives]</div>
                                        </div>
                                        <div className="font-semibold mt-4">NOTABLE ACHIEVEMENTS:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Successfully completed [major project/milestone]</div>
                                            <div>• Improved [metric/process] by [percentage/amount]</div>
                                            <div>• Received recognition for [achievement]</div>
                                        </div>
                                        <p className="mt-3">During [his/her] tenure, [Employee Name] demonstrated strong technical skills, excellent teamwork, and consistent dedication to quality work. [He/She] was a valuable member of our team.</p>
                                        <p>We wish [him/her] continued success in all future endeavors.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'exp-recommendation' && (
                                    <div className="text-sm space-y-3">
                                        <p>This letter is to certify that <strong>[Employee Name]</strong> was employed with [Company Name] as <strong>[Job Title]</strong> from <strong>[Start Date]</strong> to <strong>[End Date]</strong>.</p>
                                        <p>During [his/her] association with us, [Employee Name] worked in the [Department Name] department and reported to [Manager Name]. [His/Her] primary responsibilities included:</p>
                                        <div className="pl-4 mt-2">
                                            <div>• [Key Responsibility 1]</div>
                                            <div>• [Key Responsibility 2]</div>
                                            <div>• [Key Responsibility 3]</div>
                                        </div>
                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-4">
                                            <p className="font-semibold">PERFORMANCE ASSESSMENT:</p>
                                            <p className="mt-2">[Employee Name] consistently exhibited excellent professional conduct, strong work ethic, and outstanding performance throughout [his/her] tenure. [He/She] possesses strong [technical/analytical/leadership] skills and the ability to work effectively both independently and as part of a team.</p>
                                            <p className="mt-2">[He/She] was always proactive, reliable, and demonstrated exceptional [problem-solving/communication/management] abilities. [His/Her] positive attitude and dedication made [him/her] a valued team member.</p>
                                        </div>
                                        <p className="mt-3">We have no hesitation in recommending [Employee Name] for any future employment opportunities. We are confident that [he/she] will be an asset to any organization.</p>
                                        <p>We wish [him/her] all the very best for [his/her] future career.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'pay-standard' && (
                                    <div className="text-sm">
                                        <div className="border-2 border-gray-300 p-4 rounded">
                                            <div className="text-center font-bold text-lg mb-3 pb-2 border-b-2 border-gray-900">SALARY SLIP</div>
                                            <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-200">
                                                <div>
                                                    <p className="font-bold text-sm text-gray-900">Your Company Name</p>
                                                    <p className="text-xs text-gray-600">123 Business Street</p>
                                                    <p className="text-xs text-gray-600">City, State 500001</p>
                                                </div>
                                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">Logo</div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mb-3 pb-3 border-b border-gray-200 text-xs">
                                                <div className="flex justify-between"><span className="text-gray-600">Employee Name:</span><span className="font-semibold">John Smith</span></div>
                                                <div className="flex justify-between"><span className="text-gray-600">Employee ID:</span><span className="font-semibold">EMP001</span></div>
                                                <div className="flex justify-between"><span className="text-gray-600">Designation:</span><span className="font-semibold">Software Engineer</span></div>
                                                <div className="flex justify-between"><span className="text-gray-600">Department:</span><span className="font-semibold">Engineering</span></div>
                                                <div className="flex justify-between"><span className="text-gray-600">Pay Period:</span><span className="font-semibold">March 2026</span></div>
                                                <div className="flex justify-between"><span className="text-gray-600">PAN:</span><span className="font-semibold">ABCDE1234F</span></div>
                                            </div>
                                            <div className="grid grid-cols-4 gap-2 mb-3 pb-3 border-b border-gray-200 text-center text-xs">
                                                <div><p className="text-gray-600">Working Days</p><p className="font-bold text-sm text-gray-900">22</p></div>
                                                <div><p className="text-gray-600">Paid Leaves</p><p className="font-bold text-sm text-green-600">0</p></div>
                                                <div><p className="text-gray-600">LOP Days</p><p className="font-bold text-sm text-red-600">0</p></div>
                                                <div><p className="text-gray-600">Payable Days</p><p className="font-bold text-sm text-blue-600">22</p></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 mb-3 pb-3 border-b border-gray-200">
                                                <div>
                                                    <div className="font-semibold text-gray-900 mb-2 pb-1 border-b border-gray-300 text-xs">EARNINGS</div>
                                                    <div className="space-y-1 text-xs">
                                                        <div className="flex justify-between"><span className="text-gray-700">Basic</span><span className="font-medium">₹25,000.00</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">HRA</span><span className="font-medium">₹12,500.00</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">Special Allowance</span><span className="font-medium">₹8,500.00</span></div>
                                                        <div className="flex justify-between pt-1 border-t border-gray-300 font-bold text-gray-900"><span>Gross Earnings</span><span>₹46,000.00</span></div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-900 mb-2 pb-1 border-b border-gray-300 text-xs">DEDUCTIONS</div>
                                                    <div className="space-y-1 text-xs">
                                                        <div className="flex justify-between"><span className="text-gray-700">Provident Fund</span><span className="font-medium">₹3,000.00</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">Professional Tax</span><span className="font-medium">₹200.00</span></div>
                                                        <div className="flex justify-between pt-1 border-t border-gray-300 font-bold text-gray-900"><span>Total Deductions</span><span>₹3,200.00</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center font-bold text-sm py-2 px-3 bg-gray-100 rounded">
                                                <span>NET SALARY</span><span className="text-base">₹42,800.00</span>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1 text-right italic">Forty Two Thousand Eight Hundred Rupees Only</div>
                                            <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200 space-y-0.5">
                                                <p>* This is a computer-generated payslip and does not require a signature.</p>
                                                <p>* All amounts are in INR</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {previewTemplate.id === 'pay-detailed' && (
                                    <div className="text-sm">
                                        <div className="border-2 border-gray-300 p-4 rounded">
                                            <div className="text-center mb-4 pb-3 border-b-2 border-gray-900">
                                                <h1 className="text-lg font-bold mb-1 text-gray-900">DETAILED PAYSLIP</h1>
                                                <p className="text-xs text-gray-600">For the Month of March 2026</p>
                                            </div>
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <p className="font-bold text-sm text-gray-900">Your Company Name</p>
                                                    <p className="text-xs text-gray-600">123 Business Street</p>
                                                    <p className="text-xs text-gray-600">City, State 500001</p>
                                                </div>
                                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">Logo</div>
                                            </div>
                                            <div className="mb-3 p-2 rounded bg-gray-50">
                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                    <div><strong>Name:</strong> John Smith</div>
                                                    <div><strong>ID:</strong> EMP001</div>
                                                    <div><strong>PAN:</strong> ABCDE1234F</div>
                                                    <div><strong>Designation:</strong> Software Engineer</div>
                                                    <div><strong>Department:</strong> Engineering</div>
                                                    <div><strong>UAN:</strong> 100123456789</div>
                                                    <div><strong>Bank:</strong> HDFC Bank</div>
                                                    <div><strong>Account:</strong> ****6789</div>
                                                    <div><strong>Period:</strong> March 2026</div>
                                                </div>
                                            </div>
                                            <div className="mb-3 grid grid-cols-4 gap-2 text-center text-xs">
                                                <div className="p-1.5 bg-gray-100 rounded"><p className="font-bold text-sm text-gray-900">22</p><p className="text-gray-600">Working Days</p></div>
                                                <div className="p-1.5 bg-green-50 rounded"><p className="font-bold text-sm text-green-600">0</p><p className="text-gray-600">Paid Leaves</p></div>
                                                <div className="p-1.5 bg-red-50 rounded"><p className="font-bold text-sm text-red-600">0</p><p className="text-gray-600">LOP Days</p></div>
                                                <div className="p-1.5 bg-blue-50 rounded"><p className="font-bold text-sm text-blue-600">22</p><p className="text-gray-600">Payable Days</p></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                                                <div className="border p-2 rounded">
                                                    <div className="font-semibold mb-1.5 bg-green-100 p-1 rounded text-green-700">EARNINGS</div>
                                                    <div className="flex justify-between py-0.5"><span>Basic</span><span className="font-medium">₹25,000.00</span></div>
                                                    <div className="flex justify-between py-0.5"><span>HRA</span><span className="font-medium">₹12,500.00</span></div>
                                                    <div className="flex justify-between py-0.5"><span>Special Allowance</span><span className="font-medium">₹8,500.00</span></div>
                                                    <div className="flex justify-between py-0.5 font-semibold border-t mt-1 pt-1"><span>Gross Earnings</span><span>₹46,000.00</span></div>
                                                </div>
                                                <div className="border p-2 rounded">
                                                    <div className="font-semibold mb-1.5 bg-red-100 p-1 rounded text-red-700">DEDUCTIONS</div>
                                                    <div className="flex justify-between py-0.5"><span>Provident Fund</span><span className="font-medium">₹3,000.00</span></div>
                                                    <div className="flex justify-between py-0.5"><span>Professional Tax</span><span className="font-medium">₹200.00</span></div>
                                                    <div className="flex justify-between py-0.5 font-semibold border-t mt-1 pt-1"><span>Total Deductions</span><span>₹3,200.00</span></div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center font-bold text-sm bg-blue-50 p-2 rounded border-t-2 border-blue-500">
                                                <span>NET PAY</span><span className="text-base text-blue-700">₹42,800.00</span>
                                            </div>
                                            <div className="text-xs mt-1 text-gray-600 italic text-right">In Words: Forty Two Thousand Eight Hundred Rupees Only</div>
                                            <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200 italic space-y-0.5">
                                                <p>* This is a computer-generated payslip and does not require a signature.</p>
                                                <p>* All amounts are in INR</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {previewTemplate.id === 'pay-modern' && (
                                    <div className="text-sm">
                                        <div className="border-2 border-blue-500 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-white">
                                            <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-blue-200">
                                                <div>
                                                    <div className="text-xl font-bold text-blue-600">March</div>
                                                    <div className="text-xs text-gray-600">Payslip for 2026</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xs text-gray-600">Net Salary</div>
                                                    <div className="text-xl font-bold text-green-600">₹42,800</div>
                                                </div>
                                            </div>
                                            <div className="mb-3 p-2 rounded-lg flex justify-between items-center bg-white shadow-sm">
                                                <div>
                                                    <p className="font-bold text-xs text-gray-900">Your Company Name</p>
                                                    <p className="text-xs text-gray-600">City, State</p>
                                                </div>
                                                <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">Logo</div>
                                            </div>
                                            <div className="mb-3 grid grid-cols-2 gap-1.5 text-xs">
                                                <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span><span><strong>John Smith</strong></span></div>
                                                <div><strong>ID:</strong> EMP001</div>
                                                <div><strong>Designation:</strong> Software Engineer</div>
                                                <div><strong>Team:</strong> Engineering</div>
                                            </div>
                                            <div className="mb-3 grid grid-cols-4 gap-1.5 text-center text-xs">
                                                <div className="p-1.5 bg-blue-100 rounded border border-blue-200"><p className="font-bold text-sm text-blue-600">22</p><p className="text-gray-600">Working</p></div>
                                                <div className="p-1.5 bg-green-100 rounded border border-green-200"><p className="font-bold text-sm text-green-600">0</p><p className="text-gray-600">Leaves</p></div>
                                                <div className="p-1.5 bg-red-100 rounded border border-red-200"><p className="font-bold text-sm text-red-600">0</p><p className="text-gray-600">LOP</p></div>
                                                <div className="p-1.5 bg-purple-100 rounded border border-purple-200"><p className="font-bold text-sm text-purple-600">22</p><p className="text-gray-600">Payable</p></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="bg-white p-2 rounded-lg shadow-sm border">
                                                    <div className="text-xs text-green-600 font-semibold mb-1.5">EARNINGS</div>
                                                    <div className="space-y-0.5 text-xs">
                                                        <div className="flex justify-between"><span className="text-gray-700">Basic</span><span className="font-medium">₹25,000</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">HRA</span><span className="font-medium">₹12,500</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">Special Allowance</span><span className="font-medium">₹8,500</span></div>
                                                        <div className="flex justify-between border-t pt-0.5 font-semibold text-green-600"><span>Total</span><span>₹46,000</span></div>
                                                    </div>
                                                </div>
                                                <div className="bg-white p-2 rounded-lg shadow-sm border">
                                                    <div className="text-xs text-red-600 font-semibold mb-1.5">DEDUCTIONS</div>
                                                    <div className="space-y-0.5 text-xs">
                                                        <div className="flex justify-between"><span className="text-gray-700">Provident Fund</span><span className="font-medium">₹3,000</span></div>
                                                        <div className="flex justify-between"><span className="text-gray-700">Professional Tax</span><span className="font-medium">₹200</span></div>
                                                        <div className="flex justify-between border-t pt-0.5 font-semibold text-red-600"><span>Total</span><span>₹3,200</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-3 p-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg">
                                                <div className="flex justify-between items-center"><span className="font-semibold text-xs">NET SALARY</span><span className="text-base font-bold">₹42,800</span></div>
                                                <div className="text-xs mt-0.5 opacity-90 text-right">Forty Two Thousand Eight Hundred Rupees Only</div>
                                            </div>
                                            <div className="text-center text-xs text-gray-500 mt-3 space-y-0.5">
                                                <p>Computer-generated payslip - No signature required</p>
                                                <p>All amounts in INR</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {previewTemplate.id === 'pay-cognizant' && (
                                    <div className="text-sm" style={{ fontFamily: 'Arial, sans-serif', color: '#111' }}>
                                        <div className="p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div style={{ fontSize: 16, fontWeight: 700 }}>PAYSLIP <span style={{ fontWeight: 500 }}>MAR 2026</span></div>
                                                    <div style={{ fontSize: 11, fontWeight: 600, marginTop: 8 }}>Your Company Name</div>
                                                    <div style={{ fontSize: 10, color: '#6a6a6a', marginTop: 4 }}>123 Business Street, City, State 500001</div>
                                                </div>
                                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">Logo</div>
                                            </div>
                                            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 8 }}>John Smith</div>
                                            <hr style={{ height: 1, background: '#111', border: 'none', margin: '6px 0' }} />
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: '6px 0' }}>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Employee Number</div><div style={{ fontSize: 10, fontWeight: 500 }}>EMP001</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Date Joined</div><div style={{ fontSize: 10, fontWeight: 500 }}>15-Mar-2022</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Department</div><div style={{ fontSize: 10, fontWeight: 500 }}>Engineering</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Sub Department</div><div style={{ fontSize: 10, fontWeight: 500 }}>Backend</div></div>
                                            </div>
                                            <hr style={{ height: 1, background: '#d7d7d7', border: 'none' }} />
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: '6px 0' }}>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Designation</div><div style={{ fontSize: 10, fontWeight: 500 }}>Software Engineer</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Payment Mode</div><div style={{ fontSize: 10, fontWeight: 500 }}>Bank Transfer</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Bank</div><div style={{ fontSize: 10, fontWeight: 500 }}>HDFC Bank</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Bank IFSC</div><div style={{ fontSize: 10, fontWeight: 500 }}>HDFC0001234</div></div>
                                            </div>
                                            <hr style={{ height: 1, background: '#d7d7d7', border: 'none' }} />
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: '6px 0' }}>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Bank Account</div><div style={{ fontSize: 10, fontWeight: 500 }}>****6789</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>UAN</div><div style={{ fontSize: 10, fontWeight: 500 }}>100123456789</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>PF Number</div><div style={{ fontSize: 10, fontWeight: 500 }}>PF/HYD/12345</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>PAN Number</div><div style={{ fontSize: 10, fontWeight: 500 }}>ABCDE1234F</div></div>
                                            </div>
                                            <hr style={{ height: 1, background: '#111', border: 'none', margin: '6px 0' }} />
                                            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>SALARY DETAILS</div>
                                            <hr style={{ height: 1, background: '#111', border: 'none', margin: '6px 0' }} />
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: '6px 0' }}>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Actual Payable Days</div><div style={{ fontSize: 10, fontWeight: 500 }}>22.0</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Total Working Days</div><div style={{ fontSize: 10, fontWeight: 500 }}>22.0</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Loss Of Pay Days</div><div style={{ fontSize: 10, fontWeight: 500 }}>0.00</div></div>
                                                <div><div style={{ fontSize: 9, color: '#6a6a6a' }}>Days Payable</div><div style={{ fontSize: 10, fontWeight: 500 }}>22</div></div>
                                            </div>
                                            <hr style={{ height: 1, background: '#d7d7d7', border: 'none' }} />
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: 12, marginTop: 8 }}>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>EARNINGS</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <div className="flex justify-between text-xs"><span>Basic</span><span>25,000.00</span></div>
                                                        <div className="flex justify-between text-xs"><span>HRA</span><span>12,500.00</span></div>
                                                        <div className="flex justify-between text-xs"><span>Special Allowance</span><span>8,500.00</span></div>
                                                        <div className="flex justify-between text-xs font-bold mt-1"><span>Total Earnings (A)</span><span>46,000.00</span></div>
                                                    </div>
                                                </div>
                                                <div style={{ width: 1, background: '#d7d7d7', height: '100%' }}></div>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>TAXES & DEDUCTIONS</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <div className="flex justify-between text-xs"><span>Provident Fund</span><span>3,000.00</span></div>
                                                        <div className="flex justify-between text-xs"><span>Professional Tax</span><span>200.00</span></div>
                                                        <div className="flex justify-between text-xs font-bold mt-1"><span>Total Taxes & Deductions (B)</span><span>3,200.00</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ background: '#f3f3f3', padding: '10px 14px', marginTop: 12 }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px' }}>
                                                    <div style={{ fontSize: 11, fontWeight: 500 }}>Net Salary Payable ( A - B )</div>
                                                    <div style={{ fontSize: 11, fontWeight: 500 }}>42,800.00</div>
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', marginTop: 6 }}>
                                                    <div style={{ fontSize: 10, fontWeight: 500 }}>Net Salary in words</div>
                                                    <div style={{ fontSize: 10, fontWeight: 700 }}>Forty Two Thousand Eight Hundred Rupees only</div>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: 8, fontSize: 9 }}>
                                                <div><strong>Note :</strong> <em>All amounts displayed in this payslip are in INR</em></div>
                                                <div style={{ fontStyle: 'italic', marginTop: 4 }}>*This is computer generated statement, does not require signature.</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {previewTemplate.id === 'prom-formal' && (
                                    <div className="text-sm space-y-3">
                                        <p>We are pleased to inform you that effective <strong>[DD/MM/YYYY]</strong>, you have been promoted to the position of <strong>[New Job Title]</strong>.</p>
                                        <div className="font-semibold mt-4">REVISED EMPLOYMENT TERMS:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>New Designation:</strong> [New Job Title]</div>
                                            <div><strong>Department:</strong> [Department Name]</div>
                                            <div><strong>Reporting To:</strong> [Manager Name], [Manager Designation]</div>
                                            <div><strong>Effective Date:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>Revised Annual CTC:</strong> [Currency] [Amount] per annum</div>
                                            <div><strong>Location:</strong> [Location]</div>
                                        </div>
                                        <p className="mt-3">All other terms and conditions of your employment remain unchanged as per your original appointment letter.</p>
                                        <p>This promotion is in recognition of your consistent performance and contributions to the organization. We trust you will continue to excel in your new role.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'prom-appreciative' && (
                                    <div className="text-sm space-y-3">
                                        <p>It gives us immense pleasure to announce your promotion to <strong>[New Job Title]</strong>, effective <strong>[DD/MM/YYYY]</strong>.</p>
                                        <p>Over the past [X years/months], you have consistently demonstrated:</p>
                                        <div className="pl-4 mt-2">
                                            <div>✓ Outstanding performance in [current role]</div>
                                            <div>✓ Exceptional leadership in [specific project/area]</div>
                                            <div>✓ Dedication to achieving organizational goals</div>
                                            <div>✓ Strong collaboration with team members</div>
                                        </div>
                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-4">
                                            <div className="font-semibold mb-2">Your New Role:</div>
                                            <div className="space-y-1">
                                                <div><strong>Designation:</strong> [New Job Title]</div>
                                                <div><strong>Team:</strong> [Department Name]</div>
                                                <div><strong>Reporting To:</strong> [Manager Name]</div>
                                                <div><strong>Effective Date:</strong> [DD/MM/YYYY]</div>
                                                <div><strong>New Compensation:</strong> [Currency] [Amount] per annum</div>
                                            </div>
                                        </div>
                                        <p className="mt-3">This promotion is well-deserved recognition of your hard work and achievements. We are confident that you will continue to add value in your expanded role.</p>
                                        <p>Congratulations and best wishes for continued success!</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'prom-comprehensive' && (
                                    <div className="text-sm space-y-3">
                                        <p>We are delighted to inform you of your promotion to <strong>[New Job Title]</strong>. This advancement reflects your exemplary performance and dedication to our organization.</p>
                                        <div className="font-semibold mt-4">NEW ROLE DETAILS:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>Designation:</strong> [New Job Title]</div>
                                            <div><strong>Department/Division:</strong> [Department Name]</div>
                                            <div><strong>Reporting Manager:</strong> [Manager Name], [Manager Designation]</div>
                                            <div><strong>Direct Reports:</strong> [Number] team members</div>
                                            <div><strong>Effective Date:</strong> [DD/MM/YYYY]</div>
                                        </div>
                                        <div className="font-semibold mt-4">REVISED COMPENSATION PACKAGE:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>Annual CTC:</strong> [Currency] [Amount]</div>
                                            <div><strong>Basic Salary:</strong> [Currency] [Amount] per month</div>
                                            <div><strong>Performance Bonus:</strong> Up to [X]% of annual salary</div>
                                            <div><strong>Stock Options:</strong> [Details if applicable]</div>
                                        </div>
                                        <div className="font-semibold mt-4">ADDITIONAL BENEFITS:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Enhanced medical insurance coverage</div>
                                            <div>• Expanded learning and development budget</div>
                                            <div>• [Additional benefit 1]</div>
                                            <div>• [Additional benefit 2]</div>
                                        </div>
                                        <div className="font-semibold mt-4">KEY RESPONSIBILITIES:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Lead and manage [team/function/project]</div>
                                            <div>• Drive strategic initiatives for [area]</div>
                                            <div>• Collaborate with senior leadership on [objectives]</div>
                                            <div>• [Additional responsibility]</div>
                                        </div>
                                        <p className="mt-3">We are confident in your ability to excel in this new role and look forward to your continued contributions to our success.</p>
                                        <p>Congratulations on this well-earned promotion!</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'term-standard' && (
                                    <div className="text-sm space-y-3">
                                        <p>This letter serves as formal notification that your employment with [Company Name] will be terminated, effective <strong>[DD/MM/YYYY]</strong>.</p>
                                        <div className="font-semibold mt-4">TERMINATION DETAILS:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>Last Working Day:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>Notice Period:</strong> [X] days as per employment contract</div>
                                            <div><strong>Reason:</strong> [Reason for termination]</div>
                                        </div>
                                        <div className="font-semibold mt-4">SETTLEMENT PROCESS:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div>• Final salary payment: [Date]</div>
                                            <div>• Unused leave encashment: As per company policy</div>
                                            <div>• Full and Final Settlement: Within [X] days of last working day</div>
                                        </div>
                                        <div className="font-semibold mt-4">CLEARANCE REQUIREMENTS:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Return all company property (laptop, access cards, etc.)</div>
                                            <div>• Complete exit formalities with HR Department</div>
                                            <div>• Submit clearance certificate from all departments</div>
                                            <div>• Return any confidential documents or materials</div>
                                        </div>
                                        <p className="mt-3">Please ensure all clearance formalities are completed before your last working day to facilitate timely processing of your final settlement.</p>
                                        <p>We wish you success in your future endeavors.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'term-mutual' && (
                                    <div className="text-sm space-y-3">
                                        <p>This letter confirms our mutual agreement to separate your employment with [Company Name], effective <strong>[DD/MM/YYYY]</strong>.</p>
                                        <p>We would like to acknowledge your contributions during your tenure with us:</p>
                                        <div className="pl-4 mt-2">
                                            <div>• Joined: [Start Date]</div>
                                            <div>• Designation: [Job Title]</div>
                                            <div>• Department: [Department Name]</div>
                                            <div>• Duration of service: [X years/months]</div>
                                        </div>
                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-4">
                                            <p>During your time with us, you have made valuable contributions to [specific achievements/projects]. We appreciate your dedication and professionalism throughout your employment.</p>
                                        </div>
                                        <div className="font-semibold mt-4">SEPARATION TERMS:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>Last Working Day:</strong> [DD/MM/YYYY]</div>
                                            <div><strong>Notice Waived:</strong> By mutual agreement</div>
                                            <div><strong>Severance Package:</strong> [Details if applicable]</div>
                                            <div><strong>Benefits Continuation:</strong> Until [Date]</div>
                                        </div>
                                        <div className="font-semibold mt-4">FINAL SETTLEMENT:</div>
                                        <div className="pl-4 mt-2">
                                            <div>• Outstanding salary: [Amount]</div>
                                            <div>• Leave encashment: [Amount]</div>
                                            <div>• Other dues: [Amount]</div>
                                            <div>• Payment date: [DD/MM/YYYY]</div>
                                        </div>
                                        <p className="mt-3">We request you to complete the exit formalities and return all company property by your last working day.</p>
                                        <p>We wish you all the best in your future career endeavors and hope our paths cross again.</p>
                                    </div>
                                )}

                                {previewTemplate.id === 'term-immediate' && (
                                    <div className="text-sm space-y-3">
                                        <p>This letter serves as formal notice of immediate termination of your employment with [Company Name], effective <strong>[DD/MM/YYYY]</strong>.</p>
                                        <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-3">
                                            <div className="font-semibold">GROUNDS FOR TERMINATION:</div>
                                            <p className="mt-2">Your employment is being terminated for cause due to violation of company policies as outlined below:</p>
                                            <div className="mt-2 pl-4">
                                                <div>• Violation of [Policy Name/Code of Conduct]</div>
                                                <div>• [Specific violation details]</div>
                                                <div>• Date of incident(s): [Date(s)]</div>
                                            </div>
                                            <p className="mt-2">Reference: Company Policy Section [X], Employee Handbook Page [Y]</p>
                                        </div>
                                        <div className="font-semibold mt-4">IMMEDIATE ACTIONS REQUIRED:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div><strong>Effective Date:</strong> [DD/MM/YYYY] (Immediate)</div>
                                            <div><strong>Access Termination:</strong> All system access revoked immediately</div>
                                            <div><strong>Office Access:</strong> Security pass to be surrendered</div>
                                        </div>
                                        <div className="font-semibold mt-4">PROPERTY RETURN:</div>
                                        <div className="pl-4 mt-2">
                                            <div>You are required to return the following by [Date]:</div>
                                            <div>• Company laptop and accessories</div>
                                            <div>• Mobile phone and SIM card</div>
                                            <div>• Access cards and keys</div>
                                            <div>• Any confidential documents or materials</div>
                                            <div>• [Other company property]</div>
                                        </div>
                                        <div className="font-semibold mt-4">FINAL SETTLEMENT:</div>
                                        <div className="pl-4 mt-2 space-y-1">
                                            <div>• Salary till last working day: [Amount]</div>
                                            <div>• Notice period: Not applicable (termination for cause)</div>
                                            <div>• Leave encashment: As per company policy</div>
                                            <div>• Settlement date: Within [X] days after property return and clearance</div>
                                        </div>
                                        <div className="font-semibold mt-4">CONFIDENTIALITY & NON-COMPETE:</div>
                                        <div className="pl-4 mt-2">
                                            <div>All confidentiality and non-compete clauses in your employment agreement remain in full effect as per the terms specified.</div>
                                        </div>
                                        <p className="mt-3">Please contact HR Department to complete the exit formalities and property return process.</p>
                                    </div>
                                )}

                                {/* Closing - only for non-payslip templates */}
                                {!previewTemplate.id.startsWith('pay-') && (
                                    <div className="text-sm mt-6">
                                        <div>Sincerely,</div>
                                        <div className="mt-8">
                                            <div className="font-semibold">[Authorized Signatory]</div>
                                            <div>[Designation]</div>
                                            <div>[Company Name]</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={`p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setPreviewTemplate(null)}
                                className={`px-4 py-2 rounded-lg border transition-colors ${
                                    theme === 'dark'
                                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    handleSelectTemplate(selectedCategory, previewTemplate.id);
                                    setPreviewTemplate(null);
                                }}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                Select This Template
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}


            {/* Create/Edit Custom Template Modal */}
            {showCreateCustomTemplate && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
                    <div
                        className={`rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                        }`}
                    >
                        <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {editingCustomTemplate ? 'Edit Custom Template' : 'Create Custom Template'}
                                    </h3>
                                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Design your own template for {getCategoryName(customTemplateForm.category)}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCloseCustomTemplateModal}
                                    className={`p-2 rounded-lg transition-colors ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                    }`}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                            <div className="space-y-6">
                                {/* Template Form Fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Template Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={customTemplateForm.name}
                                            onChange={(e) => setCustomTemplateForm(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))}
                                            className={`w-full px-4 py-2 rounded-lg border ${
                                                theme === 'dark'
                                                    ? 'bg-gray-700 border-gray-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                            placeholder="Enter template name..."
                                        />
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Category
                                        </label>
                                        <select
                                            value={customTemplateForm.category}
                                            onChange={(e) => setCustomTemplateForm(prev => ({
                                                ...prev,
                                                category: e.target.value
                                            }))}
                                            className={`w-full px-4 py-2 rounded-lg border ${
                                                theme === 'dark'
                                                    ? 'bg-gray-700 border-gray-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        >
                                            {categories.map(category => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        Description
                                    </label>
                                    <textarea
                                        value={customTemplateForm.description}
                                        onChange={(e) => setCustomTemplateForm(prev => ({
                                            ...prev,
                                            description: e.target.value
                                        }))}
                                        className={`w-full px-4 py-2 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        rows={3}
                                        placeholder="Describe the purpose and use of this template..."
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        Template Content *
                                    </label>
                                    <RichTextEditorEnhanced
                                        value={customTemplateForm.content}
                                        onChange={(content) => setCustomTemplateForm(prev => ({
                                            ...prev,
                                            content: content
                                        }))}
                                        placeholder="Enter your template content here..."
                                        showPlaceholderButton={true}
                                        onInsertPlaceholder={(insertFn) => {
                                            setActiveEditor(() => insertFn);
                                            setShowPlaceholderModal(true);
                                        }}
                                    />
                                </div>

                                {/* Variables Helper */}
                                <div className={`p-4 rounded-lg ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        <Type className="w-4 h-4 inline mr-2" />
                                        Available Variables
                                    </h4>
                                    <p className={`text-sm mb-3 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        Use these placeholders in your template. They will be replaced with actual values when generating documents.
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                        {['[EMPLOYEE_NAME]', '[POSITION]', '[DEPARTMENT]', '[EMPLOYEE_ID]',
                                          '[COMPANY_NAME]', '[DATE]', '[MANAGER_NAME]', '[SALARY]',
                                          '[START_DATE]', '[END_DATE]', '[PROBATION_PERIOD]', '[EMAIL]'].map(variable => (
                                            <button
                                                key={variable}
                                                onClick={() => {
                                                    const newContent = customTemplateForm.content + ' ' + variable;
                                                    setCustomTemplateForm(prev => ({
                                                        ...prev,
                                                        content: newContent
                                                    }));
                                                }}
                                                className={`px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors ${
                                                    theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                                }`}
                                            >
                                                {variable}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
                            <button
                                onClick={handleCloseCustomTemplateModal}
                                className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-white hover:bg-gray-600'
                                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingCustomTemplate ? handleUpdateCustomTemplate : handleCreateCustomTemplate}
                                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                            >
                                {editingCustomTemplate ? 'Update Template' : 'Create Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Placeholder Selector Modal */}
            <PlaceholderSelector
                isOpen={showPlaceholderModal}
                onClose={() => setShowPlaceholderModal(false)}
                onInsert={(placeholder) => {
                    if (activeEditor) {
                        activeEditor(placeholder);
                    }
                    setShowPlaceholderModal(false);
                }}
            />

            {/* Document Preview Modal - Shows document before sending */}
            {showDocumentPreviewModal && documentPreviewData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
                            <div className="flex items-center gap-3">
                                <Eye className="w-6 h-6 text-blue-600" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Document Preview</h3>
                                    <p className="text-sm text-gray-500">
                                        Review before sending to <strong>{documentPreviewData.employeeData?.employeeName}</strong>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDocumentPreviewModal(false);
                                    setDocumentPreviewData(null);
                                }}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Preview Content */}
                        <div className="flex-1 overflow-auto p-6 bg-gray-100">
                            <div className="bg-white rounded-lg shadow-lg p-8 max-w-3xl mx-auto" style={{ minHeight: '600px' }}>
                                <div
                                    className="document-preview-content prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: documentPreviewData.previewContent }}
                                />
                            </div>
                        </div>

                        {/* Data Summary */}
                        <div className="p-4 bg-blue-50 border-t">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">Data being sent:</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div><span className="text-gray-500">Name:</span> <strong>{documentPreviewData.employeeData?.employeeName}</strong></div>
                                <div><span className="text-gray-500">Designation:</span> <strong>{documentPreviewData.employeeData?.designation || 'N/A'}</strong></div>
                                <div><span className="text-gray-500">Department:</span> <strong>{documentPreviewData.employeeData?.department || 'N/A'}</strong></div>
                                <div><span className="text-gray-500">CTC:</span> <strong>{documentPreviewData.employeeData?.salary ? `₹${documentPreviewData.employeeData.salary.toLocaleString()}` : 'N/A'}</strong></div>
                                <div><span className="text-gray-500">Manager:</span> <strong>{documentPreviewData.employeeData?.managerName || 'N/A'}</strong></div>
                                <div><span className="text-gray-500">Location:</span> <strong>{documentPreviewData.employeeData?.location || 'N/A'}</strong></div>
                                <div><span className="text-gray-500">Joining Date:</span> <strong>{documentPreviewData.employeeData?.joiningDate || 'N/A'}</strong></div>
                                <div><span className="text-gray-500">Email:</span> <strong>{documentPreviewData.employeeData?.email || 'N/A'}</strong></div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="flex gap-3 justify-end p-4 border-t bg-gray-50 rounded-b-lg">
                            <button
                                onClick={() => {
                                    setShowDocumentPreviewModal(false);
                                    setDocumentPreviewData(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmSendDocument}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                Confirm & Send Document
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Missing Employee Fields Warning Modal */}
            {showMissingFieldsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[85vh] flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-500" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                {missingFieldsData.isPlaceholderBased ? 'Missing Data for Document' : 'Missing Employee Information'}
                            </h3>
                        </div>

                        <p className="text-gray-600 mb-4">
                            {missingFieldsData.isPlaceholderBased ? (
                                <>
                                    The following fields need to be filled before generating the <strong className="capitalize">{missingFieldsData.category}</strong> letter
                                    for <strong>{missingFieldsData.employee?.first_name} {missingFieldsData.employee?.last_name}</strong>:
                                </>
                            ) : (
                                <>
                                    The following fields are required for generating a <strong className="capitalize">{missingFieldsData.category}</strong> letter
                                    but are missing from <strong>{missingFieldsData.employee?.first_name} {missingFieldsData.employee?.last_name}</strong>'s profile:
                                </>
                            )}
                        </p>

                        <div className="mb-4 bg-red-50 p-4 rounded-lg border border-red-100 max-h-[40vh] overflow-y-auto flex-shrink-0">
                            <div className="space-y-2">
                                {missingFieldsData.fields.map((f, i) => (
                                    <div key={i} className="py-2 border-b border-red-100 last:border-b-0">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${
                                                    (f.requiresDateInput && effectiveDateInput) ||
                                                    (f.requiresNumberInput && f.field === 'probationMonths' && probationPeriodInput) ||
                                                    (f.requiresNumberInput && f.field === 'noticePeriod' && noticePeriodInput)
                                                        ? 'bg-green-500' : 'bg-red-500'
                                                }`}></span>
                                                <span className="font-medium text-gray-800">{f.label}</span>
                                            </div>
                                            {f.placeholder && !f.requiresDateInput && !f.requiresNumberInput && (
                                                <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded font-mono">
                                                    {f.placeholder}
                                                </span>
                                            )}
                                        </div>
                                        {/* Show date picker for fields that require user input */}
                                        {f.requiresDateInput && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <input
                                                    type="date"
                                                    value={effectiveDateInput}
                                                    onChange={(e) => setEffectiveDateInput(e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                                {effectiveDateInput && (
                                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                                        {new Date(effectiveDateInput).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {/* Show number input for fields that require number input (probation period, notice period) */}
                                        {f.requiresNumberInput && f.field === 'probationMonths' && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    placeholder="e.g., 3 or 6"
                                                    value={probationPeriodInput}
                                                    onChange={(e) => setProbationPeriodInput(e.target.value)}
                                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                                <span className="text-sm text-gray-600">months</span>
                                                {probationPeriodInput && (
                                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                                        {probationPeriodInput} month{parseInt(probationPeriodInput) !== 1 ? 's' : ''} probation
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {f.requiresNumberInput && f.field === 'noticePeriod' && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="180"
                                                    placeholder="e.g., 30 or 60"
                                                    value={noticePeriodInput}
                                                    onChange={(e) => setNoticePeriodInput(e.target.value)}
                                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                                <span className="text-sm text-gray-600">days</span>
                                                {noticePeriodInput && (
                                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                                        {noticePeriodInput} day{parseInt(noticePeriodInput) !== 1 ? 's' : ''} notice
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Group fields by edit path for better guidance */}
                        {missingFieldsData.isPlaceholderBased && (
                            <div className="mb-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                                <p className="font-medium text-blue-800 mb-2">Where to update:</p>
                                <ul className="space-y-1">
                                    {missingFieldsData.fields.some(f => f.editPath === 'basic') && (
                                        <li>• <strong>Basic Info tab:</strong> Name, Email, Phone, Address, Employee Code</li>
                                    )}
                                    {missingFieldsData.fields.some(f => f.editPath === 'employment') && (
                                        <li>• <strong>Employment tab:</strong> Designation, Department, Manager, Location, Joining Date</li>
                                    )}
                                    {missingFieldsData.fields.some(f => f.editPath === 'compensation') && (
                                        <li>• <strong>Compensation tab:</strong> CTC/Salary, Basic Salary</li>
                                    )}
                                    {missingFieldsData.fields.some(f => f.editPath === 'exit') && (
                                        <li>• <strong>Exit Management:</strong> Last Working Day, Exit Date</li>
                                    )}
                                    {missingFieldsData.fields.some(f => f.editPath === 'company') && (
                                        <li>• <strong>Company Settings:</strong> Company Name, Address, HR Name</li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Show different message based on whether only user input fields are missing */}
                        {(() => {
                            const hasDateInputFields = missingFieldsData.fields.some(f => f.requiresDateInput);
                            const hasNumberInputFields = missingFieldsData.fields.some(f => f.requiresNumberInput);
                            const hasOtherMissingFields = missingFieldsData.fields.some(f => !f.requiresDateInput && !f.requiresNumberInput);

                            // Check if all user-input fields have been filled
                            const allInputFieldsFilled = missingFieldsData.fields.every(f => {
                                if (f.requiresDateInput) return !!effectiveDateInput;
                                if (f.requiresNumberInput && f.field === 'probationMonths') return !!probationPeriodInput;
                                if (f.requiresNumberInput && f.field === 'noticePeriod') return !!noticePeriodInput;
                                return false; // Other fields need profile edit
                            });

                            // Only user-input fields are missing (date and/or number inputs)
                            const onlyUserInputFields = missingFieldsData.fields.every(f => f.requiresDateInput || f.requiresNumberInput);

                            return (
                                <>
                                    {onlyUserInputFields && !allInputFieldsFilled && (
                                        <p className="text-sm text-blue-600 mb-4 bg-blue-50 p-2 rounded">
                                            Enter the required values above and click "Continue" to generate the document.
                                        </p>
                                    )}
                                    {onlyUserInputFields && allInputFieldsFilled && (
                                        <p className="text-sm text-green-600 mb-4 bg-green-50 p-2 rounded">
                                            All fields filled! Click "Continue" to generate the document.
                                        </p>
                                    )}
                                    {hasOtherMissingFields && (
                                        <p className="text-sm text-gray-500 mb-4">
                                            Please update the {missingFieldsData.fields.some(f => f.editPath === 'company') ? 'employee profile or company settings' : "employee's profile"} with this information before generating the document.
                                        </p>
                                    )}
                                </>
                            );
                        })()}

                        <div className="flex gap-3 justify-end flex-wrap">
                            <button
                                onClick={() => {
                                    setShowMissingFieldsModal(false);
                                    setEffectiveDateInput('');
                                    setProbationPeriodInput('');
                                    setNoticePeriodInput('');
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
                            >
                                Cancel
                            </button>
                            {missingFieldsData.fields.some(f => f.editPath === 'company') && (
                                <button
                                    onClick={() => {
                                        window.open('/admin/settings', '_blank');
                                    }}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium flex items-center gap-2"
                                >
                                    <Building className="w-4 h-4" />
                                    Company Settings
                                </button>
                            )}
                            {/* Show Continue button if only user-input fields are missing and all are filled */}
                            {(() => {
                                const allInputFieldsFilled = missingFieldsData.fields.every(f => {
                                    if (f.requiresDateInput) return !!effectiveDateInput;
                                    if (f.requiresNumberInput && f.field === 'probationMonths') return !!probationPeriodInput;
                                    if (f.requiresNumberInput && f.field === 'noticePeriod') return !!noticePeriodInput;
                                    return false;
                                });
                                const onlyUserInputFields = missingFieldsData.fields.every(f => f.requiresDateInput || f.requiresNumberInput);

                                return onlyUserInputFields && allInputFieldsFilled && (
                                    <button
                                        onClick={() => {
                                            // Close modal and automatically retry the send with user-provided values
                                            setShowMissingFieldsModal(false);
                                            // Small delay to allow state update, then re-trigger the send
                                            // The employeeDataForPreview will pick up the input values on next call
                                            console.log('✅ User filled all input fields, closing modal. User values:', {
                                                effectiveDateInput,
                                                probationPeriodInput,
                                                noticePeriodInput
                                            });
                                            // NOTE: User needs to click Send again - the input values are preserved
                                            // and will be used in the next handleSendToEmployee call
                                        }}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                                    >
                                        <Check className="w-4 h-4" />
                                        Continue (Click Send Again)
                                    </button>
                                );
                            })()}
                            {missingFieldsData.fields.some(f => !f.requiresDateInput && !f.requiresNumberInput) && (
                                <button
                                    onClick={() => {
                                        // Navigate to employee edit page
                                        window.open(`/admin/employees/${missingFieldsData.employee?.id}`, '_blank');
                                        setShowMissingFieldsModal(false);
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
                                >
                                    <Edit2 className="w-4 h-4" />
                                    Edit Employee Profile
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentBuilderEnhanced;